import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { MutableRefObject } from 'react';
import {
  getWatchBrandById,
  updateWatchReference,
  uploadReferenceImage,
  attachReferenceImageFromUrl,
  ReferenceImageFetchError,
  deleteReferenceImage,
  deleteWatchReference,
  getImageUrl,
  computeSearchText,
  getLookupCollection,
  lookupCalibre,
  saveCustomCalibre,
  getCustomCalibres,
  getCustomCalibresByIds,
  getWatchReference,
  resolveCalibre,
  enrichReferenceWithAI,
  fetchViaCloudflareProxy,
  getSiblingReferences,
  deriveFunctionsFromCalibre,
  getVariantCountForParent,
  backfillParentVariantAggregates,
  findModulesByLegacyValues,
  addAliasToElectronicModule,
  getElectronicModuleById,
  getCustomCalibreById,
  fetchImageBlob,
  uploadCalibreImage,
  appendCalibreImage,
  resolveDefaultCalibreSource,
  type SuggestElectronicModuleInput,
  type CustomCalibreEntry,
  type CalibreData,
  type CalibreTier,
  type CalibreTierOverrides,
} from '../../services/firebase';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { htmlToText, MAX_SOURCE_URL_CONTENT_CHARS } from '../../services/sourceUrlContent';
import { zipTaggedImages, type TaggedEnrichImage } from '../../services/enrichImageCollection';
import {
  CALIBRE_GALLERY_FULL_MESSAGE,
  calibreImageCopyOutcome,
  canCopyImageToCalibre,
} from '../../services/calibreImages';
// The gallery tag vocabulary (chips below) — shared with the importer, which
// pre-fills these same tags from the extractor's inferred ones.
import {
  GALLERY_IMAGE_TAGS,
  imageForWrite,
  promoteGalleryImageToMain,
} from '../../services/galleryImages';
import type { ElectronicModule, ElectronicsInfo } from '../../types';
import type { SiblingReference } from '../../services/notableVariantCandidates';
import type { CalibreOverrides } from '@watchkeeper/shared-types';
import { AdaptiveLookupField } from './AdaptiveLookupField';
import { getLookupMultiSelectFor } from '../../services/lookupFieldMultiSelect';
import {
  parseDialMarkerValues,
  dialMarkerValuesForWrite,
} from '../../utils/dialMarkerFields';
import {
  parseMultiLookupValues,
  multiLookupValuesForWrite,
  clearedArrayForWrite,
} from '../../utils/multiLookupValues';
import {
  ElectronicModulePicker,
  ElectronicModuleAiLookup,
  ModuleDetailsSummary,
} from '../ElectronicModules';
import { TagInput } from './TagInput';
import { CalibreSlugMappingsModal } from './CalibreSlugMappingsModal';
import { ImportRulesEditorModal } from './ImportRulesEditorModal';
import { CustomCalibreModal } from './CustomCalibreModal';
import { CalibreActionToolbar, isGenericMovementName } from './CalibreActionToolbar';
import { calibreEntryMatchesRef, normalizeCalibreKey } from '../../services/calibreResolution';
import {
  buildAiCalibreApply,
  buildAiCalibreMatchNotice,
  checkAiCalibreMatch,
  isTierStaleOnCalibre,
  type AiCalibreMatchCheck,
} from './aiCalibreApply';
import {
  TIER_SUGGESTION_KEYS,
  defaultAcceptForSuggestion,
  shouldHoldTierLowConfidence,
  tierPickerContribution,
} from './refTierSuggestion';
import { useAnthropicModels } from '../../hooks/useAnthropicModels';
import { SuggestionSelectToolbar } from './SuggestionSelectToolbar';
import {
  applySelectionMode,
  type SelectionMode,
  type SelectionRow,
} from './suggestionSelection';
import {
  cellAgreement,
  booleanAgreement,
  BOOLEAN_ENRICH_KEYS,
  CHECKED_REASON_LABEL,
  type CheckedReason,
} from '../../services/bulkEnrichFields';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { buildEnrichReferencePayload } from '../../services/enrichReferencePayload';
import {
  descriptionProvenancePatch,
  effectiveDescriptionSource,
  type DescriptionSource,
} from './descriptionSource';
import { Timestamp } from 'firebase/firestore';
import {
  isAlgoliaConfigured,
  searchCalibreIdsByName,
} from '../../services/algolia';
import {
  ENRICH_FIELD_LOOKUPS,
  canonicalizeEnumValue,
} from '../../services/enrichEnumFields';
import { cleanOverrides } from './CalibreTiersEditor';
import {
  applyTierOverrides,
  baseValueFor,
  displayOverrideLookupValue,
  isAccuracyKey,
  effectiveAccuracy,
  resolveTierSuggestion,
  tierPickerOptions,
  tiersForBrand,
  type OverrideKey,
} from './calibreTiers';
import { CalibrePickerModal } from './CalibrePickerModal';
import { getCurrentRevision } from '../../services/calibreLineage';
import { firstYear, revisionPickWarning, type RevisionPickWarning } from '../../services/calibreLineageLogic';
import { RefCalibreCachePanel } from './RefCalibreCachePanel';
import {
  effectiveManufacturerLabel,
  isGatedKey,
  specGates,
  type BrandNameSource,
} from './effectiveSpecDisplay';
import { CalibreSpecDetail } from './CalibreSpecDetail';
import { RefCalibreOverridesEditor } from './RefCalibreOverridesEditor';
import { VariantsPanel } from './VariantsPanel';
import {
  cacheToBaseData,
  countCalibreOverrides,
  calibreDataToCacheSnapshot,
} from './refCalibreCache';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import { CopyLinkButton } from '../common/CopyLinkButton';
import { referenceShareUrl } from '../../services/shareLinks';
import type {
  WatchReference,
  WatchLink,
  WatchBrand,
  ImageInfo,
  LookupValue,
} from '../../types';
import toast from 'react-hot-toast';

/**
 * Project a linked `ElectronicModule` doc onto the `ElectronicsInfo` cache shape
 * so the read-only `ModuleDetailsSummary` can render it directly (the summary is
 * keyed on the cache field names). Lets "Module details" reflect the fresh module
 * doc immediately on link, before the server-side `electronics.*` cache trigger
 * has run. The module's numeric fields are rendered with their units to match the
 * free-text cache values.
 */
function moduleToElectronicsInfo(m: ElectronicModule): ElectronicsInfo {
  return {
    display: m.displayType,
    lcdType: m.lcdType,
    lightType: m.lightType,
    battery: m.battery,
    batteryLife:
      m.batteryLifeMonths != null ? `${m.batteryLifeMonths} months` : undefined,
    chargeTime: m.chargeTimeHours != null ? `${m.chargeTimeHours} hours` : undefined,
    displayLayout: m.displayLayout,
    viewingAngle: m.viewingAngle,
    dstSupport: m.dstSupport,
    calendarType: m.calendarType,
    stopwatchPrecision: m.stopwatchPrecision,
    powerSource: m.powerSource,
    syncSource: m.syncSource,
    timeSystems: m.timeSystems,
    alarmTypes: m.alarmTypes,
  };
}


// The six aggregated variant-array fields a PARENT ref carries (kept in sync
// from its variants by the watchlock `variantAggregates` trigger). The editor's
// "Variant Aggregates" section lets a curator manually override any of them.
type AggregateField =
  | 'dialColorVariants'
  | 'dialStyleVariants'
  | 'dialFinishVariants'
  | 'strapTypeVariants'
  | 'caseMaterialVariants'
  | 'movementVariants';

// Display config — `lookup` omitted for movementVariants (calibre names are
// free text, not lookup-backed). Lookups mirror the trigger's field-mapping
// table in functions/src/variantAggregates.ts.
const AGGREGATE_FIELD_CONFIG: {
  key: AggregateField;
  label: string;
  lookup?: string;
}[] = [
  { key: 'dialColorVariants', label: 'Dial colors', lookup: 'lookup_dial_colors' },
  { key: 'dialStyleVariants', label: 'Dial styles', lookup: 'lookup_dial_styles' },
  { key: 'dialFinishVariants', label: 'Dial finishes', lookup: 'lookup_dial_finish' },
  { key: 'strapTypeVariants', label: 'Strap types', lookup: 'lookup_strap_materials' },
  { key: 'caseMaterialVariants', label: 'Case materials', lookup: 'lookup_case_materials' },
  { key: 'movementVariants', label: 'Movements (calibres)' },
];

/**
 * How long the Calibre input has to settle before we go looking the string up.
 * The lookup used to fire on every keystroke; typing "Sellita SW200-1" is 15
 * characters and was 15 round trips.
 */
const CALIBRE_LOOKUP_DEBOUNCE_MS = 400;

/** Whether the search index can serve the calibre name lookup at all. */
const algoliaConfigured = isAlgoliaConfigured();

/**
 * Load the handful of `custom_calibres` documents that could match `typed`.
 *
 * Narrowed by an Algolia name/alias search, then hydrated by id, because the
 * index carries neither `aliasDetails` (brand scope) nor `isCatchAll` — it can
 * shortlist but never decide, so the authoritative matching always runs locally
 * against the real documents. The full-collection scan is the fallback only:
 * ~1,823 reads, and reporting "no such calibre" because search was down would
 * have an admin create a duplicate.
 *
 * Shared by the debounced name-resolution effect and the AI-apply handler so
 * both shortlist the same way and pay the same (small) read cost.
 */
async function loadCalibreCandidatesByName(
  typed: string,
): Promise<CustomCalibreEntry[]> {
  if (!algoliaConfigured) return getCustomCalibres();
  try {
    const ids = await searchCalibreIdsByName(typed);
    return await getCustomCalibresByIds(ids);
  } catch (err) {
    console.warn('Calibre name search failed, falling back to a full scan:', err);
    return getCustomCalibres();
  }
}

// Movement fields backed by a single-value lookup. Legacy refs may have stored
// the human label (e.g. "Manual") while linked calibres store the lookup id
// (e.g. "manual") — same semantic value, different string. We resolve both
// sides through the lookup for display and comparison so they don't read as
// divergent. Fields NOT listed here (numbers, booleans, free-text like
// baseMovement / rotorType, and complication arrays) compare raw.
const MOVEMENT_LOOKUP_COLLECTIONS: Partial<Record<keyof CalibreData, string>> = {
  movementType: 'lookup_movement_types',
  windingDirection: 'lookup_winding_directions',
  manualWindingDirection: 'lookup_winding_directions',
  countryOfManufacture: 'lookup_movement_origin',
  antiShockSystem: 'lookup_shock_protection',
  regulatorSystem: 'lookup_regulation',
  hairspring: 'lookup_balance_spring',
  escapement: 'lookup_escapement_type',
  // v1.39.0 construction/decoration lookups — preloaded into movementLookupCache
  // so CalibreSpecDetail resolves display names (not raw slugs). `balanceType`
  // (multi-select since v1.91.0) and `decorations` are string[]; both are keyed
  // here only to load their collections. The single-string displayField /
  // valuesMatch paths guard on `typeof v === 'string'`, so an array falls
  // through to displayLegacy's join rather than mis-resolving. Label resolution
  // for these two happens in the effective-spec panels via
  // displayOverrideLookupValue, which resolves each member.
  balanceType: 'lookup_balance_type',
  movementArchitecture: 'lookup_movement_architecture',
  movementFinishing: 'lookup_movement_finishing',
  decorations: 'lookup_movement_decoration',
  // v1.92.0 materials lookups, keyed here for the same reason: CalibreSpecDetail
  // renders all four and would otherwise show raw slugs.
  // `caseMaterialsAvailable` is the string[] one and behaves like decorations
  // above — it loads the collection and its label resolution happens in the
  // panel, not through the single-string displayField path.
  rotorMaterial: 'lookup_rotor_material',
  bridgeMaterial: 'lookup_bridge_material',
  plateFinishing: 'lookup_plate_finishing',
  caseMaterialsAvailable: 'lookup_case_materials',
};

// Helper function to recursively remove undefined values from an object
// Firestore does not accept undefined values
function removeUndefined<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    // Clean each element in the array
    return value.map((item) => removeUndefined(item)) as T;
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (val === undefined) continue;
      const cleaned = removeUndefined(val);
      // For nested objects, only include if they have properties
      if (
        cleaned !== null &&
        typeof cleaned === 'object' &&
        !Array.isArray(cleaned)
      ) {
        if (Object.keys(cleaned).length > 0) {
          result[key] = cleaned;
        }
      } else {
        result[key] = cleaned;
      }
    }
    return result as T;
  }
  return value;
}

// Firestore fields typed as string[] may actually be a plain string in some documents.
// Coerce to a proper array so TagInput always receives string[].
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value) return [value];
  return [];
}

/**
 * Flag AI suggestions whose value isn't in the field's lookup list.
 *
 * The enrichReference callable already strict-drops these server-side, so this
 * is defence in depth — it catches the case where the deployed function predates
 * that check, or where the server's lookup read failed and it had nothing to
 * validate against. Flagged keys are never auto-accepted.
 *
 * Only the lookup collections this particular response touched are fetched, and
 * a collection that fails to load is treated as "can't verify" → not flagged,
 * so a transient read error never blocks a legitimate suggestion.
 */
async function findUnverifiedEnumSuggestions(
  suggestions: Record<string, unknown>,
): Promise<Record<string, boolean>> {
  const collections = Array.from(
    new Set(
      Object.keys(suggestions)
        .map((key) => ENRICH_FIELD_LOOKUPS[key])
        .filter((c): c is string => !!c),
    ),
  );
  if (collections.length === 0) return {};

  const loaded = await Promise.all(
    collections.map((c) =>
      getLookupCollection(c)
        .then((values) => [c, values] as const)
        .catch(() => [c, null] as const),
    ),
  );
  const valuesByCollection = new Map(loaded);

  const unverified: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(suggestions)) {
    const collectionName = ENRICH_FIELD_LOOKUPS[key];
    if (!collectionName) continue;
    const values = valuesByCollection.get(collectionName);
    if (!values || values.length === 0) continue;
    const elements = Array.isArray(value) ? value : [value];
    const anyUnknown = elements.some(
      (el) => canonicalizeEnumValue(el, values).status === 'unknown',
    );
    if (anyUnknown) unverified[key] = true;
  }
  return unverified;
}

/**
 * Coerce one AI suggestion into a single lookup slug.
 *
 * The date fields are single-select, so the model is asked for a bare string —
 * but the token renderer keys its "array vs string" header off each lookup's
 * own isMultiSelect flag, so a collection flipped to multi (or an auto-seeded
 * doc that lost the flag) would have it answering with an array. Take the first
 * element rather than dropping the suggestion on the floor.
 */
function firstSlug(v: unknown): string {
  if (Array.isArray(v)) {
    const first = v.find((el) => String(el ?? '').trim());
    return first === undefined ? '' : String(first).trim();
  }
  return typeof v === 'string' ? v.trim() : '';
}

interface ReferenceEditorProps {
  reference: WatchReference;
  brand: WatchBrand;
  onBack: () => void;
  onSaved: (updated: WatchReference) => void;
  onClone?: (cloneData: Partial<WatchReference>) => void;
  onDeleted?: () => void;
  /**
   * Optional bridge so a wrapping shell (a Modal's Esc/backdrop close, an
   * external breadcrumb, etc.) can route its own exit through the editor's
   * unsaved-changes guard. On mount the editor stores its guard here; the
   * parent calls `exitGuardRef.current?.(proceed) ?? proceed()`. The stored
   * fn runs `proceed` immediately when the form is clean, or shows the
   * confirm dialog first when there are pending changes.
   */
  exitGuardRef?: MutableRefObject<((proceed: () => void) => void) | null>;
  /**
   * Open another reference in this editor — used by the Variants panel to jump
   * from a parent to one of its children. Routed through the unsaved-changes
   * guard before it fires. Absent → variants render but aren't clickable.
   */
  onOpenReference?: (reference: WatchReference) => void;
}

export function ReferenceEditor({
  reference,
  brand,
  onBack,
  onSaved,
  onClone,
  onDeleted,
  exitGuardRef,
  onOpenReference,
}: ReferenceEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // Pending exit action awaiting confirmation. Non-null → the unsaved-changes
  // confirm dialog is open; holds the navigation to run if the user discards.
  const [pendingExit, setPendingExit] = useState<{ proceed: () => void } | null>(
    null,
  );
  const [showRawData, setShowRawData] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Collapsible sections (default closed)
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    movement: false,
    module: false,
    pocketWatch: false,
    case: false,
    dialAndHands: false,
    strap: false,
    production: false,
    functions: false,
    variantAggregates: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Basic Info
  const [refNumber, setRefNumber] = useState(reference.reference || '');
  const [model, setModel] = useState(reference.model || '');
  const [collectionName, setCollectionName] = useState(
    reference.collection || '',
  );
  const [productName, setProductName] = useState(reference.productName || '');
  const [description, setDescription] = useState(reference.description || '');
  // Provenance of the text in the box: the stored source until this session
  // changes it — 'ai' when an AI suggestion is applied, 'manual' when the
  // curator types. Saved only if the text itself changed (descriptionSource.ts).
  const [descriptionSource, setDescriptionSource] = useState<DescriptionSource | undefined>(
    reference.descriptionSource,
  );
  // Pre-AI-rewrite snapshot of `description`. Fill-empty-only, write-once:
  // captured the first time an AI suggestion overwrites `description` (see the
  // `description` suggestion handler below). Preserved from the loaded ref if it
  // already exists so we never clobber an earlier snapshot.
  const [originalDescription, setOriginalDescription] = useState(
    reference.originalDescription,
  );
  const [calibre, setCalibre] = useState(reference.calibre || '');
  // Phase 3a: authoritative FK to the linked calibre in `custom_calibres`. When
  // set, the ref-write trigger populates `calibreCache` with the effective spec
  // and the legacy `calibre` string becomes read-only (transition only).
  const [calibreId, setCalibreId] = useState(reference.calibreId || '');
  // Which tier variant of the linked calibre applies to this ref. Only
  // meaningful when the resolved calibre has `tiers[]`. Cleared → base spec.
  const [calibreTierId, setCalibreTierId] = useState(
    reference.calibreTierId || '',
  );
  // Per-ref overrides layered on top of `calibreCache` at render time. Managed
  // as the local `CalibreTierOverrides` shape (structurally identical to the
  // shared `CalibreOverrides` alias); only set keys are persisted.
  const [calibreOverrides, setCalibreOverrides] = useState<CalibreTierOverrides>(
    reference.calibreOverrides ?? {},
  );
  const [nickname, setNickname] = useState(reference.nickname || '');
  const [specialEdition, setSpecialEdition] = useState(
    reference.specialEdition || false,
  );
  const [discontinued, setDiscontinued] = useState(
    reference.knownDiscontinuation || false,
  );
  const [isLimited, setIsLimited] = useState(
    reference.production?.isLimited || false,
  );
  const [limitedSize, setLimitedSize] = useState(
    reference.production?.limitedSize?.toString() || '',
  );
  const [series, setSeries] = useState(reference.series || '');
  const [watchType, setWatchType] = useState(reference.watchType || '');
  const [occasion, setOccasion] = useState<string[]>(() => {
    const raw = reference.occasion;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === 'string' && !!v.trim());
    if (typeof raw === 'string') return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    return [];
  });

  // Load watch type lookup values to read properties for conditional section visibility
  const [watchTypeLookupValues, setWatchTypeLookupValues] = useState<
    LookupValue[]
  >([]);
  useEffect(() => {
    getLookupCollection('lookup_watch_types')
      .then(setWatchTypeLookupValues)
      .catch(() => {});
  }, []);

  const watchTypeProperties = useMemo(() => {
    if (!watchType || watchTypeLookupValues.length === 0) return [];
    const valueLower = watchType.toLowerCase();
    const match = watchTypeLookupValues.find(
      (lv) =>
        lv.value === watchType ||
        lv.displayName === watchType ||
        lv.value.toLowerCase() === valueLower ||
        lv.displayName.toLowerCase() === valueLower ||
        lv.synonyms?.some((s) => s.toLowerCase() === valueLower),
    );
    return match?.properties ?? [];
  }, [watchType, watchTypeLookupValues]);

  const showMovement = watchTypeProperties.includes('showMovement');
  const showModule = watchTypeProperties.includes('showModule');

  // Calibre write-path migration: both default ON, so accepting an AI calibre
  // suggestion writes everything it wrote before, plus the `calibreId` link.
  // Mirrors ReferenceBulkActionBar — see services/featureFlagsRegistry.ts.
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag(
    'calibre.dualWriteLegacyCalibreString',
  );
  // Explicit opt-in: only watch types whose lookup carries 'showDial' (e.g.
  // analog, ana-digi) expose the Dial & Hands section. Digital/smartwatch and
  // any unknown/missing type hide it.
  const showDial = watchTypeProperties.includes('showDial');
  // Explicit opt-in: only watch types whose lookup carries 'showPocketWatch'
  // (e.g. pocket_watch) expose the Pocket Watch section. All other types hide it.
  const showPocketWatch = watchTypeProperties.includes('showPocketWatch');
  // Opt-out: watch types whose lookup carries 'hideStrap' (e.g. stopwatch,
  // pocket_watch) don't have straps/bracelets, so hide the Strap section. This
  // only hides the UI — existing strap field values on the ref are preserved.
  const showStrap = !watchTypeProperties.includes('hideStrap');

  // Bezel. `bezel` is the MATERIAL (lookup_bezel_material) — the short name
  // predates the lookup and every reader is on it, so it stays. Single-select:
  // a bezel has one primary material, with `mixed` covering two-tone cases.
  const [bezel, setBezel] = useState(reference.bezel || '');
  const [bezelType, setBezelType] = useState(reference.bezelType || '');
  // Bezel colours (lookup_bezel_colors) — MULTI-select, string[]: a GMT-Master
  // "Pepsi" insert is ["red", "blue"]. parseMultiLookupValues also reads the
  // comma-joined shape an import or a re-applied AI suggestion can write.
  const [caseColors, setCaseColors] = useState<string[]>(() =>
    parseMultiLookupValues(reference.caseColors),
  );
  const [bezelColors, setBezelColors] = useState<string[]>(() =>
    parseMultiLookupValues(reference.bezelColors),
  );
  // Crown Type (lookup_crown_types) and Crystal (lookup_crystal_materials) are
  // both multi-select and both persist as string[]: a crown is an attachment
  // method AND a sealing system, a crystal is a material AND its features.
  // parseMultiLookupValues also reads the pre-migration scalar and the
  // comma-joined shape an import or a brand-portal edit writes, so a ref that
  // has not been through migrateCrownTypeCrystal still shows its value here
  // (as custom chips, which is the triage signal).
  const [crownType, setCrownType] = useState<string[]>(() =>
    parseMultiLookupValues(reference.crownType),
  );
  // Read-only now the Glass input is retired — carried purely so the save path
  // can fold a legacy value into `crystal` and re-write it unchanged.
  const glass = reference.glass || '';
  const [crystal, setCrystal] = useState<string[]>(() =>
    parseMultiLookupValues(reference.crystal),
  );
  const [dial, setDial] = useState(typeof reference.dial === 'string' ? reference.dial : '');

  // Movement Info
  const [movementType, setMovementType] = useState(
    reference.movementType || '',
  );
  const [frequencyVph, setFrequencyVph] = useState(
    reference.movement?.frequencyVph?.toString() || '',
  );
  const [jewels, setJewels] = useState(
    reference.movement?.jewels?.toString() || '',
  );
  const [powerReserveHours, setPowerReserveHours] = useState(
    reference.movement?.powerReserveHours?.toString() || '',
  );
  const [isChronometer, setIsChronometer] = useState(
    reference.movement?.isChronometer || false,
  );
  const [isCosc, setIsCosc] = useState(reference.movement?.isCosc || false);
  const [isGlashuetteRegulation, setIsGlashuetteRegulation] = useState(
    reference.movement?.isGlashuetteRegulation || false,
  );
  const [isGenevaSeal, setIsGenevaSeal] = useState(
    reference.movement?.isGenevaSeal || false,
  );
  const [isGrandSeikoStandard, setIsGrandSeikoStandard] = useState(
    reference.movement?.isGrandSeikoStandard || false,
  );
  const [isMETAS, setIsMETAS] = useState(
    reference.movement?.isMETAS || false,
  );
  const [hacking, setHacking] = useState(reference.movement?.hacking || false);
  const [manualWinding, setManualWinding] = useState(
    reference.movement?.manualWinding || false,
  );
  const [quickSetDate, setQuickSetDate] = useState(
    reference.movement?.quickSetDate || false,
  );
  const [windingDirection, setWindingDirection] = useState(
    reference.movement?.windingDirection || '',
  );
  const [baseMovement, setBaseMovement] = useState(
    reference.movement?.baseMovement || '',
  );
  const [movementDiameterMm, setMovementDiameterMm] = useState(
    reference.movement?.diameterMm?.toString() || '',
  );
  const [movementHeightMm, setMovementHeightMm] = useState(
    reference.movement?.heightMm?.toString() || '',
  );
  const [isChronograph, setIsChronograph] = useState(
    reference.movement?.isChronograph || false,
  );
  const [isGMT, setIsGMT] = useState(reference.movement?.isGMT || false);
  const [movementDate, setMovementDate] = useState<string[]>(
    toStringArray(reference.movement?.date),
  );
  const [movementHands, setMovementHands] = useState<string[]>(
    toStringArray(reference.movement?.hands),
  );
  const [movementChronograph, setMovementChronograph] = useState<string[]>(
    toStringArray(reference.movement?.chronograph),
  );
  const [movementAdditionals, setMovementAdditionals] = useState<string[]>(
    toStringArray(reference.movement?.additionals),
  );
  const [movementAstronomical, setMovementAstronomical] = useState<string[]>(
    toStringArray(reference.movement?.astronomical),
  );
  const [movementAcoustic, setMovementAcoustic] = useState<string[]>(
    toStringArray(reference.movement?.acoustic),
  );

  // Extended movement fields (from Caliber Corner)
  const [liftAngleDegrees, setLiftAngleDegrees] = useState(
    reference.movement?.liftAngleDegrees?.toString() || '',
  );
  const [isInHouse, setIsInHouse] = useState(
    reference.movement?.isInHouse || false,
  );
  const [countryOfManufacture, setCountryOfManufacture] = useState(
    reference.movement?.countryOfManufacture || '',
  );
  const [antiShockSystem, setAntiShockSystem] = useState(
    reference.movement?.antiShockSystem || '',
  );
  const [rotorType, setRotorType] = useState(
    reference.movement?.rotorType || '',
  );
  const [columnWheel, setColumnWheel] = useState(
    reference.movement?.columnWheel || false,
  );
  const [serviceIntervalYears, setServiceIntervalYears] = useState(
    reference.movement?.serviceIntervalYears?.toString() || '',
  );
  const [lignes, setLignes] = useState(
    reference.movement?.lignes || '',
  );
  const [regulatorSystem, setRegulatorSystem] = useState(
    reference.movement?.regulatorSystem || '',
  );
  const [casingDiameterMm, setCasingDiameterMm] = useState(
    reference.movement?.casingDiameterMm?.toString() || '',
  );
  const [manualWindingDirection, setManualWindingDirection] = useState(
    reference.movement?.manualWindingDirection || '',
  );
  const [hairspring, setHairspring] = useState(
    reference.movement?.hairspring || '',
  );
  const [doubleBarrel, setDoubleBarrel] = useState(
    reference.movement?.doubleBarrel || false,
  );
  const [escapement, setEscapement] = useState(
    reference.movement?.escapement || '',
  );
  const [batteryType, setBatteryType] = useState(
    reference.movement?.batteryType || '',
  );
  const [batteryLifeYears, setBatteryLifeYears] = useState(
    reference.movement?.batteryLifeYears?.toString() || '',
  );

  // Electronics/Module Info (for Digital and Hybrid watches)
  // Link to a shared electronicModules entity. When set, the inline electronics
  // fields below become an auto-synced cache (populated by the Cloud Function
  // fan-out trigger) — we only persist the id here.
  const [moduleId, setModuleId] = useState(reference.moduleId || '');
  // Freshly-fetched linked module doc — the source of truth for the read-only
  // "Module details" summary. Fetched on `moduleId` change (and manually via the
  // refresh button) so linking or an edit on the module editor is reflected
  // immediately, without waiting for the server-side `electronics.*` cache
  // trigger or a navigation cycle. Null while unlinked / in-flight / on error,
  // in which case the summary falls back to the ref's `electronics` cache.
  const [linkedModule, setLinkedModule] = useState<ElectronicModule | null>(null);
  const [refreshingModule, setRefreshingModule] = useState(false);

  // Fetch the linked module doc whenever the link changes.
  useEffect(() => {
    let cancelled = false;
    const id = moduleId.trim();
    if (!id) {
      setLinkedModule(null);
      return;
    }
    (async () => {
      try {
        const m = await getElectronicModuleById(id);
        if (!cancelled) setLinkedModule(m);
      } catch (err) {
        if (!cancelled) console.error('Failed to load linked module', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId]);

  // Manual refresh (button next to the module chip) — re-pull the module doc so
  // an edit made on the module editor shows here without leaving the ref editor.
  const handleRefreshModule = async () => {
    const id = moduleId.trim();
    if (!id) return;
    setRefreshingModule(true);
    try {
      const m = await getElectronicModuleById(id);
      setLinkedModule(m);
      if (m) toast.success('Module details refreshed');
    } catch (err) {
      console.error('Failed to refresh linked module', err);
      toast.error('Failed to refresh module details');
    } finally {
      setRefreshingModule(false);
    }
  };

  const [elecModule, setElecModule] = useState(
    reference.electronics?.module || '',
  );
  const [elecBattery, setElecBattery] = useState(
    reference.electronics?.battery || '',
  );
  const [elecBatteryLife, setElecBatteryLife] = useState(
    reference.electronics?.batteryLife || '',
  );
  const [elecDisplay, setElecDisplay] = useState(
    reference.electronics?.display || '',
  );
  const [elecLcdType, setElecLcdType] = useState(
    reference.electronics?.lcdType || '',
  );
  const [elecLightType, setElecLightType] = useState(
    reference.electronics?.lightType || '',
  );
  const [elecChargeTime, setElecChargeTime] = useState(
    reference.electronics?.chargeTime || '',
  );
  // Per-ref display colours (never module-sourced — admin owns these). Canonical
  // location is the nested electronics.* path; we read + write there only.
  const [displayColor, setDisplayColor] = useState(
    reference.electronics?.displayColor || '',
  );
  const [textColor, setTextColor] = useState(
    reference.electronics?.textColor || '',
  );
  const [backlightColor, setBacklightColor] = useState(
    reference.electronics?.backlightColor || '',
  );

  // Pocket Watch Info — nested under WatchReference.pocketWatch. Only surfaced
  // when the watchType lookup carries the `showPocketWatch` property.
  const [pwGrade, setPwGrade] = useState(reference.pocketWatch?.grade || '');
  const [pwCaseStyle, setPwCaseStyle] = useState(
    reference.pocketWatch?.caseStyle || '',
  );
  const [pwEscapement, setPwEscapement] = useState(
    reference.pocketWatch?.escapement || '',
  );
  const [pwJewelBearing, setPwJewelBearing] = useState(
    reference.pocketWatch?.jewelBearing || '',
  );
  const [pwMovementSize, setPwMovementSize] = useState(
    reference.pocketWatch?.movementSize || '',
  );
  const [pwAdjustments, setPwAdjustments] = useState<string[]>(
    reference.pocketWatch?.adjustments ?? [],
  );
  const [pwIsKeyWound, setPwIsKeyWound] = useState(
    reference.pocketWatch?.isKeyWound ?? false,
  );
  const [pwIsFusee, setPwIsFusee] = useState(
    reference.pocketWatch?.isFusee ?? false,
  );
  // Mount-time exists-check for the legacy elec* rows: legacy value → matching
  // module (if one exists by name/AKA). Drives the Link / AI / Add-as-AKA
  // affordances on each read-only legacy row.
  const [legacyModuleMatches, setLegacyModuleMatches] = useState<
    Map<string, ElectronicModule>
  >(new Map());
  // Legacy value currently being pushed onto the linked module's alsoKnownAs[].
  const [legacyAkaBusy, setLegacyAkaBusy] = useState<string | null>(null);

  // Mount-time batch exists-check for the legacy elec* values. One lookup covers
  // every legacy row so each can decide whether a matching module already exists.
  useEffect(() => {
    if (!showModule) return;
    const e = reference.electronics;
    const legacyValues = [
      e?.module,
      e?.battery,
      e?.batteryLife,
      e?.chargeTime,
      e?.display,
      e?.lcdType,
      e?.lightType,
    ].filter((v): v is string => !!v && v.trim().length > 0);
    if (legacyValues.length === 0) {
      setLegacyModuleMatches(new Map());
      return;
    }
    let cancelled = false;
    findModulesByLegacyValues(legacyValues)
      .then((m) => {
        if (!cancelled) setLegacyModuleMatches(m);
      })
      .catch((err) => console.warn('Legacy module exists-check failed:', err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModule, reference.id]);

  // Load movement type lookup values to check properties (e.g., needsBattery)
  const [movementTypeLookupValues, setMovementTypeLookupValues] = useState<
    LookupValue[]
  >([]);
  useEffect(() => {
    getLookupCollection('lookup_movement_types')
      .then(setMovementTypeLookupValues)
      .catch(() => {});
  }, []);

  // Cache of the lookup-backed movement collections (keyed by collection name),
  // so the read-only linked-movement list can resolve legacy label ↔ calibre id
  // synchronously at render time. See MOVEMENT_LOOKUP_COLLECTIONS.
  //
  // Loaded when the Movement section is OPEN — every display consumer
  // (CalibreSpecDetail, RefCalibreCachePanel, the legacy-values diff) renders
  // inside it, and it is collapsed by default, so this used to fetch 11 lookup
  // documents on every ref-editor mount for a panel nobody had asked to see.
  //
  // ALSO loaded whenever the ref is LINKED (`calibreId` set), open section or
  // not: `computeGroupAMigrations` runs on save for linked refs and resolves
  // both sides through this cache before deciding a legacy value diverges from
  // the calibre's. With the cache empty it would compare a display label
  // ("Côtes de Genève") against a slug ("cotes_de_geneve") and write a spurious
  // per-ref override. Correctness beats the 11 reads there.
  //
  // The lookup fields inside the section read the same documents through
  // `getLookupCollectionFull`'s session cache, so opening Movement no longer
  // refetches what this prefetch already paid for.
  const [movementLookupCache, setMovementLookupCache] = useState<
    Record<string, LookupValue[]>
  >({});
  const needsMovementLookups = expandedSections.movement || !!calibreId;
  useEffect(() => {
    if (!needsMovementLookups) return;
    const collections = Array.from(
      new Set(Object.values(MOVEMENT_LOOKUP_COLLECTIONS)),
    );
    let cancelled = false;
    Promise.all(
      collections.map((c) =>
        getLookupCollection(c)
          .then((v) => [c, v] as const)
          .catch(() => [c, [] as LookupValue[]] as const),
      ),
    ).then((pairs) => {
      if (!cancelled) setMovementLookupCache(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [needsMovementLookups]);

  // Map of movement_manufacturers/{id} → name, so the effective-spec panel can
  // resolve a linked calibre's `manufacturerId` FK to a human name instead of
  // rendering the raw slug. Loaded once when a calibre is linked (unlinked refs
  // never show the panel).
  //
  // From `watchkeeper_manufacturers`, not Firestore: this used to read all 176
  // `movement_manufacturers` documents on every open of a calibre-linked ref,
  // purely to turn one slug into one name. Only `name` is wanted here and the
  // index carries it.
  //
  // Through the shared hook, which also keeps it current: a manufacturer created
  // from the calibre editor's quick-create used to leave this panel rendering
  // the raw slug until the whole page was reloaded.
  const { manufacturers: movementManufacturers } = useManufacturerCatalogue({
    enabled: !!calibreId,
  });
  const manufacturerNameById = useMemo(
    () => Object.fromEntries(movementManufacturers.map((m) => [m.id, m.name])),
    [movementManufacturers],
  );

  // Collapsible state for the Movement sub-sections. Overrides auto-expands when
  // the ref already carries an active override; Legacy movement values auto-
  // expands only when a stored value diverges from the linked calibre (a
  // conflict worth surfacing) — otherwise both start collapsed.
  const [overridesOpen, setOverridesOpen] = useState(
    () => countCalibreOverrides(reference.calibreOverrides) > 0,
  );
  // `null` = untouched → defaults to "open when a legacy value diverges from the
  // linked calibre". Once the admin toggles it, their explicit choice sticks.
  const [legacyMovementOpen, setLegacyMovementOpen] = useState<boolean | null>(
    null,
  );

  const movementTypeNeedsBattery = useMemo(() => {
    if (!movementType || movementTypeLookupValues.length === 0) return false;
    const valueLower = movementType.toLowerCase();
    const match = movementTypeLookupValues.find(
      (lv) =>
        lv.value === movementType ||
        lv.displayName === movementType ||
        lv.value.toLowerCase() === valueLower ||
        lv.displayName.toLowerCase() === valueLower ||
        lv.synonyms?.some((s) => s.toLowerCase() === valueLower),
    );
    return match?.properties?.includes('needsBattery') ?? false;
  }, [movementType, movementTypeLookupValues]);

  // Case Info (each field writes to multiple Firebase properties on save)
  const [caseDiameter, setCaseDiameter] = useState(
    reference.case?.diameterMm?.toString() ||
      reference.case?.sizeMm?.toString() ||
      reference.case?.diameter?.toString() ||
      '',
  );
  const [caseThickness, setCaseThickness] = useState(
    reference.case?.heightMm?.toString() ||
      reference.case?.thickness?.toString() ||
      '',
  );
  const [caseLugToLug, setCaseLugToLug] = useState(
    reference.case?.lugToLugMm?.toString() ||
      reference.case?.lugToLug?.toString() ||
      '',
  );
  const [caseLugWidth, setCaseLugWidth] = useState(
    reference.case?.lugWidthMm?.toString() ||
      reference.case?.lugWidth?.toString() ||
      '',
  );
  const [caseMaterial, setCaseMaterial] = useState(
    reference.case?.material || '',
  );
  const [caseWaterResistance, setCaseWaterResistance] = useState(
    reference.case?.waterResistanceM?.toString() ||
      reference.case?.waterResistance ||
      '',
  );
  const [caseShape, setCaseShape] = useState(reference.case?.shape || '');
  const [caseCaseBack, setCaseCaseBack] = useState(
    reference.case?.caseBack || reference.case?.caseback || '',
  );
  const [caseCode, setCaseCode] = useState(reference.case?.code || '');
  const [caseCoating, setCaseCoating] = useState(reference.case?.coating || '');
  const [caseFinish, setCaseFinish] = useState(reference.case?.finish || '');
  const [caseWeightG, setCaseWeightG] = useState(
    reference.case?.weightG?.toString() || '',
  );

  // Dial and Hands
  // Dial color as multi-select array — parse from dialAndHands.color, falling back to dial field
  const [dialColors, setDialColors] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.color || reference.dial || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return raw.map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });
  // Dial STYLE / pattern — top-level single-value lookup (lookup_dial_styles),
  // distinct from dial color + dial finish.
  const [dialStyle, setDialStyle] = useState(reference.dialStyle || '');
  const [dialMaterial, setDialMaterial] = useState(
    reference.dialAndHands?.material || '',
  );
  const [dialFinish, setDialFinish] = useState(
    reference.dialAndHands?.finish || '',
  );
  const [dialHands, setDialHands] = useState(
    reference.dialAndHands?.hands || '',
  );
  // Marker TYPE (how the markers are made — applied/printed/sandwich/…), stored
  // at dialAndHands.indexes under its historical name because the RN app reads
  // that key. Multi-select, so string[]; parseDialMarkerValues also accepts the
  // pre-migration scalar and the comma-joined shape a brand-portal edit writes.
  const [dialIndexes, setDialIndexes] = useState<string[]>(() =>
    parseDialMarkerValues(reference.dialAndHands?.indexes),
  );
  // Marker STYLE (what the markers are — arabic/roman/stick/…). Same shape story.
  const [dialNumerals, setDialNumerals] = useState<string[]>(() =>
    parseDialMarkerValues(reference.dialAndHands?.numerals),
  );
  const [dialLuminova, setDialLuminova] = useState(
    reference.dialAndHands?.luminova || false,
  );
  // Lume detail — four single-select lookups, flat under dialAndHands (there
  // are no nested sub-objects on DialInfo). The DETAIL behind the Luminova
  // checkbox above, which stays: it is the boolean the RN app reads and every
  // legacy import set, and "does it glow" is worth keeping next to "with what".
  // lumePresence gates the other three the way dateDisplay gates the date block.
  const dialLumeSeed = reference.dialAndHands ?? {};
  const [lumePresence, setLumePresence] = useState(dialLumeSeed.lumePresence || '');
  const [lumeMaterial, setLumeMaterial] = useState(dialLumeSeed.lumeMaterial || '');
  const [lumeDayColor, setLumeDayColor] = useState(dialLumeSeed.lumeDayColor || '');
  const [lumeGlowColor, setLumeGlowColor] = useState(dialLumeSeed.lumeGlowColor || '');
  // Date complication — five lookups, flat under dialAndHands (there are no
  // nested sub-objects on DialInfo). dateDisplay gates the other four: a
  // pointer_date or subsidiary_dial has no aperture, so its position / frame /
  // wheel colours stay empty.
  const dialDateSeed = reference.dialAndHands ?? {};
  const [dateDisplay, setDateDisplay] = useState(dialDateSeed.dateDisplay || '');
  const [dateWindowPosition, setDateWindowPosition] = useState(
    dialDateSeed.dateWindowPosition || '',
  );
  const [dateWindowFrame, setDateWindowFrame] = useState(
    dialDateSeed.dateWindowFrame || '',
  );
  const [dateWheelColor, setDateWheelColor] = useState(
    dialDateSeed.dateWheelColor || '',
  );
  // The one MULTI-select of the five: the colour(s) of the numerals PRINTED ON
  // the disc, as against dateWheelColor which is the disc's own background. A
  // day-date routinely prints the weekend positions in red and the weekdays in
  // black on a single wheel. Comma-joined in storage, same convention as
  // handsColors / indexColors above, so the seed splits the same way.
  const [dateWheelTextColors, setDateWheelTextColors] = useState<string[]>(() => {
    const raw: unknown = dialDateSeed.dateWheelTextColor;
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return raw.map((v) => String(v ?? '').trim()).filter(Boolean);
    return [];
  });
  // Hands color as multi-select array — hands often have several colors
  // (e.g. silver hour/minute + orange seconds). Parsed from the comma-joined
  // dialAndHands.handsColor string, same convention as dialColors above.
  const [handsColors, setHandsColors] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.handsColor || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as any[]).map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });
  // Index/marker colors as multi-select array — comma-joined dialAndHands.indexColor
  // string, same convention as handsColors above.
  const [indexColors, setIndexColors] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.indexColor || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as any[]).map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });
  // Functional hand types as multi-select array — comma-joined
  // dialAndHands.handTypes string, same convention as handsColors above.
  const [handTypes, setHandTypes] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.handTypes || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as any[]).map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });
  const [dialSubdials, setDialSubdials] = useState(
    reference.dialAndHands?.subdials?.toString() || '',
  );
  // WHAT each subdial does, as a multi-select array — comma-joined
  // dialAndHands.subdialTypes string, same convention as handTypes above.
  // The companion to the Subdials COUNT above, not a replacement for it: three
  // registers is where a chronograph, a triple calendar and a regulator all
  // land, so the count alone never says which one this is. Deliberately not
  // validated against the count either — the count comes off any dial photo,
  // the functions usually need the printed register labels, so one filled
  // without the other is normal.
  const [subdialTypes, setSubdialTypes] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.subdialTypes || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as any[]).map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });

  // Chapter ring & dial furniture. Three single-select slugs plus one
  // multi-select. None of them gates another — unlike the Lume block, where
  // Presence `none` means there is nothing left to describe, a dial with no
  // chapter ring still carries a minute track printed on its face, and every
  // dial has some logo treatment.
  const [chapterRing, setChapterRing] = useState(
    reference.dialAndHands?.chapterRing || '',
  );
  const [minuteTrack, setMinuteTrack] = useState(
    reference.dialAndHands?.minuteTrack || '',
  );
  // The measurement scales the watch carries, as a multi-select array —
  // comma-joined dialAndHands.dialScales, same convention as subdialTypes
  // above. Covers the whole watch rather than only the dial: a Daytona's bezel
  // tachymeter belongs here, which is why the picker sits in this section
  // despite the scale not being printed on the dial.
  const [dialScales, setDialScales] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.dialScales || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as any[]).map((v: any) => typeof v === 'string' ? v : v?.displayValue || '').filter(Boolean);
    return [];
  });
  // Structural / decorative dial attributes — skeletonisation, apertures,
  // inlays, gem setting. Comma-joined at dialAndHands.dialFeatures, the same
  // convention as dialScales above. Distinct from `finish` (surface treatment)
  // and `material` (what the dial is made of): a meteorite DIAL is a material,
  // a meteorite INLAY is a feature.
  const [dialFeatures, setDialFeatures] = useState<string[]>(() => {
    const raw = reference.dialAndHands?.dialFeatures || '';
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[,;]+/).map((v) => v.trim()).filter(Boolean);
    }
    if (Array.isArray(raw)) return (raw as unknown[]).map((v) => String(v ?? '').trim()).filter(Boolean);
    return [];
  });
  const [logoType, setLogoType] = useState(
    reference.dialAndHands?.logoType || '',
  );

  // Strap
  const [strapMaterial, setStrapMaterial] = useState(
    reference.strap?.material || '',
  );
  const [strapColor, setStrapColor] = useState(reference.strap?.color || '');
  const [strapWidth, setStrapWidth] = useState(
    reference.strap?.width?.toString() || '',
  );
  const [strapBuckleType, setStrapBuckleType] = useState(
    reference.strap?.buckleType || '',
  );
  const [strapIsMicroAdjustable, setStrapIsMicroAdjustable] = useState(
    reference.strap?.isMicroAdjustable || false,
  );
  /*
   * A CASE-construction fact, stored top-level rather than inside `strap` —
   * an integrated watch still has strap details (its bracelet has a material
   * and a width), so the flag would be misfiled there and would vanish
   * whenever the strap object is absent. Edited here because this is where a
   * curator is already thinking about the band.
   */
  const [hasIntegratedBracelet, setHasIntegratedBracelet] = useState(
    reference.hasIntegratedBracelet || false,
  );

  // Production
  const [productionStartYear, setProductionStartYear] = useState(
    reference.production?.startYear?.toString() || '',
  );
  const [productionEndYear, setProductionEndYear] = useState(
    reference.production?.endYear?.toString() || '',
  );
  const [productionReleaseYear, setProductionReleaseYear] = useState(
    reference.production?.releaseYear?.toString() || '',
  );

  // Functions (multi-select)
  const [functions, setFunctions] = useState<string[]>(
    toStringArray(reference.functions),
  );

  // Links - initialize from existing links, or from source.url if no links exist
  const [links, setLinks] = useState<WatchLink[]>(() => {
    if (reference.links && reference.links.length > 0) {
      return reference.links;
    }
    if (reference.source?.url) {
      return [{ url: reference.source.url, title: 'Source' }];
    }
    return [];
  });
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');

  // Dedicated product-page URL surfaced as the RN "View product page" link.
  const [productUrl, setProductUrl] = useState(reference.productUrl || '');

  // --- Variants / Variant Aggregates (parent refs only) ---
  // How many refs point at this doc via parentReferenceId. Fetched once on mount
  // with a single aggregation query; a non-zero count makes this doc a variant
  // PARENT, which gates both the Variants panel and the Variant Aggregates
  // section. `aggregatesLoading` keeps them hidden until the count resolves.
  const [variantCount, setVariantCount] = useState(0);
  const isVariantParent = variantCount > 0;
  const [aggregatesLoading, setAggregatesLoading] = useState(true);
  const [recomputingAggregates, setRecomputingAggregates] = useState(false);
  // Current array values for each aggregate field (the editable buffer).
  const [aggValues, setAggValues] = useState<Record<AggregateField, string[]>>(
    () => {
      const r = reference as Partial<Record<AggregateField, unknown>>;
      return {
        dialColorVariants: toStringArray(r.dialColorVariants),
        dialStyleVariants: toStringArray(r.dialStyleVariants),
        dialFinishVariants: toStringArray(r.dialFinishVariants),
        strapTypeVariants: toStringArray(r.strapTypeVariants),
        caseMaterialVariants: toStringArray(r.caseMaterialVariants),
        movementVariants: toStringArray(r.movementVariants),
      };
    },
  );
  // Per-field manual-override flags. true => this field is hand-edited and the
  // propagation trigger leaves it alone; false => auto-managed.
  const [aggOverrides, setAggOverrides] = useState<
    Record<AggregateField, boolean>
  >(() => {
    const o =
      (reference as { aggregateOverrides?: Record<string, boolean> })
        .aggregateOverrides || {};
    return {
      dialColorVariants: o.dialColorVariants === true,
      dialStyleVariants: o.dialStyleVariants === true,
      dialFinishVariants: o.dialFinishVariants === true,
      strapTypeVariants: o.strapTypeVariants === true,
      caseMaterialVariants: o.caseMaterialVariants === true,
      movementVariants: o.movementVariants === true,
    };
  });

  // Images
  const [images, setImages] = useState<ImageInfo[]>(reference.images || []);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loadedImageUrl, setLoadedImageUrl] = useState<string | null>(null);
  // "Set from URL": pull the main image straight off a brand page. Distinct
  // from the Source URL field below the image, which only records provenance.
  const [showImageUrlInput, setShowImageUrlInput] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [fetchingImageUrl, setFetchingImageUrl] = useState(false);

  // Gallery Images
  const [galleryImages, setGalleryImages] = useState<ImageInfo[]>(
    reference.galleryImages || [],
  );
  const [galleryImageUrls, setGalleryImageUrls] = useState<(string | null)[]>(
    [],
  );
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [replacingGalleryIndex, setReplacingGalleryIndex] = useState<
    number | null
  >(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Load image URL from storage path
  useEffect(() => {
    if (images.length > 0 && images[0].storagePath) {
      getImageUrl(images[0].storagePath)
        .then((url) => setLoadedImageUrl(url))
        .catch((err) => console.error('Failed to load image:', err));
    }
  }, [images]);

  // Load gallery image URLs
  useEffect(() => {
    if (galleryImages.length === 0) {
      setGalleryImageUrls([]);
      return;
    }
    Promise.all(
      galleryImages.map((img) =>
        getImageUrl(img.storagePath).catch(() => null),
      ),
    ).then(setGalleryImageUrls);
  }, [galleryImages]);

  // Count this doc's variants (gates the Variants panel + Variant Aggregates
  // section). One aggregation query on mount, no docs read. A doc that is ITSELF
  // a variant can never be a parent — the importer doesn't nest — so skip the
  // query entirely there. Failures fail closed (count 0, sections stay hidden)
  // and never block the editor.
  useEffect(() => {
    if (reference.parentReferenceId) {
      setVariantCount(0);
      setAggregatesLoading(false);
      return;
    }
    let cancelled = false;
    setAggregatesLoading(true);
    getVariantCountForParent(brand.id, reference.id)
      .then((count) => {
        if (!cancelled) setVariantCount(count);
      })
      .catch((err) =>
        console.error('Failed to count variants for parent:', err),
      )
      .finally(() => {
        if (!cancelled) setAggregatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand.id, reference.id, reference.parentReferenceId]);

  // Mark as mounted after initial render cycle settles (covers Strict Mode double-invoke
  // AND async effects like image loading / lookup fetches that fire during mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      isMounted.current = true;
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // ── Unsaved-changes guard ──────────────────────────────────────────────────
  // Mirror `hasChanges` into a ref so the stable callbacks below (the exit
  // guard registered with a wrapping shell, and the beforeunload listener) can
  // read the latest value without re-subscribing on every keystroke.
  const hasChangesRef = useRef(false);
  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  // Guarded exit: run `proceed` immediately when the form is clean, otherwise
  // stash it and open the confirm dialog. Stable identity (reads the ref) so it
  // can be handed to parents without churn.
  const requestExit = useCallback((proceed: () => void) => {
    if (!hasChangesRef.current) {
      proceed();
      return;
    }
    setPendingExit({ proceed });
  }, []);

  // Expose the guard to a wrapping shell (Modal Esc/backdrop, external
  // breadcrumb) via the optional bridge ref. Cleared on unmount so a parent
  // never calls a stale guard once the editor is gone.
  useEffect(() => {
    if (!exitGuardRef) return;
    exitGuardRef.current = requestExit;
    return () => {
      if (exitGuardRef) exitGuardRef.current = null;
    };
  }, [exitGuardRef, requestExit]);

  // Browser Back / tab close / reload: fire the native "leave site?" prompt
  // while there are pending changes. Browsers ignore any custom message and
  // show their own generic warning.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasChangesRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Track changes - only after mount has settled
  useEffect(() => {
    if (!isMounted.current) return;
    setHasChanges(true);
  }, [
    refNumber,
    model,
    collectionName,
    productName,
    description,
    calibre,
    calibreId,
    calibreOverrides,
    calibreTierId,
    nickname,
    specialEdition,
    discontinued,
    isLimited,
    limitedSize,
    series,
    watchType,
    occasion,
    bezel,
    bezelType,
    bezelColors,
    caseColors,
    crownType,
    glass,
    crystal,
    dial,
    movementType,
    frequencyVph,
    jewels,
    powerReserveHours,
    isChronometer,
    isCosc,
    isGlashuetteRegulation,
    isGenevaSeal,
    isGrandSeikoStandard,
    isMETAS,
    hacking,
    manualWinding,
    quickSetDate,
    windingDirection,
    baseMovement,
    movementDiameterMm,
    movementHeightMm,
    isChronograph,
    isGMT,
    movementDate,
    movementHands,
    movementChronograph,
    movementAdditionals,
    movementAstronomical,
    movementAcoustic,
    liftAngleDegrees,
    isInHouse,
    countryOfManufacture,
    antiShockSystem,
    rotorType,
    columnWheel,
    serviceIntervalYears,
    lignes,
    regulatorSystem,
    casingDiameterMm,
    manualWindingDirection,
    hairspring,
    doubleBarrel,
    escapement,
    batteryType,
    batteryLifeYears,
    moduleId,
    elecModule,
    elecBattery,
    elecBatteryLife,
    elecDisplay,
    elecLcdType,
    elecLightType,
    elecChargeTime,
    displayColor,
    textColor,
    backlightColor,
    pwGrade,
    pwCaseStyle,
    pwEscapement,
    pwJewelBearing,
    pwMovementSize,
    pwAdjustments,
    pwIsKeyWound,
    pwIsFusee,
    caseDiameter,
    caseThickness,
    caseLugToLug,
    caseLugWidth,
    caseMaterial,
    caseWaterResistance,
    caseShape,
    caseCaseBack,
    caseCode,
    caseCoating,
    caseFinish,
    caseWeightG,
    dialColors,
    dialStyle,
    dialMaterial,
    dialFinish,
    dialHands,
    dialIndexes,
    dialNumerals,
    dateDisplay,
    dateWindowPosition,
    dateWindowFrame,
    dateWheelColor,
    dateWheelTextColors,
    dialLuminova,
    lumePresence,
    lumeMaterial,
    lumeDayColor,
    lumeGlowColor,
    handsColors,
    dialSubdials,
    subdialTypes,
    chapterRing,
    minuteTrack,
    dialScales,
    dialFeatures,
    logoType,
    strapMaterial,
    strapColor,
    strapWidth,
    strapBuckleType,
    strapIsMicroAdjustable,
    hasIntegratedBracelet,
    productionStartYear,
    productionEndYear,
    productionReleaseYear,
    functions,
    links,
    productUrl,
    aggValues,
    aggOverrides,
  ]);

  // Sync dial and dialAndHands.color
  const handleDialChange = (values: string[]) => {
    setDial(values.join(', '));
    setDialColors(values);
  };

  const handleAggValueChange = (key: AggregateField, values: string[]) =>
    setAggValues((prev) => ({ ...prev, [key]: values }));

  const handleAggOverrideToggle = (key: AggregateField, on: boolean) =>
    setAggOverrides((prev) => ({ ...prev, [key]: on }));

  // Recompute now — hands every (non-overridden) parent in THIS BRAND back to
  // auto-management immediately, instead of waiting for the next variant write.
  // Brand-scoped (the callable's narrowest unit); overridden fields are skipped
  // server-side, so this never clobbers a manual edit. Save first if there are
  // pending edits — the recompute reads what's currently persisted.
  const handleRecomputeAggregates = async () => {
    setRecomputingAggregates(true);
    try {
      const res = await backfillParentVariantAggregates({ brandId: brand.id });
      toast.success(
        `Recomputed ${brand.name} aggregates — ${res.parentsUpdated} parent(s) updated. Reopen this reference to see refreshed values.`,
      );
    } catch (err) {
      console.error('Recompute aggregates failed:', err);
      toast.error('Recompute failed (is the backfill function deployed?)');
    } finally {
      setRecomputingAggregates(false);
    }
  };

  // Link a legacy-matched module to this ref (persisted on save with moduleId).
  const handleLinkLegacyModule = (m: ElectronicModule) => {
    setModuleId(m.id);
    toast.success(`Linked module “${m.name}” — save to persist`);
  };

  // Fold a legacy elec* value into the linked module's alsoKnownAs[]. Writes
  // straight to the module doc (arrayUnion) — independent of the ref save.
  const handleAddLegacyAka = async (value: string) => {
    if (!moduleId.trim()) return;
    setLegacyAkaBusy(value);
    try {
      await addAliasToElectronicModule(moduleId.trim(), value);
      toast.success(`Added “${value}” as an alias on the linked module`);
    } catch (err) {
      console.error('Add-as-AKA failed:', err);
      toast.error('Failed to add alias');
    } finally {
      setLegacyAkaBusy(null);
    }
  };

  // Best-effort manufacturer pre-fill for "Create New": only when the brand has
  // exactly one manually-linked manufacturer (an unambiguous, safe resolution).
  const defaultModuleManufacturerId =
    brand.manufacturerIdsManualInclude?.length === 1
      ? brand.manufacturerIdsManualInclude[0]
      : undefined;

  // Build the `suggestElectronicModule` context from the current ref: brand +
  // product name + whatever legacy inline electronics values are set. Lazy so it
  // captures the latest state at click time. An optional `hint` (a specific
  // legacy row value) narrows the request for the per-row mini-button.
  const buildModuleAiContext = (hint?: string): SuggestElectronicModuleInput => {
    const legacyValues: NonNullable<SuggestElectronicModuleInput['legacyValues']> =
      {};
    if (elecModule.trim()) legacyValues.module = elecModule.trim();
    if (elecDisplay.trim()) legacyValues.display = elecDisplay.trim();
    if (elecBattery.trim()) legacyValues.battery = elecBattery.trim();
    if (elecLightType.trim()) legacyValues.lightType = elecLightType.trim();
    if (elecLcdType.trim()) legacyValues.lcdType = elecLcdType.trim();
    return {
      brand: brand.name || undefined,
      productName: productName.trim() || model.trim() || undefined,
      ...(Object.keys(legacyValues).length > 0 ? { legacyValues } : {}),
      ...(hint?.trim() ? { hint: hint.trim() } : {}),
    };
  };

  // Link a module surfaced by the AI lookup (existing match or freshly created).
  const handleLinkModuleFromAi = (id: string, name: string) => {
    setModuleId(id);
    toast.success(
      name ? `Linked module “${name}” — save to persist` : 'Module linked — save to persist',
    );
  };

  const handleClone = () => {
    if (!onClone) return;

    // Clone everything except reference, images, and id.
    const cloneData: Partial<WatchReference> = {
      model,
      collection: collectionName || undefined,
      productName: productName || undefined,
      description: description || undefined,
      calibre: calibre || undefined,
      // Clone the tier alongside the calibre — same calibre ⇒ same tier applies.
      calibreTierId: calibreTierId || undefined,
      movementType: movementType || undefined,
      nickname: nickname || undefined,
      specialEdition,
      knownDiscontinuation: discontinued,
      series: series || undefined,
      watchType: watchType || undefined,
      occasion: occasion.length > 0 ? occasion.join(', ') : undefined,
      bezel: bezel || undefined,
      bezelType: bezelType || undefined,
      bezelColors: multiLookupValuesForWrite(bezelColors),
      caseColors: multiLookupValuesForWrite(caseColors),
      crownType: multiLookupValuesForWrite(crownType),
      glass: glass || undefined,
      crystal: multiLookupValuesForWrite(crystal),
      dial: dial || undefined,
      dialStyle: dialStyle.trim() || undefined,
      movement: reference.movement,
      case: reference.case,
      dialAndHands: reference.dialAndHands,
      strap: reference.strap,
      electronics: reference.electronics,
      production: reference.production,
      functions: functions.length > 0 ? functions : undefined,
      links: links.length > 0 ? links : undefined,
    };

    onClone(cloneData);
  };

  const handleDelete = async () => {
    setIsProcessing(true);
    try {
      const imagePaths: string[] = [
        ...(reference.images ?? []).map((img) => img.storagePath),
        ...(galleryImages ?? []).map((img) => img.storagePath),
      ].filter(Boolean);
      await deleteWatchReference(brand.id, reference.id, imagePaths);
      toast.success('Reference deleted');
      setShowDeleteConfirm(false);
      (onDeleted ?? onBack)();
    } catch (error) {
      console.error('Error deleting reference:', error);
      toast.error('Failed to delete reference');
    } finally {
      setIsProcessing(false);
    }
  };

  const [showSlugMappings, setShowSlugMappings] = useState(false);
  const [showCustomCalibres, setShowCustomCalibres] = useState(false);
  const [showImportRules, setShowImportRules] = useState(false);

  // AI enrichment
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  // Pre-enrichment dialog: gathers optional extra context before running the
  // AI. "Fetch product URL" defaults OFF (cost control — it scrapes the page).
  const [showAIPreDialog, setShowAIPreDialog] = useState(false);
  const [aiFetchUrl, setAIFetchUrl] = useState(false);
  // "Use product images" defaults ON: the whole reason it exists is that
  // text-only enrichment was guessing dial colour / numerals / hand types from
  // the product name. It only has an effect when the ref has images, and adds
  // ~$0.005 per image sent.
  const [aiUseImage, setAIUseImage] = useState(true);
  /**
   * A ONE-OFF model override for the next run. `''` means the model configured
   * for enrichReference, which is the default and what it resets to after every
   * run — a curator reaching for Opus on a hard reference should not silently
   * keep paying for it on the next twenty.
   */
  const [aiModel, setAIModel] = useState('');
  const aiModelChoices = useAnthropicModels({ curated: true });
  const [aiAdditionalContext, setAIAdditionalContext] = useState('');
  const [aiApplying, setAIApplying] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState<Record<string, unknown>>({});
  const [aiConfidence, setAIConfidence] = useState<Record<string, string>>({});
  const [aiAccepted, setAIAccepted] = useState<Record<string, boolean>>({});
  /** Why each row is (or is not) pre-ticked — the row's tooltip. */
  const [aiAcceptReason, setAIAcceptReason] = useState<Record<string, CheckedReason>>({});
  // Suggestion keys whose value could NOT be matched against the field's lookup
  // list. The enrichReference callable already strict-drops these server-side,
  // so this should stay empty in practice — it's defence in depth for an older
  // deployed function or a lookup that was unreadable server-side. Anything in
  // here is never auto-accepted and is flagged in the review table, so a human
  // has to make the call.
  const [aiUnverified, setAIUnverified] = useState<Record<string, boolean>>({});
  /**
   * What the AI's proposed `calibre` string resolves to in `custom_calibres`,
   * checked BEFORE the review dialog opens.
   *
   * The link itself is still made at apply time — this is the same question
   * asked early so the answer can be shown next to the suggestion instead of
   * discovered afterwards. Without it a proposed name that matches nothing
   * arrived pre-ticked on high confidence and applied in silence: the reference
   * kept the AI's free text and got no `calibreId`, with nothing on screen
   * saying so.
   *
   * `null` when the response carried no calibre suggestion, or when the ref is
   * in module mode (there `calibre` means an electronic module, which is not a
   * calibre and has no catalogue to check).
   */
  const [aiCalibreCheck, setAICalibreCheck] = useState<AiCalibreMatchCheck | null>(
    null,
  );
  /**
   * The "Select:" shortcuts. A blanket select still refuses the rows the
   * opening pass held back — an unverified lookup value, a calibre that matches
   * no catalogue entry, and a description a person wrote — because those are
   * the three that would otherwise be applied unexamined on one click. They
   * stay tickable by hand, and the toolbar says how many it left alone.
   */
  const aiSelectionRows = useMemo<SelectionRow[]>(
    () =>
      Object.keys(aiSuggestions).map((key) => {
        const reason = aiAcceptReason[key];
        const heldBack =
          reason === 'unverifiedLookup' ||
          reason === 'unresolvedCalibre' ||
          reason === 'manualDescription'
            ? reason
            : undefined;
        return { key, confidence: aiConfidence[key], heldBack };
      }),
    [aiSuggestions, aiConfidence, aiAcceptReason],
  );

  const applyAISelectionMode = useCallback(
    (mode: SelectionMode) => {
      const result = applySelectionMode(aiSelectionRows, mode);
      setAIAccepted(result.accepted);
      setAIAcceptReason((prev) => ({ ...prev, ...result.reasons }));
    },
    [aiSelectionRows],
  );
  /**
   * The tier the admin explicitly chose in the review table's tier picker.
   *
   * `null` means "no explicit choice — use whatever the AI suggested", which is
   * the state the dialog opens in. A string is an explicit choice and WINS over
   * the model's answer: `''` is the Clear button (apply no tier), any other
   * value is a tier id picked from the linked calibre's own list, so it never
   * needs name resolution.
   *
   * Reset every time a new suggestion set arrives — a choice made against the
   * previous response must not leak into the next one.
   */
  const [aiTierOverride, setAITierOverride] = useState<string | null>(null);
  const [pushCalibreLoading, setPushCalibreLoading] = useState(false);
  const [calibreExistsInDb, setCalibreExistsInDb] = useState(false);
  // The custom_calibres entry the `calibre` string resolves to (by name/alias),
  // or null when unresolved. Source of truth for the tier picker + effective-
  // spec preview. Fetched alongside the exists-in-db check below.
  const [linkedCalibre, setLinkedCalibre] = useState<CustomCalibreEntry | null>(
    null,
  );
  // Phase 3a: the calibre resolved by the authoritative `calibreId` FK (vs the
  // name-based `linkedCalibre` above). When `calibreId` is set this is the
  // source of truth for the tier picker + spec; `null` while loading/unset.
  const [linkedCalibreById, setLinkedCalibreById] =
    useState<CustomCalibreEntry | null>(null);
  // When the linked calibre is deprecated we transparently resolve to its
  // redirect target; this holds the original (deprecated) name for a small hint.
  const [calibreRedirectFrom, setCalibreRedirectFrom] = useState<string | null>(
    null,
  );
  const [showCalibrePicker, setShowCalibrePicker] = useState(false);
  // A non-current revision picked from a calibre line, held until the curator
  // confirms it (memo D14). Null = nothing pending.
  const [pendingRevisionPick, setPendingRevisionPick] = useState<
    { entry: CustomCalibreEntry; warning: RevisionPickWarning } | null
  >(null);
  // Prefer the id-linked calibre when a `calibreId` is set; otherwise fall back
  // to the legacy name-based resolution. Drives the tier picker + AI tier match.
  const activeLinkedCalibre = calibreId ? linkedCalibreById : linkedCalibre;
  // The subset of that calibre's tiers this ref's BRAND may actually use — the
  // universal grades plus this brand's own modifications. A shared base movement
  // carries brand-specific tiers for every brand that modifies it, and handing
  // the AI the lot let it pick a foreign brand's tier off a loose textual cue.
  // Used for the enrichment prompt AND for resolving what the AI answers; the
  // DISPLAY lookups below deliberately keep reading the full `tiers[]` so an
  // already-stored (even foreign) pointer still renders its name.
  const brandScopedTiers = useMemo(
    () => tiersForBrand(activeLinkedCalibre?.data?.tiers, brand.id),
    [activeLinkedCalibre, brand.id],
  );

  // The selected tier's BRAND, for the effective spec's Manufacturer row. A
  // brand-scoped tier is normally this reference's own brand (the picker offers
  // no other), which needs no read. A stored pointer at another brand's tier is
  // fetched once; `brand: null` records that the brand does not exist. Keyed by
  // id so a slow fetch for a previous tier can never label the current one.
  const specTierBrandId = useMemo(() => {
    const tier = calibreTierId
      ? activeLinkedCalibre?.data?.tiers?.find((t) => t.id === calibreTierId)
      : undefined;
    return tier?.brandId?.trim() || undefined;
  }, [activeLinkedCalibre, calibreTierId]);
  const [foreignTierBrand, setForeignTierBrand] = useState<
    { id: string; brand: BrandNameSource | null } | undefined
  >(undefined);
  useEffect(() => {
    if (!specTierBrandId || specTierBrandId === brand.id) return;
    if (foreignTierBrand?.id === specTierBrandId) return;
    let cancelled = false;
    getWatchBrandById(specTierBrandId)
      .then((b) => {
        if (!cancelled) setForeignTierBrand({ id: specTierBrandId, brand: b });
      })
      .catch((err) => {
        console.warn('Could not load the tier brand:', err);
        if (!cancelled) setForeignTierBrand({ id: specTierBrandId, brand: null });
      });
    return () => {
      cancelled = true;
    };
  }, [specTierBrandId, brand.id, foreignTierBrand?.id]);
  // The linked calibre currently open in the edit-in-modal flow (item 5). Set to
  // the resolved calibre entry to open a CustomCalibreModal over the ref editor;
  // null when closed.
  const [editingLinkedCalibre, setEditingLinkedCalibre] =
    useState<CustomCalibreEntry | null>(null);
  // Live mirror of the ref's `calibreCache`, seeded from the prop. The cache
  // trigger (`onCustomCalibreWrite`) refreshes it asynchronously after a calibre
  // edit, so we refetch the ref doc on modal close and swap in the fresh cache
  // here (the two cache-consuming panels read this, not the prop). Kept in sync
  // if the parent hands us a newer `reference`.
  const [liveCalibreCache, setLiveCalibreCache] = useState(
    reference.calibreCache,
  );
  useEffect(() => {
    setLiveCalibreCache(reference.calibreCache);
  }, [reference.calibreCache]);

  // mode: 'overwrite' = always set if data available (from calibre field)
  //        'fill_empty' = only set if current value is empty/default (from base movement field)
  const populateFromCalibreData = (d: import('../../services/firebase').CalibreData, mode: 'overwrite' | 'fill_empty' = 'overwrite'): number => {
    let fieldsUpdated = 0;
    const canSet = (currentVal: string | boolean | string[] | undefined, isBoolean = false): boolean => {
      if (mode === 'overwrite') return true;
      // fill_empty: only set if field is empty/default
      if (isBoolean) return currentVal === false || currentVal === undefined;
      if (Array.isArray(currentVal)) return currentVal.length === 0;
      return !currentVal || currentVal === '';
    };
    // Check if value actually changes (for accurate count)
    const changed = (currentVal: string | boolean | string[] | undefined, newVal: string | boolean | string[] | undefined): boolean => {
      if (Array.isArray(currentVal) && Array.isArray(newVal)) return JSON.stringify(currentVal) !== JSON.stringify(newVal);
      return currentVal !== newVal;
    };

    if (d.movementType && canSet(movementType) && changed(movementType, d.movementType)) { setMovementType(d.movementType); fieldsUpdated++; }
    if (d.frequencyVph && canSet(frequencyVph) && changed(frequencyVph, d.frequencyVph.toString())) { setFrequencyVph(d.frequencyVph.toString()); fieldsUpdated++; }
    if (d.jewels && canSet(jewels) && changed(jewels, d.jewels.toString())) { setJewels(d.jewels.toString()); fieldsUpdated++; }
    if (d.powerReserveHours && canSet(powerReserveHours) && changed(powerReserveHours, d.powerReserveHours.toString())) { setPowerReserveHours(d.powerReserveHours.toString()); fieldsUpdated++; }
    if (d.diameterMm && canSet(movementDiameterMm) && changed(movementDiameterMm, d.diameterMm.toString())) { setMovementDiameterMm(d.diameterMm.toString()); fieldsUpdated++; }
    if (d.heightMm && canSet(movementHeightMm) && changed(movementHeightMm, d.heightMm.toString())) { setMovementHeightMm(d.heightMm.toString()); fieldsUpdated++; }
    if (d.hacking !== undefined && canSet(hacking, true) && changed(hacking, d.hacking)) { setHacking(d.hacking); fieldsUpdated++; }
    if (d.manualWinding !== undefined && canSet(manualWinding, true) && changed(manualWinding, d.manualWinding)) { setManualWinding(d.manualWinding); fieldsUpdated++; }
    if (d.quickSetDate !== undefined && canSet(quickSetDate, true) && changed(quickSetDate, d.quickSetDate)) { setQuickSetDate(d.quickSetDate); fieldsUpdated++; }
    if (d.windingDirection && canSet(windingDirection) && changed(windingDirection, d.windingDirection)) { setWindingDirection(d.windingDirection); fieldsUpdated++; }
    // Base movement: always update when called from calibre lookup (the slug may resolve to a better name)
    if (d.baseMovement && changed(baseMovement, d.baseMovement)) { setBaseMovement(d.baseMovement); fieldsUpdated++; }
    if (d.isCosc !== undefined && canSet(isCosc, true) && changed(isCosc, d.isCosc)) { setIsCosc(d.isCosc); fieldsUpdated++; }
    if (d.isGlashuetteRegulation !== undefined && canSet(isGlashuetteRegulation, true) && changed(isGlashuetteRegulation, d.isGlashuetteRegulation)) { setIsGlashuetteRegulation(d.isGlashuetteRegulation); fieldsUpdated++; }
    if (d.isChronometer !== undefined && canSet(isChronometer, true) && changed(isChronometer, d.isChronometer)) { setIsChronometer(d.isChronometer); fieldsUpdated++; }
    if (d.isGenevaSeal !== undefined && canSet(isGenevaSeal, true) && changed(isGenevaSeal, d.isGenevaSeal)) { setIsGenevaSeal(d.isGenevaSeal); fieldsUpdated++; }
    if (d.isGrandSeikoStandard !== undefined && canSet(isGrandSeikoStandard, true) && changed(isGrandSeikoStandard, d.isGrandSeikoStandard)) { setIsGrandSeikoStandard(d.isGrandSeikoStandard); fieldsUpdated++; }
    if (d.isMETAS !== undefined && canSet(isMETAS, true) && changed(isMETAS, d.isMETAS)) { setIsMETAS(d.isMETAS); fieldsUpdated++; }
    if (d.isGMT !== undefined && canSet(isGMT, true) && changed(isGMT, d.isGMT)) { setIsGMT(d.isGMT); fieldsUpdated++; }
    if (d.isChronograph !== undefined && canSet(isChronograph, true) && changed(isChronograph, d.isChronograph)) { setIsChronograph(d.isChronograph); fieldsUpdated++; }
    if (d.chronograph && d.chronograph.length > 0 && canSet(movementChronograph) && changed(movementChronograph, d.chronograph)) { setMovementChronograph(d.chronograph); fieldsUpdated++; }
    if (d.functions && d.functions.length > 0 && canSet(functions) && changed(functions, d.functions)) { setFunctions(d.functions); fieldsUpdated++; }
    if (d.date && d.date.length > 0 && canSet(movementDate) && changed(movementDate, d.date)) { setMovementDate(d.date); fieldsUpdated++; }
    if (d.hands && d.hands.length > 0 && canSet(movementHands) && changed(movementHands, d.hands)) { setMovementHands(d.hands); fieldsUpdated++; }
    if (d.additionals && d.additionals.length > 0 && canSet(movementAdditionals) && changed(movementAdditionals, d.additionals)) { setMovementAdditionals(d.additionals); fieldsUpdated++; }
    if (d.astronomical && d.astronomical.length > 0 && canSet(movementAstronomical) && changed(movementAstronomical, d.astronomical)) { setMovementAstronomical(d.astronomical); fieldsUpdated++; }
    if (d.acoustic && d.acoustic.length > 0 && canSet(movementAcoustic) && changed(movementAcoustic, d.acoustic)) { setMovementAcoustic(d.acoustic); fieldsUpdated++; }
    if (d.liftAngleDegrees && canSet(liftAngleDegrees) && changed(liftAngleDegrees, d.liftAngleDegrees.toString())) { setLiftAngleDegrees(d.liftAngleDegrees.toString()); fieldsUpdated++; }
    if (d.isInHouse !== undefined && canSet(isInHouse, true) && changed(isInHouse, d.isInHouse)) { setIsInHouse(d.isInHouse); fieldsUpdated++; }
    if (d.countryOfManufacture && canSet(countryOfManufacture) && changed(countryOfManufacture, d.countryOfManufacture)) { setCountryOfManufacture(d.countryOfManufacture); fieldsUpdated++; }
    if (d.antiShockSystem && canSet(antiShockSystem) && changed(antiShockSystem, d.antiShockSystem)) { setAntiShockSystem(d.antiShockSystem); fieldsUpdated++; }
    if (d.rotorType && canSet(rotorType) && changed(rotorType, d.rotorType)) { setRotorType(d.rotorType); fieldsUpdated++; }
    if (d.columnWheel !== undefined && canSet(columnWheel, true) && changed(columnWheel, d.columnWheel)) { setColumnWheel(d.columnWheel); fieldsUpdated++; }
    if (d.serviceIntervalYears && canSet(serviceIntervalYears) && changed(serviceIntervalYears, d.serviceIntervalYears.toString())) { setServiceIntervalYears(d.serviceIntervalYears.toString()); fieldsUpdated++; }
    if (d.lignes && canSet(lignes) && changed(lignes, d.lignes)) { setLignes(d.lignes); fieldsUpdated++; }
    if (d.regulatorSystem && canSet(regulatorSystem) && changed(regulatorSystem, d.regulatorSystem)) { setRegulatorSystem(d.regulatorSystem); fieldsUpdated++; }
    if (d.casingDiameterMm && canSet(casingDiameterMm) && changed(casingDiameterMm, d.casingDiameterMm.toString())) { setCasingDiameterMm(d.casingDiameterMm.toString()); fieldsUpdated++; }
    if (d.manualWindingDirection && canSet(manualWindingDirection) && changed(manualWindingDirection, d.manualWindingDirection)) { setManualWindingDirection(d.manualWindingDirection); fieldsUpdated++; }
    if (d.hairspring && canSet(hairspring) && changed(hairspring, d.hairspring)) { setHairspring(d.hairspring); fieldsUpdated++; }
    if (d.doubleBarrel !== undefined && canSet(doubleBarrel, true) && changed(doubleBarrel, d.doubleBarrel)) { setDoubleBarrel(d.doubleBarrel); fieldsUpdated++; }
    if (d.escapement && canSet(escapement) && changed(escapement, d.escapement)) { setEscapement(d.escapement); fieldsUpdated++; }
    if (d.batteryType && canSet(batteryType) && changed(batteryType, d.batteryType)) { setBatteryType(d.batteryType); fieldsUpdated++; }
    if (d.batteryLifeYears && canSet(batteryLifeYears) && changed(batteryLifeYears, d.batteryLifeYears.toString())) { setBatteryLifeYears(d.batteryLifeYears.toString()); fieldsUpdated++; }

    // Derive reference-level functions from calibre complication arrays + booleans
    const derivedFunctions = deriveFunctionsFromCalibre(d, mode === 'fill_empty' ? functions : undefined);
    if (derivedFunctions.length > 0 && changed(functions, derivedFunctions)) {
      setFunctions(derivedFunctions);
      fieldsUpdated++;
    }

    return fieldsUpdated;
  };

  // The old "Lookup Calibre" buttons (which hydrated the legacy movement.*
  // fields from a matched DB calibre) were removed: linking a calibre supersedes
  // that legacy-copy pattern. `populateFromCalibreData` + `lookupCalibre` are
  // retained only for the AI-apply normalization path below.
  //
  // Phase 5b-i: single-caller, but NOT retirable alongside the other legacy
  // movement dual-writes. The bulk action bar's equivalent builds a Firestore
  // payload it writes directly; this one drives the editor's own form state,
  // which `handleSave` serialises. Dropping it would mean writing `movement.*`
  // behind the form, desyncing what the admin is looking at from what lands.
  // It is gated by `calibre.dualWriteLegacyMovement` at its call site instead.

  /**
   * Resolve the typed calibre STRING against `custom_calibres` — "does this
   * exist in the DB?" (the Push-to-DB pill) and "which calibre is it?" (the
   * offer-to-link pill).
   *
   * Both answers are only ever rendered for an UNLINKED ref: `activeLinkedCalibre`
   * prefers `linkedCalibreById` whenever `calibreId` is set, and the two pills
   * are `!calibreId`-gated. So a linked ref skips this entirely and pays nothing.
   *
   * For an unlinked ref this used to be `getCustomCalibres()` — a ~1,822-document
   * full collection scan, doubled by Strict Mode, re-fired on EVERY KEYSTROKE in
   * the Calibre input, and again on every AI-accepted calibre change. It is now:
   *   1. debounced, so a burst of typing costs one lookup, not one per character;
   *   2. narrowed by an Algolia name/alias search to a handful of candidate ids;
   *   3. resolved by reading only those few documents.
   *
   * The authoritative matching still runs LOCALLY against the real documents —
   * the index carries neither `aliasDetails` (brand scope) nor `isCatchAll`, so
   * it can narrow but never decide. If Algolia is unconfigured or the search
   * throws we fall back to the full scan: reporting "not in the DB" because
   * search was down would have the admin create a duplicate calibre.
   */
  useEffect(() => {
    const typed = calibre.trim();
    if (!typed || calibreId.trim()) {
      setCalibreExistsInDb(false);
      setLinkedCalibre(null);
      return;
    }
    let cancelled = false;

    const resolve = async () => {
      const candidates = await loadCalibreCandidatesByName(typed);
      if (cancelled) return;

      const nameLower = normalizeCalibreKey(typed);
      // "Exists in DB" is a brand-agnostic existence check (drives the
      // Push-to-DB affordance) — unchanged: any name/AKA hit counts.
      const existsMatch = candidates.some((e) => {
        if (normalizeCalibreKey(e.name) === nameLower) return true;
        if (e.alsoKnownAs?.some((alias) => normalizeCalibreKey(alias) === nameLower)) return true;
        return false;
      });
      setCalibreExistsInDb(existsMatch);
      // The LINKED calibre (drives the tier picker, effective-spec preview +
      // AI tier match) is brand-scope-aware: a brand-scoped AKA only matches
      // refs from that brand, catch-all calibres + placeholder strings never
      // resolve. Real calibres (canonical name / universal AKA) are unaffected.
      const linked = candidates.find((e) =>
        calibreEntryMatchesRef(e, typed, brand.id),
      );
      setLinkedCalibre(linked ?? null);
    };

    const timer = setTimeout(() => {
      resolve().catch(() => {});
    }, CALIBRE_LOOKUP_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [calibre, calibreId, brand.id]);

  // Phase 3a: resolve the calibre linked by the authoritative `calibreId` FK.
  // Kept separate from the name-based resolution above so the two never fight —
  // the tier picker + spec prefer this one whenever a `calibreId` is set.
  useEffect(() => {
    const id = calibreId.trim();
    if (!id) {
      setLinkedCalibreById(null);
      setCalibreRedirectFrom(null);
      return;
    }
    let cancelled = false;
    // Follow a deprecation redirect: if this calibre was reclassified into a
    // brand-specific tier of another calibre, resolve to the live target so the
    // tier picker + spec read from the right calibre.
    resolveCalibre(id)
      .then((resolved) => {
        if (cancelled) return;
        if (!resolved) {
          setLinkedCalibreById(null);
          setCalibreRedirectFrom(null);
          return;
        }
        setLinkedCalibreById(resolved.calibre);
        setCalibreRedirectFrom(
          resolved.redirected ? resolved.source?.name ?? id : null,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLinkedCalibreById(null);
          setCalibreRedirectFrom(null);
        }
      });
    return () => { cancelled = true; };
  }, [calibreId]);

  const handlePushCalibre = async () => {
    if (!calibre.trim()) return;
    setPushCalibreLoading(true);
    try {
      const calibreData = {
        movementType: movementType || undefined,
        frequencyVph: frequencyVph ? parseInt(frequencyVph) : undefined,
        jewels: jewels ? parseInt(jewels) : undefined,
        powerReserveHours: powerReserveHours ? parseInt(powerReserveHours) : undefined,
        diameterMm: movementDiameterMm ? parseFloat(movementDiameterMm) : undefined,
        heightMm: movementHeightMm ? parseFloat(movementHeightMm) : undefined,
        hacking: hacking || undefined,
        manualWinding: manualWinding || undefined,
        quickSetDate: quickSetDate || undefined,
        windingDirection: windingDirection || undefined,
        baseMovement: baseMovement || undefined,
        isCosc: Boolean(isCosc),
        isGlashuetteRegulation: Boolean(isGlashuetteRegulation),
        isChronometer: Boolean(isChronometer),
        isGenevaSeal: Boolean(isGenevaSeal),
        isGrandSeikoStandard: Boolean(isGrandSeikoStandard),
        isMETAS: Boolean(isMETAS),
        isGMT: Boolean(isGMT),
        isChronograph: Boolean(isChronograph),
        liftAngleDegrees: liftAngleDegrees ? parseFloat(liftAngleDegrees) : undefined,
        isInHouse: Boolean(isInHouse),
        countryOfManufacture: countryOfManufacture || undefined,
        antiShockSystem: antiShockSystem || undefined,
        rotorType: rotorType || undefined,
        columnWheel: columnWheel || undefined,
        serviceIntervalYears: serviceIntervalYears ? parseInt(serviceIntervalYears) : undefined,
        lignes: lignes || undefined,
        regulatorSystem: regulatorSystem || undefined,
        casingDiameterMm: casingDiameterMm ? parseFloat(casingDiameterMm) : undefined,
        manualWindingDirection: manualWindingDirection || undefined,
        hairspring: hairspring || undefined,
        doubleBarrel: doubleBarrel || undefined,
        escapement: escapement || undefined,
        batteryType: batteryType || undefined,
        batteryLifeYears: batteryLifeYears ? parseFloat(batteryLifeYears) : undefined,
      };
      const isGeneric = isGenericMovementName(calibre.trim());
      await saveCustomCalibre(
        calibre.trim(),
        calibreData,
        isGeneric ? undefined : brand.name,
      );
      setCalibreExistsInDb(true);
      toast.success(
        isGeneric
          ? `Saved "${calibre.trim()}" to custom calibre database (unscoped)`
          : `Saved "${calibre.trim()}" to custom calibre database`,
      );
    } catch (error) {
      console.error('Failed to push calibre to database:', error);
      toast.error('Failed to save calibre to database');
    } finally {
      setPushCalibreLoading(false);
    }
  };

  const handleCalibreToolbarSuccess = () => {
    setCalibreExistsInDb(true);
    // No refetch here. This used to fire `getCustomCalibres()` and DISCARD the
    // result — a ~1,822-document scan warming nothing, since that helper has no
    // cache. The name-resolution effect above re-runs on the next calibre change
    // and reads the fresh document then.
  };

  // ── Phase 3a/3b: calibre link (calibreId) ─────────────────────────────────
  //
  // Helpers first (they're referenced by the link/unlink handlers below).

  const setCalibreOverride = <K extends keyof CalibreTierOverrides>(
    key: K,
    value: CalibreTierOverrides[K],
  ) => {
    setCalibreOverrides((prev) => ({ ...prev, [key]: value }));
  };

  // The effective cache spec projected onto CalibreData, for legacy-diff checks.
  // Reads the live cache mirror so an in-modal calibre edit (item 5) flows into
  // the legacy-values divergence display + save-path migration too.
  const cacheBaseData = cacheToBaseData(liveCalibreCache);

  /**
   * The best-available authoritative value for a calibre field: prefer the
   * effective cache (base + tier), fall back to the linked calibre doc (covers
   * the moment right after linking, before the cache trigger has run, and the
   * orphan fields the snapshot doesn't carry).
   */
  const calibreValueFor = (
    key: keyof CalibreData,
  ): boolean | number | string | string[] | undefined => {
    const c = cacheBaseData[key];
    if (c !== undefined && c !== '') return c as never;
    return activeLinkedCalibre?.data?.[key] as never;
  };

  // Group A — the override-eligible legacy `movement.*` values. Each carries a
  // `value` getter (for migration) and a `set` writer (for unlink bake-back).
  // positionsAdjusted and the accuracy trio are overridable but have no legacy
  // movement field, so they're intentionally absent.
  const legacyOverrideSources: {
    key: OverrideKey;
    value: boolean | number | string | undefined;
    set: (v: boolean | number | string | undefined) => void;
  }[] = [
    { key: 'isChronometer', value: isChronometer || undefined, set: (v) => setIsChronometer(v === true) },
    { key: 'isCosc', value: isCosc || undefined, set: (v) => setIsCosc(v === true) },
    { key: 'isGlashuetteRegulation', value: isGlashuetteRegulation || undefined, set: (v) => setIsGlashuetteRegulation(v === true) },
    { key: 'isGenevaSeal', value: isGenevaSeal || undefined, set: (v) => setIsGenevaSeal(v === true) },
    { key: 'isGrandSeikoStandard', value: isGrandSeikoStandard || undefined, set: (v) => setIsGrandSeikoStandard(v === true) },
    { key: 'isMETAS', value: isMETAS || undefined, set: (v) => setIsMETAS(v === true) },
    { key: 'hacking', value: hacking || undefined, set: (v) => setHacking(v === true) },
    { key: 'quickSetDate', value: quickSetDate || undefined, set: (v) => setQuickSetDate(v === true) },
    { key: 'regulatorSystem', value: regulatorSystem || undefined, set: (v) => setRegulatorSystem(typeof v === 'string' ? v : '') },
    { key: 'hairspring', value: hairspring || undefined, set: (v) => setHairspring(typeof v === 'string' ? v : '') },
    { key: 'escapement', value: escapement || undefined, set: (v) => setEscapement(typeof v === 'string' ? v : '') },
    { key: 'antiShockSystem', value: antiShockSystem || undefined, set: (v) => setAntiShockSystem(typeof v === 'string' ? v : '') },
    { key: 'rotorType', value: rotorType || undefined, set: (v) => setRotorType(typeof v === 'string' ? v : '') },
    { key: 'jewels', value: jewels.trim() ? parseInt(jewels, 10) : undefined, set: (v) => setJewels(v == null ? '' : String(v)) },
    { key: 'powerReserveHours', value: powerReserveHours.trim() ? parseInt(powerReserveHours, 10) : undefined, set: (v) => setPowerReserveHours(v == null ? '' : String(v)) },
  ];

  const displayLegacy = (
    v: boolean | number | string | string[] | undefined,
  ): string => {
    if (v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    return String(v);
  };

  /** Find the lookup entry for `rawValue` in `collection`, matching value,
   *  displayName, or synonym (case-insensitive) — mirrors LookupFieldSelect's
   *  own resolution so display and comparison agree with what the editor shows.
   *  Returns null if the collection isn't cached yet or nothing matches. */
  const resolveLookupEntry = (
    collection: string,
    rawValue: string,
  ): LookupValue | null => {
    const entries = movementLookupCache[collection];
    if (!entries || entries.length === 0) return null;
    const lower = rawValue.toLowerCase();
    return (
      entries.find(
        (lv) =>
          lv.value === rawValue ||
          lv.displayName === rawValue ||
          lv.value.toLowerCase() === lower ||
          lv.displayName.toLowerCase() === lower ||
          lv.synonyms?.some((s) => s.toLowerCase() === lower),
      ) ?? null
    );
  };

  /** Render a movement value for display. For a lookup-backed field, resolve
   *  the raw value to its human displayName (falling back to the raw value so
   *  unknown/legacy junk still renders); everything else uses displayLegacy. */
  const displayField = (
    ck: keyof CalibreData,
    v: boolean | number | string | string[] | undefined,
  ): string => {
    const collection = MOVEMENT_LOOKUP_COLLECTIONS[ck];
    if (collection && typeof v === 'string' && v !== '') {
      return resolveLookupEntry(collection, v)?.displayName ?? v;
    }
    return displayLegacy(v);
  };

  /** Resolve a raw lookup id to its human `displayName` (falls back to the raw
   *  value when the collection isn't cached or nothing matches). Passed to the
   *  effective-spec panels so they render labels, not stored ids. */
  const resolveLookupLabel = (collection: string, raw: string): string =>
    resolveLookupEntry(collection, raw)?.displayName ?? raw;

  /** True when a legacy value and a calibre value are semantically equal. For a
   *  lookup-backed field, both sides normalize to the lookup's canonical id
   *  (so legacy label "Manual" == calibre id "manual"); otherwise fall back to
   *  the rendered display string, preserving the previous raw comparison for
   *  numbers, booleans, free text, and arrays. */
  const valuesMatch = (
    ck: keyof CalibreData,
    legacy: boolean | number | string | string[] | undefined,
    calVal: boolean | number | string | string[] | undefined,
  ): boolean => {
    const collection = MOVEMENT_LOOKUP_COLLECTIONS[ck];
    if (collection && typeof legacy === 'string' && typeof calVal === 'string') {
      const norm = (v: string) =>
        (resolveLookupEntry(collection, v)?.value ?? v).toLowerCase();
      return norm(legacy) === norm(calVal);
    }
    return displayLegacy(legacy) === displayLegacy(calVal);
  };

  /**
   * Group A auto-migration: diverging legacy values that aren't already
   * overridden become per-ref overrides. DUAL-WRITE — the legacy `movement.*`
   * fields are intentionally left populated (a later PR clears them once the RN
   * override-application path is confirmed). Compared against `spec` (the linked
   * calibre's effective values). Returns the override delta to apply.
   */
  const computeGroupAMigrations = (
    spec: CalibreData | undefined,
  ): Partial<CalibreTierOverrides> => {
    const out: Partial<CalibreTierOverrides> = {};
    if (!spec) return out;
    for (const s of legacyOverrideSources) {
      if (s.value === undefined) continue;
      if (typeof s.value === 'number' && Number.isNaN(s.value)) continue;
      if (calibreOverrides[s.key] !== undefined) continue;
      // Lookup-aware equality: a legacy label ("Manual") must not migrate over
      // an identical calibre id ("manual"). Falls back to raw compare otherwise.
      // baseValueFor, not a raw index — `hasDate` has no CalibreData counterpart
      // and is derived from the base `date[]` instead.
      if (valuesMatch(s.key as keyof CalibreData, s.value, baseValueFor(spec, s.key)))
        continue;
      (out as Record<string, unknown>)[s.key] = s.value;
    }
    return out;
  };

  /** Link the ref to the picked calibre. Sets the authoritative `calibreId`,
   *  drops a now-invalid tier pointer, and auto-migrates diverging legacy
   *  movement values into per-ref overrides. We deliberately do NOT overwrite
   *  the legacy `calibre` string — preserving the original keeps the read-only
   *  legacy display meaningful and lets "Save as AKA" attach the real string. */
  const handlePickCalibre = (entry: CustomCalibreEntry) => {
    setCalibreId(entry.id);
    setLinkedCalibreById(entry);
    // Clear a tier pointer that doesn't exist on the newly-linked calibre.
    const tiers = entry.data?.tiers ?? [];
    if (calibreTierId && !tiers.some((t) => t.id === calibreTierId)) {
      setCalibreTierId('');
    }
    // Auto-migrate diverging legacy values into overrides (dual-write). A value
    // that disagrees with the linked calibre most likely means the calibre was
    // updated and this ref is stale, so we treat the ref value as an override.
    const migrations = computeGroupAMigrations(entry.data);
    const n = Object.keys(migrations).length;
    if (n > 0) {
      setCalibreOverrides((prev) => ({ ...prev, ...migrations }));
      toast.success(
        `Auto-migrated ${n} legacy value${n === 1 ? '' : 's'} to override${n === 1 ? '' : 's'}`,
      );
    }
    setShowCalibrePicker(false);
  };

  /**
   * The picker's onSelect. A lineage member that is not the current revision
   * asks first — unless this ref's year sits inside the picked revision's
   * years, which is the evidence the warning would otherwise ask for.
   */
  const handlePickCalibreChecked = async (entry: CustomCalibreEntry) => {
    if (!entry.calibreLineageId || (entry.isCurrent && !entry.isLineagePlaceholder)) {
      handlePickCalibre(entry);
      return;
    }
    let current: CustomCalibreEntry | null = null;
    try {
      current = await getCurrentRevision(entry.calibreLineageId);
    } catch (err) {
      console.warn('Could not read the current revision; linking without the check', err);
    }
    const refYear = firstYear(productionReleaseYear) ?? firstYear(productionStartYear);
    const warning = revisionPickWarning(entry, current, refYear);
    if (!warning) {
      handlePickCalibre(entry);
      return;
    }
    setShowCalibrePicker(false);
    setPendingRevisionPick({ entry, warning });
  };

  /** Unlink the calibre. Bakes the per-ref overrides back into the legacy
   *  `movement.*` fields first (so ref-specific values survive the unlink —
   *  cache-derived values already live in the dual-written legacy fields), then
   *  clears the link, tier pointer, and overrides. */
  const handleUnlinkCalibre = () => {
    for (const s of legacyOverrideSources) {
      const ov = calibreOverrides[s.key];
      if (ov !== undefined) s.set(ov as boolean | number | string);
    }
    setCalibreId('');
    setLinkedCalibreById(null);
    setCalibreTierId('');
    setCalibreOverrides({});
  };

  /**
   * Refetch after the linked calibre is edited-and-saved in the modal (item 5).
   * Two sources go stale:
   *   1. The linked calibre doc itself — re-resolve it so the tier list +
   *      effective-spec preview (which read `activeLinkedCalibre.data`) update
   *      immediately, with no dependence on the async cache trigger.
   *   2. The ref's own `calibreCache` — the `onCustomCalibreWrite` trigger
   *      rebuilds it for every linked ref, but asynchronously. Poll the ref doc
   *      a few times until the cache changes, then swap it into `liveCalibreCache`
   *      (the cache panel + overrides base read that). Until it lands, the
   *      effective-spec preview above already reflects the fresh calibre doc, so
   *      the user sees their edit right away (optimistic fallback).
   */
  /**
   * Poll the ref doc for the trigger-rebuilt `calibreCache` (3 attempts × ~1.2s)
   * and update the live mirror in place, so the effective-spec panel reflects
   * the fresh snapshot without a close/reopen. Stops as soon as the cache
   * changes from `previous` (the value shown before the write that kicked the
   * trigger). Shared by the calibre-edit-saved flow and the post-save refetch.
   */
  const refetchCalibreCache = async (previous: unknown) => {
    if (!calibreId.trim()) return;
    const before = JSON.stringify(previous ?? null);
    for (let attempt = 0; attempt < 3; attempt++) {
      await new Promise((r) => setTimeout(r, 1200));
      try {
        const fresh = await getWatchReference(brand.id, reference.id);
        const next = fresh?.calibreCache;
        if (JSON.stringify(next ?? null) !== before) {
          setLiveCalibreCache(next);
          return;
        }
      } catch (err) {
        console.error('Failed to refetch ref cache', err);
      }
    }
  };

  const handleLinkedCalibreSaved = async () => {
    const id = calibreId.trim();
    if (!id) return;
    // 1. Fresh calibre doc → tier list + effective-spec preview.
    try {
      const resolved = await resolveCalibre(id);
      if (resolved) setLinkedCalibreById(resolved.calibre);
    } catch (err) {
      console.error('Failed to re-resolve linked calibre after edit', err);
    }
    // 2. Poll the ref doc for the rebuilt cache.
    await refetchCalibreCache(liveCalibreCache);
  };

  // ── AI enrichment ─────────────────────────────────────────────────────────
  // Map from suggestion key → { label, current value, apply suggestion }
  /**
   * The stored tri-state of a boolean field: `undefined` when nobody has
   * answered, which the editor's own state cannot express — it holds a plain
   * boolean, so an unset field and a stored `false` both read as false. The
   * review table needs the difference (see booleanPreTicks), so this reads the
   * STORED value and lets a tick made this session win.
   */
  const boolCurrent = (stored: unknown, live: boolean): boolean | undefined =>
    live ? true : stored === undefined || stored === null ? undefined : false;

  const aiFieldHandlers: Record<
    string,
    {
      label: string;
      currentDisplay: string;
      /** Raw stored value, for rows whose display string loses information. */
      currentValue?: unknown;
      apply: (v: unknown) => void;
    }
  > = {
    calibre: {
      label: showModule ? 'Module (from calibre)' : 'Calibre',
      currentDisplay: showModule ? elecModule : calibre,
      apply: (v) => {
        if (typeof v !== 'string') return;
        if (showModule) {
          // For smartwatches/digital, calibre maps to module
          setElecModule(v);
        } else {
          setCalibre(v);
        }
      },
    },
    // Calibre tier suggestion. The AI returns the tier's stable id (preferred)
    // or its name; both resolve against the linked calibre's tiers[]. No match
    // → warn and leave unset (never guess). Only meaningful when the linked
    // calibre actually defines tiers.
    // NOTE: both tier suggestions are intercepted and resolved specially in
    // handleApplyAISuggestions (against the NEW calibre's tiers when the calibre
    // itself changes, with the old pointer cleared on a dangling result). These
    // `apply` fns are the self-consistent fallback for the no-calibre-change
    // path — kept in the map so the suggestion row still renders a label +
    // current value. Both use the fuzzy resolver so a paraphrased tier name
    // ("Elaboré" for "Special (Elaboré)") still resolves.
    calibreTierId: {
      label: 'Calibre tier',
      currentDisplay:
        (activeLinkedCalibre?.data?.tiers ?? []).find((t) => t.id === calibreTierId)?.name ?? '',
      apply: (v) => {
        if (typeof v !== 'string' || !v.trim()) return;
        // Brand-scoped: a tier belonging to another brand must not resolve.
        const tiers = brandScopedTiers;
        const resolved = resolveTierSuggestion(tiers, v, undefined);
        if (resolved) setCalibreTierId(resolved);
        else
          console.warn(
            `AI suggested calibreTierId "${v}" not found among the tiers of linked calibre "${activeLinkedCalibre?.name ?? calibre}" available to ${brand.name}; leaving tier unset`,
          );
      },
    },
    calibreTierName: {
      label: 'Calibre tier (by name)',
      currentDisplay:
        (activeLinkedCalibre?.data?.tiers ?? []).find((t) => t.id === calibreTierId)?.name ?? '',
      apply: (v) => {
        if (typeof v !== 'string' || !v.trim()) return;
        // Brand-scoped: a tier belonging to another brand must not resolve.
        const tiers = brandScopedTiers;
        const resolved = resolveTierSuggestion(tiers, undefined, v);
        if (resolved) setCalibreTierId(resolved);
        else
          console.warn(
            `AI suggested calibreTierName "${v}" not found among the tiers of linked calibre "${activeLinkedCalibre?.name ?? calibre}" available to ${brand.name}; leaving tier unset`,
          );
      },
    },
    movementType: {
      label: 'Movement type',
      currentDisplay: movementType,
      apply: (v) => typeof v === 'string' && setMovementType(v),
    },
    caseMaterial: {
      label: 'Case material',
      currentDisplay: caseMaterial,
      apply: (v) => typeof v === 'string' && setCaseMaterial(v),
    },
    crystal: {
      label: 'Crystal',
      currentDisplay: crystal.join(', '),
      // Multi-select since the crown/crystal split. The AI returns an array of
      // slugs, but a stored legacy scalar can still reach here via a re-applied
      // suggestion — parseMultiLookupValues normalises both.
      apply: (v) => setCrystal(parseMultiLookupValues(v)),
    },
    crownType: {
      label: 'Crown type',
      currentDisplay: crownType.join(', '),
      apply: (v) => setCrownType(parseMultiLookupValues(v)),
    },
    caseFinish: {
      label: 'Case finish',
      currentDisplay: caseFinish,
      apply: (v) => typeof v === 'string' && setCaseFinish(v),
    },
    waterRes: {
      label: 'Water resistance (m)',
      currentDisplay: caseWaterResistance,
      apply: (v) => {
        if (typeof v === 'number') setCaseWaterResistance(v.toString());
        else if (typeof v === 'string') setCaseWaterResistance(v);
      },
    },
    caseSize: {
      label: 'Case size (mm)',
      currentDisplay: caseDiameter,
      apply: (v) => {
        if (typeof v === 'number') setCaseDiameter(v.toString());
        else if (typeof v === 'string') setCaseDiameter(v);
      },
    },
    caseHeight: {
      label: 'Case height (mm)',
      currentDisplay: caseThickness,
      apply: (v) => {
        if (typeof v === 'number') setCaseThickness(v.toString());
        else if (typeof v === 'string') setCaseThickness(v);
      },
    },
    description: {
      label: 'Description',
      currentDisplay: description,
      apply: (v) => {
        if (typeof v !== 'string') return;
        // Snapshot the pre-AI text once, before the AI value overwrites it.
        if (!originalDescription && description) setOriginalDescription(description);
        setDescription(v);
        setDescriptionSource('ai');
      },
    },
    bezelType: {
      label: 'Bezel type',
      currentDisplay: bezelType,
      apply: (v) => typeof v === 'string' && setBezelType(v),
    },
    caseColors: {
      label: 'Case colors',
      currentDisplay: caseColors.join(', '),
      // Multi-select, same three input shapes as bezelColors.
      apply: (v) => setCaseColors(parseMultiLookupValues(v)),
    },
    bezelColors: {
      label: 'Bezel colors',
      currentDisplay: bezelColors.join(', '),
      // Multi-select: the AI returns an array of colour slugs, but a stored
      // legacy scalar or a comma-joined import value can reach here via a
      // re-applied suggestion — parseMultiLookupValues normalises all three.
      apply: (v) => setBezelColors(parseMultiLookupValues(v)),
    },
    strapMaterial: {
      label: 'Strap material',
      currentDisplay: strapMaterial,
      apply: (v) => typeof v === 'string' && setStrapMaterial(v),
    },
    dialColor: {
      label: 'Dial color',
      currentDisplay: dialColors.join(', '),
      // Multi-select lookup — the AI returns an array of color slugs (token-
      // backed) or a comma/semicolon-joined string; normalise both to string[].
      apply: (v) => {
        let vals: string[] | null = null;
        if (Array.isArray(v)) {
          vals = (v as unknown[]).map((s) => String(s).trim()).filter(Boolean);
        } else if (typeof v === 'string') {
          vals = v.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
        }
        if (vals) {
          setDialColors(vals);
          if (!dial) setDial(vals.join(', '));
        }
      },
    },
    dialStyle: {
      // Single-value lookup (lookup_dial_styles). AI returns the canonical slug
      // (e.g. "panda"); setDialStyle is in the dirty-tracking deps so Save enables.
      label: 'Dial style',
      currentDisplay: dialStyle,
      apply: (v) => typeof v === 'string' && setDialStyle(v),
    },
    isChronograph: {
      label: 'Is chronograph',
      currentDisplay: isChronograph ? 'true' : 'false',
      currentValue: boolCurrent(reference.movement?.isChronograph, isChronograph),
      apply: (v) => typeof v === 'boolean' && setIsChronograph(v),
    },
    isGMT: {
      label: 'Is GMT',
      currentDisplay: isGMT ? 'true' : 'false',
      currentValue: boolCurrent(reference.movement?.isGMT, isGMT),
      apply: (v) => typeof v === 'boolean' && setIsGMT(v),
    },
    collection: {
      label: 'Collection',
      currentDisplay: collectionName,
      apply: (v) => typeof v === 'string' && setCollectionName(v),
    },
    jewels: {
      label: 'Jewels',
      currentDisplay: jewels,
      apply: (v) => {
        if (typeof v === 'number') setJewels(v.toString());
        else if (typeof v === 'string') setJewels(v);
      },
    },
    powerReserve: {
      label: 'Power reserve (h)',
      currentDisplay: showModule ? elecBatteryLife : powerReserveHours,
      apply: (v) => {
        const val = typeof v === 'number' ? v.toString() : typeof v === 'string' ? v : null;
        if (val) {
          if (showModule) {
            setElecBatteryLife(val);
          } else {
            setPowerReserveHours(val);
          }
        }
      },
    },
    // Case - additional fields
    caseShape: {
      label: 'Case shape',
      currentDisplay: caseShape,
      apply: (v) => typeof v === 'string' && setCaseShape(v),
    },
    caseBack: {
      label: 'Case back',
      currentDisplay: caseCaseBack,
      apply: (v) => typeof v === 'string' && setCaseCaseBack(v),
    },
    caseCoating: {
      label: 'Case coating',
      currentDisplay: caseCoating,
      apply: (v) => typeof v === 'string' && setCaseCoating(v),
    },
    caseWeight: {
      label: 'Case weight (g)',
      currentDisplay: caseWeightG,
      apply: (v) => {
        if (typeof v === 'number') setCaseWeightG(v.toString());
        else if (typeof v === 'string') setCaseWeightG(v);
      },
    },
    lugWidth: {
      label: 'Lug width (mm)',
      currentDisplay: caseLugWidth,
      apply: (v) => {
        if (typeof v === 'number') setCaseLugWidth(v.toString());
        else if (typeof v === 'string') setCaseLugWidth(v);
      },
    },
    lugToLug: {
      label: 'Lug-to-lug (mm)',
      currentDisplay: caseLugToLug,
      apply: (v) => {
        if (typeof v === 'number') setCaseLugToLug(v.toString());
        else if (typeof v === 'string') setCaseLugToLug(v);
      },
    },
    bezelMaterial: {
      label: 'Bezel material',
      currentDisplay: bezel,
      apply: (v) => typeof v === 'string' && setBezel(v),
    },
    // Dial & hands - additional fields
    dialMaterial: {
      label: 'Dial material',
      currentDisplay: dialMaterial,
      apply: (v) => typeof v === 'string' && setDialMaterial(v),
    },
    dialFinish: {
      label: 'Dial finish',
      currentDisplay: dialFinish,
      apply: (v) => typeof v === 'string' && setDialFinish(v),
    },
    dialIndexes: {
      label: 'Marker type',
      currentDisplay: dialIndexes.join(', '),
      // Multi-select since the marker-type split. The AI returns an array of
      // slugs, but a stored legacy scalar can still reach here via a re-applied
      // suggestion — parseDialMarkerValues normalises both.
      apply: (v) => setDialIndexes(parseDialMarkerValues(v)),
    },
    dialNumerals: {
      label: 'Dial numerals',
      currentDisplay: dialNumerals.join(', '),
      apply: (v) => setDialNumerals(parseDialMarkerValues(v)),
    },
    hands: {
      label: 'Hands style',
      currentDisplay: dialHands,
      apply: (v) => typeof v === 'string' && setDialHands(v),
    },
    handsColor: {
      label: 'Hands color',
      currentDisplay: handsColors.join(', '),
      // Multi-select lookup — the AI returns an array of color slugs or a
      // comma/semicolon-joined string; normalise both to string[].
      apply: (v) => {
        if (Array.isArray(v)) {
          setHandsColors((v as unknown[]).map((s) => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setHandsColors(v.split(/[,;]+/).map((s) => s.trim()).filter(Boolean));
        }
      },
    },
    indexColor: {
      label: 'Index color',
      currentDisplay: indexColors.join(', '),
      // Multi-select lookup — the AI may return an array of slugs (token-backed)
      // or a comma-joined string; normalise both to string[].
      apply: (v) => {
        if (Array.isArray(v)) {
          setIndexColors((v as unknown[]).map(s => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setIndexColors(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        }
      },
    },
    handTypes: {
      label: 'Hand types',
      currentDisplay: handTypes.join(', '),
      apply: (v) => {
        if (Array.isArray(v)) {
          setHandTypes((v as unknown[]).map(s => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setHandTypes(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        }
      },
    },
    // Date complication. The first four are single-select, so the AI returns a
    // bare slug string — but tolerate an array's first element the way the
    // multi-select handlers tolerate a scalar, rather than silently dropping a
    // shape mismatch. dateWheelTextColor below is the multi one.
    dateDisplay: {
      label: 'Date display',
      currentDisplay: dateDisplay,
      apply: (v) => setDateDisplay(firstSlug(v)),
    },
    dateWindowPosition: {
      label: 'Date window position',
      currentDisplay: dateWindowPosition,
      apply: (v) => setDateWindowPosition(firstSlug(v)),
    },
    dateWindowFrame: {
      label: 'Date window frame',
      currentDisplay: dateWindowFrame,
      apply: (v) => setDateWindowFrame(firstSlug(v)),
    },
    dateWheelColor: {
      label: 'Date wheel color',
      currentDisplay: dateWheelColor,
      apply: (v) => setDateWheelColor(firstSlug(v)),
    },
    dateWheelTextColor: {
      label: 'Date wheel text color',
      currentDisplay: dateWheelTextColors.join(', '),
      // Multi-select — the prompt asks for an array and the server validator
      // filters it per element, so an array is the expected shape. A scalar is
      // tolerated too, and matters more here than for the other multi-selects:
      // this field is backed by lookup_text_colors, which is flagged
      // isMultiSelect: false for the single-select electronics `textColor` that
      // shares it, so the rendered token still heads its list "single value".
      apply: (v) => {
        if (Array.isArray(v)) {
          setDateWheelTextColors((v as unknown[]).map(s => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setDateWheelTextColors(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        }
      },
    },
    hasLume: {
      label: 'Has lume',
      currentDisplay: dialLuminova ? 'true' : 'false',
      currentValue: boolCurrent(reference.dialAndHands?.luminova, dialLuminova),
      apply: (v) => typeof v === 'boolean' && setDialLuminova(v),
    },
    // Lume detail — four single-select slugs, so the plain string apply the
    // date fields use. Accepting them does NOT tick the Luminova box above:
    // hasLume is its own suggestion and the model answers it separately, so
    // inferring one from the other would silently overwrite a deliberate
    // admin un-tick with a side effect of a different field's accept.
    lumePresence: {
      label: 'Lume presence',
      currentDisplay: lumePresence,
      apply: (v) => typeof v === 'string' && setLumePresence(v),
    },
    lumeMaterial: {
      label: 'Lume material',
      currentDisplay: lumeMaterial,
      apply: (v) => typeof v === 'string' && setLumeMaterial(v),
    },
    lumeDayColor: {
      label: 'Lume day color',
      currentDisplay: lumeDayColor,
      apply: (v) => typeof v === 'string' && setLumeDayColor(v),
    },
    lumeGlowColor: {
      label: 'Lume glow color',
      currentDisplay: lumeGlowColor,
      apply: (v) => typeof v === 'string' && setLumeGlowColor(v),
    },
    subdials: {
      label: 'Subdials',
      currentDisplay: dialSubdials,
      apply: (v) => {
        if (typeof v === 'number') setDialSubdials(v.toString());
        else if (typeof v === 'string') setDialSubdials(v);
      },
    },
    // Subdial FUNCTIONS — multi-select, so the array-or-joined-string handler
    // the other multis use. Accepting it deliberately does NOT set the count
    // above from the array's length: the two are separate suggestions the model
    // answers separately, and a dial photo settles the count while the
    // functions need the printed register labels, so deriving one from the
    // other would overwrite a good count with a partial reading.
    subdialTypes: {
      label: 'Subdial types',
      currentDisplay: subdialTypes.join(', '),
      apply: (v) => {
        if (Array.isArray(v)) {
          setSubdialTypes((v as unknown[]).map(s => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setSubdialTypes(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        }
      },
    },
    // Chapter ring & dial furniture. Four independent suggestions — accepting
    // one never touches another, because none of them gates another: a
    // `chapterRing: none` answer does not clear the minute track, which is
    // routinely printed on the dial face of a ring-less dial.
    chapterRing: {
      label: 'Chapter ring',
      currentDisplay: chapterRing,
      apply: (v) => typeof v === 'string' && setChapterRing(v),
    },
    minuteTrack: {
      label: 'Minute track',
      currentDisplay: minuteTrack,
      apply: (v) => typeof v === 'string' && setMinuteTrack(v),
    },
    // Multi-select, so the array-or-joined-string handler the other multis use.
    dialScales: {
      label: 'Dial scales',
      currentDisplay: dialScales.join(', '),
      apply: (v) => {
        if (Array.isArray(v)) {
          setDialScales((v as unknown[]).map(s => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setDialScales(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
        }
      },
    },
    dialFeatures: {
      label: 'Dial features',
      currentDisplay: dialFeatures.join(', '),
      // The prompt asks for an array; the rendered lookup header can still
      // invite a bare slug, so both shapes land — as dialScales does.
      apply: (v) => {
        if (Array.isArray(v)) {
          setDialFeatures((v as unknown[]).map((x) => String(x).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setDialFeatures(v.split(/[,;]+/).map((x) => x.trim()).filter(Boolean));
        }
      },
    },
    logoType: {
      label: 'Logo type',
      currentDisplay: logoType,
      apply: (v) => typeof v === 'string' && setLogoType(v),
    },
    // Strap - additional fields
    strapColor: {
      label: 'Strap color',
      currentDisplay: strapColor,
      apply: (v) => typeof v === 'string' && setStrapColor(v),
    },
    strapWidth: {
      label: 'Strap width (mm)',
      currentDisplay: strapWidth,
      apply: (v) => {
        if (typeof v === 'number') setStrapWidth(v.toString());
        else if (typeof v === 'string') setStrapWidth(v);
      },
    },
    strapBuckleType: {
      label: 'Buckle type',
      currentDisplay: strapBuckleType,
      apply: (v) => typeof v === 'string' && setStrapBuckleType(v),
    },
    isMicroAdjustable: {
      label: 'Micro-adjustable',
      currentDisplay: strapIsMicroAdjustable ? 'true' : 'false',
      currentValue: boolCurrent(reference.strap?.isMicroAdjustable, strapIsMicroAdjustable),
      apply: (v) => typeof v === 'boolean' && setStrapIsMicroAdjustable(v),
    },
    hasIntegratedBracelet: {
      label: 'Integrated bracelet',
      currentDisplay: hasIntegratedBracelet ? 'true' : 'false',
      currentValue: boolCurrent(reference.hasIntegratedBracelet, hasIntegratedBracelet),
      apply: (v) => typeof v === 'boolean' && setHasIntegratedBracelet(v),
    },
    // Production
    releaseYear: {
      label: 'Release year',
      currentDisplay: productionReleaseYear,
      apply: (v) => {
        if (typeof v === 'number') setProductionReleaseYear(v.toString());
        else if (typeof v === 'string') setProductionReleaseYear(v);
      },
    },
    discontinued: {
      label: 'Discontinued',
      currentDisplay: discontinued ? 'true' : 'false',
      currentValue: boolCurrent(reference.knownDiscontinuation, discontinued),
      apply: (v) => typeof v === 'boolean' && setDiscontinued(v),
    },
    specialEdition: {
      label: 'Special edition',
      currentDisplay: specialEdition ? 'true' : 'false',
      currentValue: boolCurrent(reference.specialEdition, specialEdition),
      apply: (v) => typeof v === 'boolean' && setSpecialEdition(v),
    },
    isLimited: {
      label: 'Limited edition',
      currentDisplay: isLimited ? 'true' : 'false',
      currentValue: boolCurrent(reference.production?.isLimited, isLimited),
      apply: (v) => typeof v === 'boolean' && setIsLimited(v),
    },
    limitedEditionCount: {
      label: 'Edition count',
      currentDisplay: limitedSize,
      apply: (v) => {
        if (typeof v === 'number') setLimitedSize(v.toString());
        else if (typeof v === 'string') setLimitedSize(v);
      },
    },
    // Classification
    watchType: {
      label: 'Watch type',
      currentDisplay: watchType,
      apply: (v) => typeof v === 'string' && setWatchType(v),
    },
    occasion: {
      label: 'Occasion',
      currentDisplay: occasion.join(', '),
      apply: (v) => {
        if (Array.isArray(v)) setOccasion(v.map(String));
        else if (typeof v === 'string') setOccasion(v.split(/[,;]+/).map(s => s.trim()).filter(Boolean));
      },
    },
    functions: {
      label: 'Functions',
      currentDisplay: functions.join(', '),
      apply: (v) => {
        if (Array.isArray(v)) setFunctions(v.map(String));
        else if (typeof v === 'string') setFunctions(v.split(',').map((s: string) => s.trim()).filter(Boolean));
      },
    },
    series: {
      label: 'Series',
      currentDisplay: series,
      apply: (v) => typeof v === 'string' && setSeries(v),
    },
    nickname: {
      label: 'Nickname',
      currentDisplay: nickname,
      apply: (v) => typeof v === 'string' && setNickname(v),
    },
    // Electronics/Module fields
    elecModule: {
      label: 'Module',
      currentDisplay: elecModule,
      apply: (v) => {
        if (typeof v !== 'string') return;
        setElecModule(v);
        // Keep calibre in sync for smartwatches
        if (showModule && !calibre) setCalibre(v);
      },
    },
    elecBattery: {
      label: 'Battery',
      currentDisplay: elecBattery,
      apply: (v) => typeof v === 'string' && setElecBattery(v),
    },
    elecBatteryLife: {
      label: 'Battery life',
      currentDisplay: elecBatteryLife,
      apply: (v) => typeof v === 'string' && setElecBatteryLife(v),
    },
    elecChargeTime: {
      label: 'Charge time',
      currentDisplay: elecChargeTime,
      apply: (v) => typeof v === 'string' && setElecChargeTime(v),
    },
    elecDisplay: {
      label: 'Display type',
      currentDisplay: elecDisplay,
      apply: (v) => typeof v === 'string' && setElecDisplay(v),
    },
    elecLcdType: {
      label: 'LCD type',
      currentDisplay: elecLcdType,
      apply: (v) => typeof v === 'string' && setElecLcdType(v),
    },
    elecLightType: {
      label: 'Light type',
      currentDisplay: elecLightType,
      apply: (v) => typeof v === 'string' && setElecLightType(v),
    },
    // The display's own colours — per-ref, saved at electronics.* beside the
    // module cache but never overwritten by it.
    elecDisplayColor: {
      label: 'Display color',
      currentDisplay: displayColor,
      apply: (v) => setDisplayColor(firstSlug(v)),
    },
    elecTextColor: {
      label: 'Text color',
      currentDisplay: textColor,
      apply: (v) => setTextColor(firstSlug(v)),
    },
    elecBacklightColor: {
      label: 'Backlight color',
      currentDisplay: backlightColor,
      apply: (v) => setBacklightColor(firstSlug(v)),
    },
    // Pocket watch fields — Claude returns these flat (grade, caseStyle, …) and
    // they route into the nested pocketWatch.* section. Only saved when
    // showPocketWatch is set (see handleSave), so applying them on a non-pocket
    // watch is harmless.
    grade: {
      label: 'Grade (pocket watch)',
      currentDisplay: pwGrade,
      apply: (v) => typeof v === 'string' && setPwGrade(v),
    },
    isKeyWound: {
      label: 'Key-wound (pocket watch)',
      currentDisplay: pwIsKeyWound ? 'true' : 'false',
      currentValue: boolCurrent(reference.pocketWatch?.isKeyWound, pwIsKeyWound),
      apply: (v) => typeof v === 'boolean' && setPwIsKeyWound(v),
    },
    caseStyle: {
      label: 'Case style (pocket watch)',
      currentDisplay: pwCaseStyle,
      apply: (v) => typeof v === 'string' && setPwCaseStyle(v),
    },
    escapement: {
      label: 'Escapement (pocket watch)',
      currentDisplay: pwEscapement,
      apply: (v) => typeof v === 'string' && setPwEscapement(v),
    },
    jewelBearing: {
      label: 'Jewel bearing (pocket watch)',
      currentDisplay: pwJewelBearing,
      apply: (v) => typeof v === 'string' && setPwJewelBearing(v),
    },
    movementSize: {
      label: 'Movement size (pocket watch)',
      currentDisplay: pwMovementSize,
      apply: (v) => typeof v === 'string' && setPwMovementSize(v),
    },
    adjustments: {
      label: 'Adjustments (pocket watch)',
      currentDisplay: pwAdjustments.join(', '),
      // Multi-select lookup — AI may return an array of slugs or a joined string.
      apply: (v) => {
        if (Array.isArray(v)) {
          setPwAdjustments((v as unknown[]).map((s) => String(s).trim()).filter(Boolean));
        } else if (typeof v === 'string') {
          setPwAdjustments(v.split(/[,;]+/).map((s) => s.trim()).filter(Boolean));
        }
      },
    },
    isFusee: {
      label: 'Fusée (pocket watch)',
      currentDisplay: pwIsFusee ? 'true' : 'false',
      currentValue: boolCurrent(reference.pocketWatch?.isFusee, pwIsFusee),
      apply: (v) => typeof v === 'boolean' && setPwIsFusee(v),
    },
  };

  /**
   * The ref's images, in the shape enrichReference wants. The editor already
   * holds RESOLVED download URLs (`loadedImageUrl` / `galleryImageUrls`), so it
   * only needs the shared zip — index-aligning each gallery URL with its
   * ImageInfo, since a failed resolve leaves a null in the URL array. The bulk
   * enrich tool starts from raw docs instead and uses
   * `collectTaggedImagesForRef` from the same module.
   */
  const buildTaggedImagesForAI = (): TaggedEnrichImage[] =>
    zipTaggedImages(
      loadedImageUrl,
      galleryImageUrls.map((url, i) => ({ url, tags: galleryImages[i]?.tags })),
    );

  /** How many images a "Suggest with AI" run would send right now. */
  const aiImageCount = buildTaggedImagesForAI().length;

  /**
   * Resolve an AI-proposed `calibre` string against `custom_calibres`, for the
   * note the review table shows beside it.
   *
   * Returns `null` when there is nothing to check — no calibre in the response,
   * a non-string value, or module mode, where the same key carries an electronic
   * module instead.
   *
   * A failure to LOAD the corpus is reported as `unavailable` rather than as a
   * miss. `loadCalibreCandidatesByName` already falls back from the Algolia
   * shortlist to a full collection scan, so reaching this catch means both went
   * down; saying "matches no calibre" on the strength of that is how an admin
   * ends up creating a duplicate of a calibre that was there all along.
   */
  const checkAiCalibreSuggestion = async (
    value: unknown,
  ): Promise<AiCalibreMatchCheck | null> => {
    if (showModule) return null;
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const candidates = await loadCalibreCandidatesByName(value);
      return checkAiCalibreMatch(candidates, value, brand);
    } catch (err) {
      console.warn('Calibre catalogue check failed for the AI suggestion:', err);
      return checkAiCalibreMatch([], value, brand, true);
    }
  };

  const handleSuggestWithAI = async (opts?: {
    fetchUrl?: boolean;
    additionalContext?: string;
    useImage?: boolean;
    model?: string;
  }) => {
    setAILoading(true);
    try {
      // If the user opted to fetch the product URL, scrape it client-side via
      // the Cloudflare proxy and strip to text. Non-fatal: on any failure we
      // warn and continue enriching without the page content.
      let sourceUrlContent: string | undefined;
      const trimmedProductUrl = productUrl.trim();
      if (opts?.fetchUrl && trimmedProductUrl) {
        try {
          const html = await fetchViaCloudflareProxy(trimmedProductUrl);
          const text = html ? htmlToText(html) : '';
          if (text) {
            sourceUrlContent = text.slice(0, MAX_SOURCE_URL_CONTENT_CHARS);
          } else {
            toast('Product URL fetch returned no usable text — enriching without it');
          }
        } catch (err) {
          console.warn('Product URL fetch failed:', err);
          toast('Product URL fetch failed — enriching without it');
        }
      }
      const additionalContext = opts?.additionalContext?.trim() || undefined;

      // Fetch the other references in THIS REF'S COLLECTION as ground truth for
      // the prompt's Notable Variants section (so the AI draws from real refs,
      // not invented ones, and not from another collection). Excludes this ref
      // (by doc id, so an edited/blank ref number can't defeat it) and the
      // brand's variants. Served by Algolia, so a 1,400-ref brand costs no
      // Firestore reads. The collection is the one in the form right now, so a
      // collection the admin has just corrected is what gets matched. A ref with
      // no collection gets no list. Non-fatal — empty on failure.
      let siblingReferences: SiblingReference[] = [];
      try {
        siblingReferences = await getSiblingReferences(brand.id, {
          id: reference.id,
          reference: refNumber,
          collection: collectionName,
        });
      } catch (err) {
        console.warn('Failed to load sibling references:', err);
      }
      const snapshot = {
        brand: brand.name,
        reference: refNumber,
        siblingReferences: siblingReferences.length > 0 ? siblingReferences : undefined,
        model,
        collection: collectionName || undefined,
        productName: productName || undefined,
        description: description || undefined,
        calibre: calibre || undefined,
        movementType: movementType || undefined,
        nickname: nickname || undefined,
        specialEdition: specialEdition || undefined,
        series: series || undefined,
        watchType: watchType || undefined,
        occasion: occasion.length > 0 ? occasion : undefined,
        bezel: bezel || undefined,
        bezelType: bezelType || undefined,
        bezelColors: multiLookupValuesForWrite(bezelColors),
        caseColors: multiLookupValuesForWrite(caseColors),
        crownType: multiLookupValuesForWrite(crownType),
        glass: glass || undefined,
        crystal: multiLookupValuesForWrite(crystal),
        dial: dial || undefined,
        dialColor: dialColors.length > 0 ? dialColors.join(', ') : undefined,
        dialStyle: dialStyle || undefined,
        knownDiscontinuation: Boolean(discontinued),
        case: {
          material: caseMaterial || undefined,
          finish: caseFinish || undefined,
          coating: caseCoating || undefined,
          shape: caseShape || undefined,
          caseBack: caseCaseBack || undefined,
          diameterMm: caseDiameter || undefined,
          heightMm: caseThickness || undefined,
          lugWidthMm: caseLugWidth || undefined,
          lugToLugMm: caseLugToLug || undefined,
          waterResistanceM: caseWaterResistance || undefined,
          weightG: caseWeightG || undefined,
        },
        dialAndHands: {
          color: dialColors.length > 0 ? dialColors.join(', ') : undefined,
          material: dialMaterial || undefined,
          finish: dialFinish || undefined,
          indexes: dialMarkerValuesForWrite(dialIndexes),
          numerals: dialMarkerValuesForWrite(dialNumerals),
          hands: dialHands || undefined,
          handsColor: handsColors.length > 0 ? handsColors.join(', ') : undefined,
          indexColor: indexColors.length > 0 ? indexColors.join(', ') : undefined,
          handTypes: handTypes.length > 0 ? handTypes.join(', ') : undefined,
          // Sent so the model can see what the reference already claims about
          // its date — the prompt's DEFER TO EXISTING VALUE rules read off this
          // payload, and dateDisplay being present is also the strongest signal
          // that the watch HAS a date at all.
          dateDisplay: dateDisplay || undefined,
          dateWindowPosition: dateWindowPosition || undefined,
          dateWindowFrame: dateWindowFrame || undefined,
          dateWheelColor: dateWheelColor || undefined,
          dateWheelTextColor:
            dateWheelTextColors.length > 0 ? dateWheelTextColors.join(', ') : undefined,
          luminova: dialLuminova || undefined,
          // Sent for the same reason as the date fields above — the prompt's
          // DEFER TO EXISTING VALUE rules read off this payload, and a
          // lumePresence already on the ref is the strongest signal that the
          // watch has lume at all.
          lumePresence: lumePresence || undefined,
          lumeMaterial: lumeMaterial || undefined,
          lumeDayColor: lumeDayColor || undefined,
          lumeGlowColor: lumeGlowColor || undefined,
          subdials: dialSubdials ? parseInt(dialSubdials) : undefined,
          // Sent joined, the shape it persists in — the prompt's DEFER TO
          // EXISTING VALUE rules read off this payload, and the functions
          // already recorded are the strongest constraint on the ones the model
          // is about to add.
          subdialTypes: subdialTypes.length > 0 ? subdialTypes.join(', ') : undefined,
          // Chapter ring & dial furniture, sent for the same DEFER TO EXISTING
          // VALUE reason. `dialScales` goes joined, the shape it persists in.
          chapterRing: chapterRing || undefined,
          minuteTrack: minuteTrack || undefined,
          dialScales: dialScales.length > 0 ? dialScales.join(', ') : undefined,
          dialFeatures: dialFeatures.length > 0 ? dialFeatures.join(', ') : undefined,
          logoType: logoType || undefined,
        },
        strap: {
          material: strapMaterial || undefined,
          color: strapColor || undefined,
          width: strapWidth || undefined,
          buckleType: strapBuckleType || undefined,
          isMicroAdjustable: strapIsMicroAdjustable || undefined,
        },
        // Left as `|| undefined` deliberately: this object is the AI INPUT
        // snapshot, not a write. `removeUndefined` strips undefined before it
        // is sent, and the callable's existingData filter drops null too, so
        // the null-to-clear rule below buys nothing here.
        hasIntegratedBracelet: hasIntegratedBracelet || undefined,
        production: {
          isLimited: Boolean(isLimited),
          limitedSize: limitedSize || undefined,
          releaseYear: productionReleaseYear || undefined,
        },
        movement: {
          jewels: jewels || undefined,
          powerReserveHours: powerReserveHours || undefined,
          isChronograph: Boolean(isChronograph),
          isGMT: Boolean(isGMT),
          baseMovement: baseMovement || undefined,
        },
        electronics: showModule ? {
          module: elecModule || undefined,
          battery: elecBattery || undefined,
          batteryLife: elecBatteryLife || undefined,
          chargeTime: elecChargeTime || undefined,
          display: elecDisplay || undefined,
          lcdType: elecLcdType || undefined,
          lightType: elecLightType || undefined,
          // Sent so the model confirms or corrects the stored colours rather
          // than answering blind.
          displayColor: displayColor || undefined,
          textColor: textColor || undefined,
          backlightColor: backlightColor || undefined,
        } : undefined,
        functions: functions.length > 0 ? functions : undefined,
      };
      // Through the one payload builder every enrichReference caller uses, so
      // the callable's allowlist sees the same stored-ref names from here as
      // from the bulk tools and the importer.
      const cleaned = buildEnrichReferencePayload(
        removeUndefined(snapshot as unknown as Record<string, unknown>),
        { brandName: brand.name, brandFounded: brand.founded, siblingReferences },
      );
      // When the ref is linked to a calibre that defines tier variants, pass the
      // ones THIS brand may use (see `brandScopedTiers`) so the AI can suggest
      // which tier applies (returns calibreTierId/Name) without ever seeing —
      // and so never picking — another brand's modification of the same base.
      // The ref's images as Firebase Storage download URLs, each carrying its
      // tags so the callable can route a field to the image that shows it. The
      // primary shot gets the synthetic `primary` tag; gallery images carry
      // whatever the curator tagged them with (possibly nothing — an untagged
      // image is still sent, and the prompt treats it as supporting evidence).
      // Empty when the ref has no images or their URLs have not resolved yet, in
      // which case the callable runs text-only.
      const taggedImages =
        opts?.useImage === false ? [] : buildTaggedImagesForAI();
      if (taggedImages.length > 0) {
        // Box / wrist / papers shots are filtered out upstream — this is what
        // actually gets paid for, so log the tags rather than a bare count.
        console.info(
          `[enrich] sending ${taggedImages.length} image(s): ` +
            taggedImages
              .map((i) => (i.tags.length ? i.tags.join('+') : 'untagged'))
              .join(', '),
        );
      }

      const result = await enrichReferenceWithAI(cleaned, brandScopedTiers, {
        sourceUrlContent,
        additionalContext,
        taggedImages,
        model: opts?.model,
      });
      const suggestions = result.suggestions || {};
      const confidence = result.confidence || {};
      // Years the callable dropped for predating the brand's founding — say so,
      // or the missing year reads as the model having had no answer.
      for (const r of result.yearFloorRejections ?? []) {
        toast(`AI ${r.field} ${r.year} rejected: before ${brand.name}'s founding (${r.founded})`, { icon: '⚠️' });
      }
      if (Object.keys(suggestions).length === 0) {
        toast('No new suggestions — fields look complete');
        return;
      }
      // Verify every lookup-backed suggestion against the field's CURRENT slug
      // list before deciding what to auto-accept. Only the collections this
      // response actually touched are fetched (typically a handful), once, on a
      // path the user explicitly triggered.
      const unverified = await findUnverifiedEnumSuggestions(suggestions);

      // Same idea for the one free-text field with a catalogue behind it rather
      // than a lookup list: resolve the proposed calibre now, so the review row
      // can say whether ticking it will actually link the reference. Costs the
      // one Algolia search + handful of document reads that
      // `loadCalibreCandidatesByName` already does for the Calibre input, on a
      // path the admin explicitly triggered.
      //
      // Skipped in module mode, where the `calibre` key carries an electronic
      // module — a different entity, in a different collection, that this
      // resolver knows nothing about.
      const calibreCheck = await checkAiCalibreSuggestion(suggestions.calibre);

      // Default-accept high-confidence suggestions — except any value we could
      // not match to its lookup, which always needs a human look.
      //
      // The calibre tier is the one deliberate exception: it pre-checks on
      // MEDIUM as well as high. Every other field is a property of the watch
      // that the model either read or guessed, so a medium guess deserves a
      // human. A tier is a pick from a closed list the request itself supplied,
      // scoped to this brand and usually two or three entries long — "medium"
      // there means the model is choosing between known options rather than
      // inventing one, and `resolveTierSuggestion` throws the answer away
      // outright if it names no tier this calibre defines. A low-confidence
      // tier is still never pre-checked, and the table says so when the ref
      // already carries a tier the admin set by hand.
      //
      // A calibre naming no catalogue entry is held back the same way, and for
      // the same reason a lookup miss is: it would otherwise arrive ticked on
      // the model's own high confidence and apply in silence. An unreadable
      // catalogue is NOT held back — that is ignorance, not a miss, and the row
      // says as much instead.
      // Agreement is judged on the DISPLAY strings the row renders, through the
      // bulk grid's own helper — so "does this match what we hold?" is answered
      // identically on both screens rather than twice, differently.
      const accepted: Record<string, boolean> = {};
      const reasons: Record<string, CheckedReason> = {};
      for (const [key, value] of Object.entries(suggestions)) {
        const handler = aiFieldHandlers[key];
        const suggestedDisplay =
          typeof value === 'object' ? JSON.stringify(value) : String(value);
        // A boolean row compares RAW values, not display strings: the editor
        // renders an unset boolean and a stored `false` identically, and the two
        // tick differently (unset is an empty field; `false` being raised to
        // `true` is a discovery). Every other row compares as it always has.
        const storedBoolean = BOOLEAN_ENRICH_KEYS.has(key)
          ? { value: handler?.currentValue as boolean | undefined }
          : undefined;
        const state = defaultAcceptForSuggestion(
          key,
          confidence[key],
          !!unverified[key],
          !!calibreCheck && !calibreCheck.unavailable && !calibreCheck.matchedName,
          storedBoolean
            ? booleanAgreement(storedBoolean.value, value)
            : handler ? cellAgreement(handler.currentDisplay, suggestedDisplay) : 'new',
          // Live, not stored: a description the curator typed this session is
          // already 'manual' and is protected like a saved one. Brand-authored
          // copy counts as manual too (effectiveDescriptionSource).
          effectiveDescriptionSource(
            descriptionSource,
            // Ref-level and written by the portal callables, but missing from the
            // admin's local WatchReference type — the same read the portal uses.
            (reference as { useAuthoredDescription?: boolean }).useAuthoredDescription,
          ),
          storedBoolean,
        );
        accepted[key] = state.accepted;
        reasons[key] = state.reason;
      }
      setAISuggestions(suggestions);
      setAIConfidence(confidence);
      setAIAccepted(accepted);
      setAIAcceptReason(reasons);
      setAIUnverified(unverified);
      setAICalibreCheck(calibreCheck);
      // A picker choice from the previous response must not survive into this
      // one — the suggested tier, and possibly the whole calibre, just changed.
      setAITierOverride(null);
      setShowAISuggestions(true);
    } catch (error) {
      console.error('AI enrichment failed:', error);
      const message = error instanceof Error ? error.message : 'AI enrichment failed';
      toast.error(message);
    } finally {
      setAILoading(false);
    }
  };

  const handleApplyAISuggestions = async () => {
    let applied = 0;
    let calibreWasApplied = false;
    let appliedCalibreName = '';
    // True when the accepted calibre value was deliberately NOT written to the
    // legacy `calibre` string because that dual-write is switched off. The
    // suggestion still counts as applied if it produces a `calibreId` link.
    let legacyCalibreStringSuppressed = false;

    // Tier suggestions are resolved specially (against the correct calibre's
    // tiers, AFTER any calibre change lands) so they're deferred out of the
    // generic per-field apply loop. The AI may return a stable id, a name, or
    // both — capture whichever were accepted.
    let tierWasAccepted = false;
    let tierIdSuggestion: string | undefined;
    let tierNameSuggestion: string | undefined;

    for (const [key, value] of Object.entries(aiSuggestions)) {
      if (!aiAccepted[key]) continue;
      if (TIER_SUGGESTION_KEYS.has(key)) {
        if (typeof value === 'string' && value.trim()) {
          tierWasAccepted = true;
          if (key === 'calibreTierId') tierIdSuggestion = value;
          else tierNameSuggestion = value;
        }
        continue;
      }
      const handler = aiFieldHandlers[key];
      if (!handler) continue;

      // Phase 2 PR1: consult the collection-level `isMultiSelect` flag as the
      // single source of truth for AI-suggestion shape routing. Each field's
      // handler.apply already handles either scalar or array — the helper's
      // job here is to warn when the AI returned a shape that doesn't match
      // what the current lookup config says the field is, so a Phase 3
      // single→multi flip surfaces routing gaps before they land silently.
      // Unknown fields (not in ENRICH_FIELD_LOOKUPS — e.g. isLimited,
      // discontinued) resolve to false and skip the check.
      const expectsMulti = await getLookupMultiSelectFor(key);
      if (expectsMulti && !Array.isArray(value) && typeof value === 'string') {
        console.warn(
          `handleApplyAISuggestions: field ${key} is multi-select but AI returned a scalar`,
        );
      } else if (!expectsMulti && Array.isArray(value)) {
        console.warn(
          `handleApplyAISuggestions: field ${key} is single-select but AI returned an array`,
        );
      }

      // The legacy free-text `calibre` string is one of the two dual-writes the
      // calibres-as-modules migration retires, so its flag decides whether the
      // accepted value reaches the form at all. The value is captured either
      // way — it is what resolves the authoritative `calibreId` link below.
      // In module mode the AI's `calibre` maps to `elecModule`, which is an
      // electronic module rather than a calibre and is no part of this
      // migration, so the flag leaves that path alone.
      const suppressLegacyCalibreString =
        key === 'calibre' && !showModule && !dualWriteLegacyCalibreString;
      if (suppressLegacyCalibreString) {
        legacyCalibreStringSuppressed = true;
      } else {
        handler.apply(value);
        applied++;
      }

      if (key === 'calibre' && typeof value === 'string') {
        calibreWasApplied = true;
        appliedCalibreName = value;
      }
    }

    // An explicit pick in the review table's tier picker overrides the model.
    // The picker only ever offers ids off the linked calibre's own brand-scoped
    // list, so the choice needs no name resolution and no fuzzy matching — and
    // the empty string is the Clear button, meaning "apply no tier", which is a
    // decision rather than an absence and so still counts as accepted.
    //
    // Deliberately ignored when the same apply also re-links the calibre: the
    // picker was populated from the OUTGOING calibre's tiers, so its ids mean
    // nothing on the incoming one. The table disables the picker in that case;
    // this is the matching guard on the write side, so a stale choice can never
    // survive a race between the two.
    const tierPick = tierPickerContribution(aiTierOverride, calibreWasApplied);
    if (tierPick.overridesModel) {
      tierWasAccepted = true;
      tierIdSuggestion = tierPick.tierId;
      tierNameSuggestion = undefined;
    }

    // Does the reference end up on a DIFFERENT calibre than it started on? A
    // re-link invalidates every tier pointer, because tier ids belong to the
    // calibre that defines them. Decided below, once the accepted name has been
    // resolved: a resolved id that differs re-links the ref, and with no id
    // resolved an already-linked ref keeps its identity while an unlinked one
    // is re-linked by NAME exactly as it always was.
    let calibreIdentityChanged = false;

    // The tiers a tier suggestion resolves against: the newly-linked calibre's
    // (fetched below) when the identity changed, else the currently linked one.
    // Brand-scoped either way — the AI was only ever offered this brand's tiers,
    // and a fuzzy NAME match must not be able to reach past that scope onto
    // another brand's modification of the same base movement.
    let tierTargetTiers: CalibreTier[] = brandScopedTiers;

    // The FULL tier list of whatever calibre the ref ends up linked to, or null
    // when we could not learn it. Feeds the staleness guard at the end, which
    // treats null as "leave the pointer alone": a failed lookup is ignorance,
    // not evidence that the stored tier is dangling.
    let nextCalibreTiers: CalibreTier[] | undefined;

    // Where the tier pointer should land. Threaded through a local rather than
    // written straight to state so the staleness guard can validate whatever the
    // branches below chose — `calibreTierId` is React state and would still read
    // its pre-apply value at that point.
    let nextTierId = calibreTierId;

    // If a calibre was accepted, resolve it to the authoritative `calibreId`
    // link, populate the legacy movement fields, and capture the new calibre's
    // tiers for tier resolution.
    if (calibreWasApplied && appliedCalibreName.trim()) {
      setAIApplying(true);
      const accepted = appliedCalibreName.trim();
      try {
        // Two independent lookups: the callable that hydrates the movement spec
        // (and normalizes the display name), and the local shortlist the
        // `calibreId` resolver decides against. Neither needs the other, so they
        // run together and each is allowed to fail on its own — a dead callable
        // must not also cost us the link, and vice versa.
        const [lookup, candidates] = await Promise.all([
          lookupCalibre(accepted, brand.name).catch((err) => {
            // Non-fatal — the calibre suggestion still applied, we just have no
            // movement spec to copy from.
            console.warn('Calibre lookup failed during AI apply:', err);
            return null;
          }),
          showModule
            ? Promise.resolve<CustomCalibreEntry[]>([])
            : loadCalibreCandidatesByName(accepted).catch((err) => {
                console.warn('Calibre candidate search failed during AI apply:', err);
                return [] as CustomCalibreEntry[];
              }),
        ]);

        // `buildAiCalibreApply` falls back to the callable's `normalizedName`
        // when the accepted name resolves to nothing — but the shortlist above
        // was searched for the ACCEPTED name, so the document that fallback
        // needs may not be in it. Top the corpus up with a second search when
        // the two strings genuinely differ, and only then: this runs on the miss
        // path of an explicitly triggered apply, and skipping it would leave the
        // fallback resolving against a corpus that cannot contain its answer.
        const normalizedName = lookup?.normalizedName?.trim() || '';
        const needsNormalizedCandidates =
          !showModule &&
          !!normalizedName &&
          normalizeCalibreKey(normalizedName) !== normalizeCalibreKey(accepted) &&
          !candidates.some(
            (c) => normalizeCalibreKey(c.name || '') === normalizeCalibreKey(normalizedName),
          );
        const extraCandidates = needsNormalizedCandidates
          ? await loadCalibreCandidatesByName(normalizedName).catch((err) => {
              console.warn(
                'Calibre candidate search for the normalized name failed during AI apply:',
                err,
              );
              return [] as CustomCalibreEntry[];
            })
          : [];

        // One resolver decides both halves, as it does for the bulk action bar:
        // the `calibreId` link and any legacy specs written beside it come off
        // the same entry, so the ref can never point at one calibre while
        // carrying another's movement values.
        const plan = buildAiCalibreApply(
          [...candidates, ...extraCandidates],
          accepted,
          lookup?.normalizedName,
          brand,
          {
            legacyMovement: dualWriteLegacyMovement,
            legacyCalibreString: dualWriteLegacyCalibreString,
          },
        );

        const previousCalibreId = calibreId.trim();
        calibreIdentityChanged = plan.calibreId
          ? plan.calibreId !== previousCalibreId
          : !previousCalibreId;

        if (plan.calibreId) {
          setCalibreId(plan.calibreId);
          if (plan.resolved) setLinkedCalibreById(plan.resolved.entry);
          // The review table warned that the accepted name matches nothing, and
          // a link appeared anyway — because the calibre database knew a fuller
          // name for it. Say which name won, rather than let the apply quietly
          // contradict the warning the admin just read.
          if (plan.resolvedVia === 'normalized' && plan.resolved) {
            toast.success(
              `Linked to “${plan.resolved.entry.name}” — matched via the calibre database, not the suggested name`,
            );
          }
          // The accepted suggestion produced no visible form change when the
          // legacy string is switched off, but it did produce the link — so it
          // still counts as an applied suggestion.
          if (legacyCalibreStringSuppressed) applied++;
        } else if (legacyCalibreStringSuppressed) {
          toast.error(
            `"${accepted}" matched no calibre — nothing to write with the legacy calibre string switched off`,
          );
        } else if (!showModule) {
          // The dual-write is ON, so the free text lands and the reference stays
          // unlinked. The review row said this would happen; this is the
          // after-the-fact confirmation, because the dialog is now closed and
          // the Calibre field several sections down is the only other tell.
          toast(
            `“${accepted}” matched no calibre — saved as text, reference left unlinked`,
            { icon: '⚠️' },
          );
        }

        // Prefer the resolved entry's own tiers: they come off the very document
        // `calibreId` now points at. The callable's copy is the fallback for a
        // name-based apply that resolved to no id.
        nextCalibreTiers = plan.resolved?.entry.data?.tiers ?? lookup?.data?.tiers;

        if (lookup?.success && lookup.data) {
          if (plan.applyLegacyMovement) {
            const fieldsUpdated = populateFromCalibreData(lookup.data, 'overwrite');
            if (fieldsUpdated > 0) {
              toast.success(`Populated ${fieldsUpdated} movement fields from calibre database`);
            }
          }
          if (plan.calibreString) setCalibre(plan.calibreString);
        }

        if (calibreIdentityChanged) {
          tierTargetTiers = tiersForBrand(nextCalibreTiers, brand.id);
        }
      } catch (err) {
        // Non-fatal, as this step has always been — the calibre suggestion is
        // already applied to the form and the admin can still save. With no
        // tiers learned, `nextCalibreTiers` stays undefined and the staleness
        // guard below leaves the tier pointer alone rather than clearing it on
        // the strength of a failure.
        console.warn('Calibre apply failed after the AI suggestion landed:', err);
      } finally {
        setAIApplying(false);
      }
    }

    // Resolve/clear the tier once, against the target tiers chosen above.
    //
    // An explicit Clear in the picker is a decision, not a failed resolution:
    // it must empty the pointer outright rather than fall through to the
    // "nothing resolved, leave it alone" branch, which exists for a suggestion
    // that could not be matched. Same re-link guard as the override itself —
    // clearing a tier chosen against the outgoing calibre would act on a stale
    // view.
    if (tierPick.cleared) {
      if (nextTierId) applied++;
      nextTierId = '';
    } else if (tierWasAccepted) {
      const resolved = resolveTierSuggestion(
        tierTargetTiers,
        tierIdSuggestion,
        tierNameSuggestion,
      );
      if (resolved) {
        nextTierId = resolved;
        applied++;
        if (calibreIdentityChanged) {
          // Tell the user which tier the AI's suggestion mapped to on the newly
          // linked calibre — the tier id changed silently underneath them.
          const name = tierTargetTiers.find((t) => t.id === resolved)?.name ?? '';
          if (name) toast.success(`Applied tier "${name}" from new calibre`);
        }
      } else if (calibreIdentityChanged) {
        // Calibre re-linked but the suggested tier doesn't resolve on it — clear
        // the (now-dangling) pointer rather than leave the old calibre's id. We
        // deliberately do NOT fall back to the previous tier: the AI actively
        // named a different tier, so a leftover shouldn't override that.
        nextTierId = '';
      } else {
        console.warn(
          `AI suggested tier (id "${tierIdSuggestion ?? ''}", name "${tierNameSuggestion ?? ''}") did not resolve on linked calibre "${activeLinkedCalibre?.name ?? calibre}"; leaving tier unchanged`,
        );
      }
    } else if (calibreIdentityChanged) {
      // Calibre re-linked with NO AI tier suggestion, but the ref had a tier on
      // the OLD calibre whose id is now dangling. Option B: try to carry that
      // tier across by NAME (case-insensitive + alias/AKA, never a fuzzy guess)
      // to the new calibre; clear only if nothing resolves (Option A fallback).
      const prevTierName = (activeLinkedCalibre?.data?.tiers ?? []).find(
        (t) => t.id === calibreTierId.trim(),
      )?.name;
      const preserved = prevTierName
        ? resolveTierSuggestion(tierTargetTiers, undefined, prevTierName)
        : null;
      if (preserved) {
        nextTierId = preserved;
        const newName =
          tierTargetTiers.find((t) => t.id === preserved)?.name ?? prevTierName;
        toast.success(`Preserved tier "${newName}" on new calibre`);
      } else {
        nextTierId = '';
      }
    }

    // Final staleness guard, after every branch above has had its say. Those
    // branches choose against the BRAND-SCOPED tier list, and the
    // identity-unchanged case never looks at the tiers at all — but the ref is
    // now linked to whatever `calibreId` names, and a pointer naming no tier of
    // THAT calibre is dangling however it got there. It catches the case none of
    // the branches can see: a tier left behind by some earlier calibre on a ref
    // whose calibre identity did not change this time round.
    //
    // Runs only when the new calibre's full tier list is actually known —
    // clearing a pointer because a lookup failed would be acting on ignorance.
    if (nextCalibreTiers && isTierStaleOnCalibre(nextTierId, nextCalibreTiers)) {
      console.warn(
        `Clearing calibre tier "${nextTierId}" — it names no tier on the linked calibre`,
      );
      nextTierId = '';
    }

    if (nextTierId !== calibreTierId) setCalibreTierId(nextTierId);

    if (applied > 0) {
      toast.success(`Applied ${applied} AI suggestion${applied === 1 ? '' : 's'}`);
    }

    setShowAISuggestions(false);
  };

  const handleSave = async () => {
    if (!refNumber.trim()) {
      toast.error('Reference number is required');
      return;
    }

    setIsProcessing(true);
    try {
      const diameterVal = caseDiameter ? parseFloat(caseDiameter) : undefined;
      const thicknessVal = caseThickness
        ? parseFloat(caseThickness)
        : undefined;
      const lugToLugVal = caseLugToLug ? parseFloat(caseLugToLug) : undefined;
      const lugWidthVal = caseLugWidth ? parseFloat(caseLugWidth) : undefined;
      const waterResVal = caseWaterResistance
        ? parseFloat(caseWaterResistance)
        : undefined;
      const waterResStr = caseWaterResistance || undefined;

      const updates: Omit<
        Partial<WatchReference>,
        | 'moduleId'
        | 'calibreTierId'
        | 'calibreId'
        | 'calibreOverrides'
        | 'hasIntegratedBracelet'
      > & {
        // null clears the link (vs. undefined which removeUndefined would strip)
        moduleId?: string | null;
        // Same null-to-clear semantics: unticking the box must actually remove
        // the flag, and `undefined` would be stripped before the write.
        hasIntegratedBracelet?: boolean | null;
        // Same null-to-clear semantics for the tier pointer.
        calibreTierId?: string | null;
        // Phase 3a: null-to-clear for the authoritative calibre FK and the
        // per-ref overrides map (removeUndefined keeps null but drops {} — so an
        // emptied overrides object must be written as null to actually clear).
        calibreId?: string | null;
        calibreOverrides?: CalibreOverrides | null;
      } = {
        reference: refNumber,
        model,
        collection: collectionName || undefined,
        productName: productName || undefined,
        description: description || undefined,
        // Provenance, written only when the description text changed.
        ...descriptionProvenancePatch(
          reference.description,
          description,
          descriptionSource,
          Timestamp.now(),
        ),
        // Write-once, fill-empty snapshot: only set when an AI rewrite captured
        // it this session (or the ref already carried one). `undefined` on plain
        // saves is dropped by removeUndefined, so it never clobbers an existing
        // value.
        originalDescription: originalDescription || undefined,
        calibre: calibre || undefined,
        // Authoritative calibre link. null clears it; the trigger clears
        // calibreCache on the next write when this is absent.
        calibreId: calibreId.trim() ? calibreId.trim() : null,
        // Per-ref overrides: only the set keys, or null to clear (no link, or all
        // fields back on Inherit). Written as a whole-map replace.
        calibreOverrides: (() => {
          if (!calibreId.trim()) return null;
          // Fold in any diverging Group A legacy values not yet overridden —
          // the authoritative "first save after link" migration pass (also
          // catches refs linked before this landed). Dual-write: legacy stays.
          // Prefer the effective cache (base + tier) when it exists; fall back
          // to the linked calibre doc for refs linked but not yet cache-built.
          const spec = liveCalibreCache
            ? cacheBaseData
            : activeLinkedCalibre?.data ?? cacheBaseData;
          const merged = {
            ...calibreOverrides,
            ...computeGroupAMigrations(spec),
          };
          const clean = cleanOverrides(merged);
          return Object.keys(clean).length > 0 ? clean : null;
        })(),
        // Persist the selected tier, or null to clear (Base / no tiers / a
        // dangling pointer to a deleted tier). The picker already resets its
        // displayed value to Base when the tier is missing; write null so the
        // stale id is actually removed.
        calibreTierId: (() => {
          const id = calibreTierId.trim();
          if (!id) return null;
          const tiers = activeLinkedCalibre?.data?.tiers ?? [];
          return tiers.some((t) => t.id === id) ? id : null;
        })(),
        movementType: movementType || undefined,
        nickname: nickname || undefined,
        specialEdition,
        knownDiscontinuation: discontinued,
        series: series || undefined,
        watchType: watchType || undefined,
        // Write '' (not undefined) when cleared so the empty value overwrites the
        // stale one — undefined is stripped by removeUndefined, leaving the old
        // occasion on the doc.
        occasion: occasion.length > 0 ? occasion.join(', ') : '',
        bezel: bezel || undefined,
        bezelType: bezelType || undefined,
        // Same clear-must-write-[] rule as crownType below, and for the same
        // reason: [] only when the ref actually HAD colours to clear.
        bezelColors: clearedArrayForWrite(bezelColors, reference.bezelColors),
        caseColors: clearedArrayForWrite(caseColors, reference.caseColors),
        // Clearing must write [] (not undefined) or removeUndefined strips the
        // key and the stale crown survives — the same trap `occasion` above
        // documents by writing ''. But [] is only written when there WAS
        // something to clear: stamping an empty array onto every ref that never
        // had a crown is what migrateCrownTypeCrystal deliberately avoids
        // (an empty array reads as "curated as none" and keeps the ref in the
        // "has a value" bucket for the fill-empty bulk tools).
        crownType: clearedArrayForWrite(crownType, reference.crownType),
        // `glass` was a free-text twin of `crystal`, which is the lookup-backed
        // (lookup_crystal_materials) canonical field. The Glass input is gone
        // from the form; the value is folded into `crystal` on the next save of
        // any ref that has one and no crystal — one-way, fill-empty, so a
        // curated crystal is never clobbered. `glass` itself is left on the doc:
        // watchlock's mapper still reads it as the last fallback in the
        // case.crystal → crystal → glass chain.
        glass: glass || undefined,
        // Same clear-with-[] rule, and the glass fold now splits: a legacy
        // free-text `glass` is comma-joined prose as often as it is one value.
        crystal: clearedArrayForWrite(
          crystal.length > 0 ? crystal : parseMultiLookupValues(glass),
          reference.crystal,
        ),
        // Legacy top-level dial mirror. Write '' (not undefined) when cleared so
        // the patch explicitly overwrites the stale value — undefined gets dropped
        // by removeUndefined, leaving the old dial color to reappear via the
        // `dialAndHands?.color || reference.dial` read-back fallback.
        dial: dial || '',
        movement: {
          frequencyVph: frequencyVph ? parseInt(frequencyVph) : undefined,
          jewels: jewels ? parseInt(jewels) : undefined,
          powerReserveHours: powerReserveHours
            ? parseInt(powerReserveHours)
            : undefined,
          isChronometer: Boolean(isChronometer),
          isCosc: Boolean(isCosc),
          // Dual-name trap shim: shared-types carries BOTH spellings and the RN
          // app reads only `isCOSC` (ReferenceDetailScreen's certification chip,
          // WatchReferenceDetailDrawer, watchReferenceMapper). Writing only the
          // admin spelling meant every curated ref rendered with no COSC row.
          // Write both until a later phase picks a canonical and migrates.
          isCOSC: Boolean(isCosc),
          isGlashuetteRegulation: Boolean(isGlashuetteRegulation),
          isGenevaSeal: Boolean(isGenevaSeal),
          isGrandSeikoStandard: Boolean(isGrandSeikoStandard),
          isMETAS: Boolean(isMETAS),
          hacking: hacking || undefined,
          manualWinding: manualWinding || undefined,
          quickSetDate: quickSetDate || undefined,
          windingDirection: windingDirection || undefined,
          baseMovement: baseMovement || undefined,
          diameterMm: movementDiameterMm
            ? parseFloat(movementDiameterMm)
            : undefined,
          heightMm: movementHeightMm ? parseFloat(movementHeightMm) : undefined,
          isChronograph: Boolean(isChronograph),
          isGMT: Boolean(isGMT),
          date: movementDate.length > 0 ? movementDate : undefined,
          hands: movementHands.length > 0 ? movementHands : undefined,
          chronograph:
            movementChronograph.length > 0 ? movementChronograph : undefined,
          additionals:
            movementAdditionals.length > 0 ? movementAdditionals : undefined,
          astronomical:
            movementAstronomical.length > 0 ? movementAstronomical : undefined,
          acoustic: movementAcoustic.length > 0 ? movementAcoustic : undefined,
          liftAngleDegrees: liftAngleDegrees ? parseFloat(liftAngleDegrees) : undefined,
          isInHouse: Boolean(isInHouse),
          countryOfManufacture: countryOfManufacture || undefined,
          antiShockSystem: antiShockSystem || undefined,
          rotorType: rotorType || undefined,
          columnWheel: columnWheel || undefined,
          serviceIntervalYears: serviceIntervalYears ? parseInt(serviceIntervalYears) : undefined,
          lignes: lignes || undefined,
          regulatorSystem: regulatorSystem || undefined,
          casingDiameterMm: casingDiameterMm ? parseFloat(casingDiameterMm) : undefined,
          manualWindingDirection: manualWindingDirection || undefined,
          hairspring: hairspring || undefined,
          doubleBarrel: doubleBarrel || undefined,
          escapement: escapement || undefined,
          batteryType: batteryType || undefined,
          batteryLifeYears: batteryLifeYears ? parseFloat(batteryLifeYears) : undefined,
        },
        case: {
          diameter: diameterVal,
          diameterMm: diameterVal,
          sizeMm: diameterVal,
          thickness: thicknessVal,
          heightMm: thicknessVal,
          lugToLug: lugToLugVal,
          lugToLugMm: lugToLugVal,
          lugWidth: lugWidthVal,
          lugWidthMm: lugWidthVal,
          material: caseMaterial || undefined,
          waterResistance: waterResStr,
          waterResistanceM: waterResVal,
          shape: caseShape || undefined,
          caseBack: caseCaseBack || undefined,
          caseback: caseCaseBack || undefined,
          code: caseCode || undefined,
          coating: caseCoating || undefined,
          finish: caseFinish || undefined,
          weightG: caseWeightG ? parseFloat(caseWeightG) : undefined,
        },
        dialStyle: dialStyle.trim() || undefined,
        dialAndHands: {
          // Write '' (not undefined) when cleared. undefined is stripped by
          // removeUndefined, and if it were the only field the whole dialAndHands
          // map would be dropped too — leaving the stale color on the doc.
          color: dialColors.length > 0 ? dialColors.join(', ') : '',
          material: dialMaterial || undefined,
          finish: dialFinish || undefined,
          hands: dialHands || undefined,
          // Write [] (not undefined) when cleared, for the same reason `color`
          // above writes '': removeUndefined strips undefined, which would leave
          // the stale markers on the doc.
          indexes: dialIndexes.length > 0 ? dialIndexes : [],
          numerals: dialNumerals.length > 0 ? dialNumerals : [],
          luminova: dialLuminova || undefined,
          // Dual-name trap shim — same story as movement.isCOSC above: the RN
          // app reads only `lume`, so a ref saved with just `luminova` rendered
          // with no "Luminous" row at all.
          lume: dialLuminova || undefined,
          // Write '' (not undefined) when cleared so the multi-select clearing
          // persists — same rationale as `color` above.
          handsColor: handsColors.length > 0 ? handsColors.join(', ') : '',
          indexColor: indexColors.length > 0 ? indexColors.join(', ') : '',
          handTypes: handTypes.length > 0 ? handTypes.join(', ') : '',
          // Write '' (not undefined) when cleared, same rationale as `color` and
          // `handsColor` above: removeUndefined strips undefined, which would
          // leave the stale slug on the doc. Matters more here than elsewhere —
          // clearing dateDisplay is how an admin corrects a ref the AI wrongly
          // decided had a date.
          dateDisplay: dateDisplay || '',
          dateWindowPosition: dateWindowPosition || '',
          dateWindowFrame: dateWindowFrame || '',
          dateWheelColor: dateWheelColor || '',
          dateWheelTextColor:
            dateWheelTextColors.length > 0 ? dateWheelTextColors.join(', ') : '',
          // Write '' (not undefined) when cleared, same rationale as the date
          // fields above: removeUndefined strips undefined, which would leave
          // the stale slug on the doc. Matters here too — clearing
          // lumePresence is how an admin corrects a ref the AI decided had
          // lume when it does not.
          lumePresence: lumePresence || '',
          lumeMaterial: lumeMaterial || '',
          lumeDayColor: lumeDayColor || '',
          lumeGlowColor: lumeGlowColor || '',
          subdials: dialSubdials ? parseInt(dialSubdials) : undefined,
          // '' (not undefined) when cleared, same rationale as handTypes and
          // the lume fields above: removeUndefined strips undefined, which
          // would leave the stale joined string on the doc.
          subdialTypes: subdialTypes.length > 0 ? subdialTypes.join(', ') : '',
          // Same '' rather than undefined for all four: clearing Chapter Ring
          // is how an admin corrects a ref the AI decided had a rehaut when the
          // dial runs flat to the case.
          chapterRing: chapterRing || '',
          minuteTrack: minuteTrack || '',
          dialScales: dialScales.length > 0 ? dialScales.join(', ') : '',
          dialFeatures: dialFeatures.length > 0 ? dialFeatures.join(', ') : '',
          logoType: logoType || '',
        },
        strap: {
          material: strapMaterial || undefined,
          color: strapColor || undefined,
          width: strapWidth ? parseFloat(strapWidth) : undefined,
          buckleType: strapBuckleType || undefined,
          isMicroAdjustable: strapIsMicroAdjustable || undefined,
        },
        // `null`, not `undefined`, when unticked — undefined is stripped from
        // the write, so `|| undefined` made this a one-way door: a curator
        // could turn it on and never off. Same reasoning as `moduleId` below.
        hasIntegratedBracelet: hasIntegratedBracelet ? true : null,
        // Linked module entity. Write null (not undefined) when cleared so an
        // unlink actually removes the link — undefined would be stripped, leaving
        // the stale id. The fan-out trigger keys off this to sync the cache.
        moduleId: moduleId.trim() ? moduleId.trim() : null,
        // Per-ref display colours live at the canonical nested electronics.*
        // path (never top-level WatchReference.displayColor/textColor). The
        // module/battery/… siblings here are the CF-synced cache round-tripped
        // from the editor state.
        electronics: removeUndefined({
          module: elecModule || undefined,
          battery: elecBattery || undefined,
          batteryLife: elecBatteryLife || undefined,
          chargeTime: elecChargeTime || undefined,
          display: elecDisplay || undefined,
          lcdType: elecLcdType || undefined,
          lightType: elecLightType || undefined,
          displayColor: displayColor || undefined,
          textColor: textColor || undefined,
          backlightColor: backlightColor || undefined,
        }),
        // Pocket-watch fields write to the canonical nested pocketWatch.* path
        // (never top-level). Only persisted for watch types that expose the
        // section; undefined otherwise so removeUndefined strips the key.
        pocketWatch: showPocketWatch
          ? removeUndefined({
              grade: pwGrade || undefined,
              caseStyle: pwCaseStyle || undefined,
              escapement: pwEscapement || undefined,
              jewelBearing: pwJewelBearing || undefined,
              movementSize: pwMovementSize || undefined,
              adjustments: pwAdjustments.length > 0 ? pwAdjustments : undefined,
              isKeyWound: pwIsKeyWound || undefined,
              isFusee: pwIsFusee || undefined,
            })
          : undefined,
        production: {
          startYear: productionStartYear
            ? parseInt(productionStartYear)
            : undefined,
          endYear: productionEndYear ? parseInt(productionEndYear) : undefined,
          releaseYear: productionReleaseYear
            ? parseInt(productionReleaseYear)
            : undefined,
          isLimited: Boolean(isLimited),
          limitedSize: limitedSize ? parseInt(limitedSize) : undefined,
        },
        // Write [] (not undefined) when cleared so the empty array overwrites the
        // stale one — undefined is stripped by removeUndefined, leaving the old
        // array on the doc.
        functions: functions.length > 0 ? functions : [],
        links: links.length > 0 ? links : [],
        productUrl: productUrl.trim() || undefined,
        searchText: computeSearchText(
          brand.name,
          refNumber,
          model,
          calibre,
          movementType,
          diameterVal,
          collectionName,
        ),
      };

      // Variant Aggregates (parent refs only). Persist the full override map so a
      // toggle OFF clears a prior override; write the array verbatim ONLY for
      // overridden fields — auto-managed fields are left untouched so the
      // propagation trigger keeps owning them.
      if (isVariantParent) {
        updates.aggregateOverrides = { ...aggOverrides };
        for (const { key } of AGGREGATE_FIELD_CONFIG) {
          if (aggOverrides[key]) {
            updates[key] = aggValues[key];
          }
        }
      }

      // Remove all undefined values recursively (Firestore doesn't accept undefined)
      const cleanedUpdates = removeUndefined(
        updates as Record<string, unknown>,
      );

      await updateWatchReference(brand.id, reference.id, cleanedUpdates);

      toast.success('Reference saved successfully');
      setHasChanges(false);
      // null moduleId means "unlinked" — normalise to undefined for the
      // in-memory WatchReference handed back to the parent (Firestore already
      // received the null write above).
      onSaved({
        ...reference,
        ...updates,
        dialAndHands: updates.dialAndHands,
        moduleId: updates.moduleId ?? undefined,
        // Same normalisation: Firestore took the null (which is what clears
        // the flag), but the in-memory WatchReference handed back to the
        // parent must stay `boolean | undefined`.
        hasIntegratedBracelet: updates.hasIntegratedBracelet ?? undefined,
        // Normalise null (cleared) back to undefined for the in-memory ref.
        calibreTierId: updates.calibreTierId ?? undefined,
        calibreId: updates.calibreId ?? undefined,
        calibreOverrides: updates.calibreOverrides ?? undefined,
      });
      // Refresh the effective-spec panel in place once the ref-write trigger
      // rebuilds `calibreCache` (fire-and-forget; only when a calibre is linked).
      if (updates.calibreId) void refetchCalibreCache(liveCalibreCache);
    } catch (error) {
      console.error('Error saving reference:', error);
      toast.error('Failed to save reference');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Upload returns the storage path (not URL)
      const storagePath = await uploadReferenceImage(
        brand.id,
        reference.id,
        file,
      );

      // Create new image info
      const newImage: ImageInfo = {
        storagePath,
        contentType: file.type,
        index: 0,
      };

      // Update the reference with the new image.
      await updateWatchReference(brand.id, reference.id, {
        images: [newImage],
      });

      // Update local state instead of reloading
      setImages([newImage]);

      // Fetch and display the new image URL
      const url = await getImageUrl(storagePath);
      setLoadedImageUrl(url);

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  /**
   * Set the main image from a pasted URL.
   *
   * The whole fetch-and-store chain already exists for the importer —
   * `attachReferenceImageFromUrl` runs direct fetch → Cloudflare worker →
   * `proxyImage` callable, which is what gets past a brand site's CORS — so
   * this is only the affordance in front of it. The bytes land in our own
   * Storage; we never hotlink.
   */
  const handleSetImageFromUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a full image URL starting with http:// or https://');
      return;
    }

    setFetchingImageUrl(true);
    try {
      const image = await attachReferenceImageFromUrl(brand.id, reference.id, url);
      setImages([image]);
      setLoadedImageUrl(await getImageUrl(image.storagePath));
      setImageUrlInput('');
      setShowImageUrlInput(false);
      toast.success('Main image set from URL');
    } catch (error) {
      console.error('Error setting image from URL:', error);
      // The helper's own messages name the actual problem (wrong content type,
      // too large); anything else is the 3-strategy chain having run out.
      toast.error(
        error instanceof ReferenceImageFetchError
          ? error.message
          : "Couldn't fetch that image — check the URL, or save it and upload the file",
      );
    } finally {
      setFetchingImageUrl(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!images.length) return;

    try {
      // Delete from Storage
      for (const img of images) {
        try {
          await deleteReferenceImage(img.storagePath);
        } catch {
          // File may already be deleted or not exist
        }
      }

      // Clear from Firestore
      await updateWatchReference(brand.id, reference.id, {
        images: [],
      });

      setImages([]);
      setLoadedImageUrl(null);
      toast.success('Image deleted');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleAddLink = () => {
    if (!newLinkUrl.trim()) return;

    const newLink: WatchLink = { url: newLinkUrl.trim() };
    if (newLinkTitle.trim()) {
      newLink.title = newLinkTitle.trim();
    }
    setLinks([...links, newLink]);
    setNewLinkUrl('');
    setNewLinkTitle('');
  };

  const handleRemoveLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleGalleryUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingGalleryImage(true);
    try {
      const storagePath = await uploadReferenceImage(
        brand.id,
        reference.id,
        file,
      );
      const newImage: ImageInfo = {
        storagePath,
        contentType: file.type,
        index: replacingGalleryIndex ?? galleryImages.length,
      };

      let updatedGallery: ImageInfo[];
      if (replacingGalleryIndex !== null) {
        updatedGallery = galleryImages.map((img, i) =>
          i === replacingGalleryIndex ? newImage : img,
        );
      } else {
        updatedGallery = [...galleryImages, newImage];
      }

      await updateWatchReference(brand.id, reference.id, {
        galleryImages: updatedGallery,
      });

      setGalleryImages(updatedGallery);
      toast.success(
        replacingGalleryIndex !== null
          ? 'Gallery image replaced'
          : 'Gallery image added',
      );
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      toast.error('Failed to upload gallery image');
    } finally {
      setUploadingGalleryImage(false);
      setReplacingGalleryIndex(null);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleRemoveGalleryImage = async (index: number) => {
    const updatedGallery = galleryImages.filter((_, i) => i !== index);
    try {
      await updateWatchReference(brand.id, reference.id, {
        galleryImages: updatedGallery,
      });
      setGalleryImages(updatedGallery);
      toast.success('Gallery image removed');
    } catch (error) {
      console.error('Error removing gallery image:', error);
      toast.error('Failed to remove gallery image');
    }
  };

  /**
   * Make a gallery image the main one, swapping the current main down into the
   * slot it vacated.
   *
   * Worth the confirm: tag toggles above persist on click, so the tags this
   * clears are already in Firestore and only a re-tag brings them back. Both
   * arrays go in ONE `updateWatchReference` — a half-applied swap would either
   * duplicate an image across both fields or lose one entirely.
   */
  const handlePromoteGalleryImage = async (index: number) => {
    const swap = promoteGalleryImageToMain(images, galleryImages, index);
    if (!swap) return;

    const droppedTags = galleryImages[index]?.tags ?? [];
    if (droppedTags.length > 0) {
      const ok = window.confirm(
        `Make this the main image?\n\nIts tags (${droppedTags.join(', ')}) will be cleared — a main image is the whole watch, not a detail shot. The current main image moves into this gallery slot.`,
      );
      if (!ok) return;
    }

    const previousImages = images;
    const previousGallery = galleryImages;
    setImages(swap.images);
    setGalleryImages(swap.galleryImages);
    try {
      await updateWatchReference(brand.id, reference.id, {
        images: swap.images,
        galleryImages: swap.galleryImages,
      });
      toast.success('Main image updated');
    } catch (error) {
      console.error('Error promoting gallery image:', error);
      toast.error('Failed to set main image');
      setImages(previousImages);
      setGalleryImages(previousGallery);
    }
  };

  /**
   * Copy a movement photo from this reference's gallery into the linked
   * calibre's.
   *
   * Offered only on an image tagged `movement`, and only when the ref is linked
   * — `canCopyImageToCalibre` owns both halves, from state already in hand.
   *
   * The calibre document is RE-READ here rather than trusted from
   * `activeLinkedCalibre`, which was fetched when this editor opened. The cap is
   * three, another screen may have filled the gallery since, and a stale count
   * would write a fourth image past a limit the calibre editor enforces with
   * nothing downstream to notice.
   *
   * Copies the BYTES to a fresh `calibres/{id}/images/...` path rather than
   * pointing the calibre at this reference's storage path. The calibre editor's
   * remove action calls `deleteCalibreImage(storagePath)` unconditionally, so a
   * shared path would mean deleting the image from the calibre gallery silently
   * destroyed the reference's own photo.
   *
   * The `movement` tag does NOT carry over: `CalibreImage` has no tags field,
   * and adding one is a shared-types change. A copied image is indistinguishable
   * from a hand-added one, which is the correct outcome for a gallery whose
   * every image is of the movement anyway.
   */
  const [copyingCalibreImageIndex, setCopyingCalibreImageIndex] = useState<number | null>(
    null,
  );
  const handleCopyGalleryImageToCalibre = async (index: number) => {
    const calibre = activeLinkedCalibre;
    const url = galleryImageUrls[index];
    if (!calibre || !url || copyingCalibreImageIndex !== null) return;

    setCopyingCalibreImageIndex(index);
    try {
      const fresh = await getCustomCalibreById(calibre.id);
      if (!fresh) {
        toast.error('That calibre no longer exists.');
        return;
      }
      const current = fresh.images ?? fresh.data?.images ?? [];
      if (calibreImageCopyOutcome(current) === 'at-max') {
        toast.error(CALIBRE_GALLERY_FULL_MESSAGE);
        return;
      }

      const blob = await fetchImageBlob(url);
      const fileName = url.split('/').pop()?.split('?')[0] || 'movement.jpg';
      const storagePath = await uploadCalibreImage(fresh.id, blob, fileName);
      await appendCalibreImage(fresh.id, current, {
        storagePath,
        source: resolveDefaultCalibreSource(fresh.brandId, fresh.manufacturerId),
      });
      toast.success(`Image copied to ${fresh.name}.`);
    } catch (error) {
      console.error('Failed to copy gallery image to calibre:', error);
      toast.error('Failed to copy the image to the calibre.');
    } finally {
      setCopyingCalibreImageIndex(null);
    }
  };

  const handleToggleGalleryTag = async (index: number, tag: string) => {
    const img = galleryImages[index];
    const currentTags = img.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];
    const updatedGallery = galleryImages.map((g, i) =>
      i === index ? { ...g, tags: newTags } : g,
    );
    setGalleryImages(updatedGallery);
    try {
      await updateWatchReference(brand.id, reference.id, {
        galleryImages: updatedGallery,
      });
    } catch (error) {
      console.error('Error updating gallery image tags:', error);
      toast.error('Failed to update tags');
      setGalleryImages(galleryImages);
    }
  };

  // Source URL is free text — update local state per keystroke, persist on blur.
  const handleGallerySourceUrlChange = (index: number, sourceUrl: string) => {
    setGalleryImages((prev) =>
      prev.map((g, i) => (i === index ? { ...g, sourceUrl } : g)),
    );
  };

  // Clearing the field DROPS the key rather than writing `sourceUrl: undefined`
  // — nested undefined reaches the SDK (updateWatchReference's stripUndefined
  // is shallow) and throws, so clearing a source URL used to fail every time.
  const handleGallerySourceUrlBlur = async (index: number) => {
    const updatedGallery = galleryImages.map((g, i) =>
      i === index ? imageForWrite(g, { index: i }) : g,
    );
    setGalleryImages(updatedGallery);
    try {
      await updateWatchReference(brand.id, reference.id, {
        galleryImages: updatedGallery,
      });
    } catch (error) {
      console.error('Error updating gallery image source URL:', error);
      toast.error('Failed to update source URL');
    }
  };

  const handleMainImageSourceUrlChange = (sourceUrl: string) => {
    setImages((prev) =>
      prev.map((img, i) => (i === 0 ? { ...img, sourceUrl } : img)),
    );
  };

  const handleMainImageSourceUrlBlur = async () => {
    if (images.length === 0) return;
    // Same reasoning as handleGallerySourceUrlBlur above.
    const updated = images.map((img, i) =>
      i === 0 ? imageForWrite(img, { index: 0 }) : img,
    );
    setImages(updated);
    try {
      await updateWatchReference(brand.id, reference.id, { images: updated });
    } catch (error) {
      console.error('Error updating image source URL:', error);
      toast.error('Failed to update source URL');
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <button
            onClick={() => requestExit(onBack)}
            className='text-gray-500 hover:text-gray-700'
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
          </button>
          <div>
            <h2 className='text-xl font-semibold text-gray-900'>
              {reference.reference}
            </h2>
            <p className='text-sm text-gray-500'>{brand.name}</p>
          </div>
          <CopyLinkButton
            url={referenceShareUrl(brand.id, reference.id)}
            noun='reference'
          />
        </div>
        <div className='flex gap-2'>
          {onClone && (
            <Button
              variant='secondary'
              onClick={handleClone}
              disabled={hasChanges}
              title={
                hasChanges
                  ? 'Save or discard changes before cloning'
                  : 'Clone this reference'
              }
            >
              Clone
            </Button>
          )}
          <Button
            variant='secondary'
            onClick={() => setShowAIPreDialog(true)}
            loading={aiLoading}
            title='Use Claude to suggest values for empty fields'
          >
            ✨ Suggest with AI
          </Button>
          <Button
            variant='secondary'
            onClick={() => setShowImportRules(true)}
          >
            Import Rules
          </Button>
          <Button
            variant='secondary'
            onClick={() => setShowRawData(true)}
          >
            View Raw
          </Button>
          <Button
            variant='danger'
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isProcessing}
          >
            Delete
          </Button>
          <Button
            onClick={handleSave}
            loading={isProcessing}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column - Image */}
        <div className='lg:col-span-1'>
          <div className='bg-white shadow rounded-lg p-4'>
            <h3 className='font-medium text-gray-900 mb-3'>Image</h3>

            <div className='aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3 relative'>
              {loadedImageUrl ? (
                <img
                  src={loadedImageUrl}
                  alt={reference.reference}
                  className='w-full h-full object-cover'
                />
              ) : (
                <div className='w-full h-full flex items-center justify-center text-gray-400'>
                  <svg
                    className='w-16 h-16'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={1}
                      d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                </div>
              )}
            </div>

            <input
              type='file'
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept='image/*'
              className='hidden'
            />
            <div className='flex gap-2'>
              <Button
                variant='secondary'
                className='flex-1'
                onClick={() => fileInputRef.current?.click()}
                loading={uploadingImage}
              >
                {loadedImageUrl ? 'Replace Image' : 'Upload Image'}
              </Button>
              {loadedImageUrl && (
                <Button
                  variant='danger'
                  size='sm'
                  onClick={handleDeleteImage}
                  title='Delete image'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                    />
                  </svg>
                </Button>
              )}
            </div>

            {/* Pull the main image off a brand page. Separate from the Source
                URL field below, which records provenance without fetching. */}
            {showImageUrlInput ? (
              <div className='mt-2'>
                <input
                  type='url'
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSetImageFromUrl();
                    if (e.key === 'Escape') setShowImageUrlInput(false);
                  }}
                  placeholder='https://brand.com/.../image.jpg'
                  autoFocus
                  disabled={fetchingImageUrl}
                  className='w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
                <div className='flex gap-2 mt-2'>
                  <Button
                    size='sm'
                    className='flex-1'
                    onClick={handleSetImageFromUrl}
                    loading={fetchingImageUrl}
                    disabled={!imageUrlInput.trim()}
                  >
                    Fetch & Set
                  </Button>
                  <Button
                    variant='secondary'
                    size='sm'
                    onClick={() => {
                      setShowImageUrlInput(false);
                      setImageUrlInput('');
                    }}
                    disabled={fetchingImageUrl}
                  >
                    Cancel
                  </Button>
                </div>
                <p className='text-[10px] text-gray-400 mt-1'>
                  Downloads the image into our Storage and replaces the main
                  image. The URL is kept as the source.
                </p>
              </div>
            ) : (
              <Button
                variant='secondary'
                size='sm'
                className='w-full mt-2'
                onClick={() => setShowImageUrlInput(true)}
              >
                Set from URL
              </Button>
            )}

            {images.length > 0 && (
              <div className='mt-3'>
                <label className='block text-xs font-medium text-gray-600 mb-1'>
                  Source URL
                </label>
                <input
                  type='url'
                  value={images[0]?.sourceUrl ?? ''}
                  onChange={(e) => handleMainImageSourceUrlChange(e.target.value)}
                  onBlur={handleMainImageSourceUrlBlur}
                  placeholder='https://...'
                  className='w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                />
              </div>
            )}
          </div>

          {/* Gallery Images */}
          <div className='bg-white shadow rounded-lg p-4 mt-4'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='font-medium text-gray-900'>
                Gallery Images
                <span className='ml-1 text-xs font-normal text-gray-400'>
                  ({galleryImages.length}/5)
                </span>
              </h3>
            </div>

            {galleryImages.length > 0 && (
              <div className='grid grid-cols-2 gap-2 mb-3'>
                {galleryImages.map((img, i) => (
                  <div
                    key={i}
                    className='relative group'
                  >
                    <div className='aspect-square bg-gray-100 rounded overflow-hidden border border-gray-200'>
                      {galleryImageUrls[i] ? (
                        <img
                          src={galleryImageUrls[i]!}
                          alt={`Gallery ${i + 1}`}
                          className='w-full h-full object-cover'
                        />
                      ) : (
                        <div className='w-full h-full flex items-center justify-center text-xs text-gray-400'>
                          Loading...
                        </div>
                      )}
                    </div>
                    <div className='absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                      <button
                        onClick={() => handlePromoteGalleryImage(i)}
                        className='bg-white rounded-full p-1 shadow text-gray-600 hover:text-brand-600'
                        title='Set as main image'
                      >
                        <svg
                          className='w-3.5 h-3.5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 4h16M12 20V9m0 0l-4 4m4-4l4 4'
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          setReplacingGalleryIndex(i);
                          galleryInputRef.current?.click();
                        }}
                        className='bg-white rounded-full p-1 shadow text-gray-600 hover:text-brand-600'
                        title='Replace'
                      >
                        <svg
                          className='w-3.5 h-3.5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                          />
                        </svg>
                      </button>
                      {canCopyImageToCalibre({
                        calibreId,
                        calibreResolved: !!activeLinkedCalibre,
                        tags: img.tags,
                      }) && (
                        <button
                          onClick={() => handleCopyGalleryImageToCalibre(i)}
                          disabled={copyingCalibreImageIndex !== null}
                          className='bg-white rounded-full p-1 shadow text-gray-600 hover:text-brand-600 disabled:text-gray-300'
                          title={`Copy this movement photo to ${
                            activeLinkedCalibre?.name ?? 'the linked calibre'
                          }'s gallery`}
                          aria-label={`Copy this movement photo to ${
                            activeLinkedCalibre?.name ?? 'the linked calibre'
                          }'s gallery`}
                        >
                          {copyingCalibreImageIndex === i ? (
                            <span className='block w-3.5 h-3.5 text-[10px] leading-[14px]'>…</span>
                          ) : (
                            /* Two stacked plates — the "copy" idiom, distinct at
                               14px from the promote arrow and the replace loop. */
                            <svg
                              className='w-3.5 h-3.5'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                              />
                            </svg>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveGalleryImage(i)}
                        className='bg-white rounded-full p-1 shadow text-gray-600 hover:text-red-600'
                        title='Remove'
                      >
                        <svg
                          className='w-3.5 h-3.5'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </button>
                    </div>
                    <div className='flex flex-wrap gap-0.5 mt-1'>
                      {GALLERY_IMAGE_TAGS.map((tag) => {
                        const active = img.tags?.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => handleToggleGalleryTag(i, tag)}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${
                              active
                                ? 'bg-brand-100 border-brand-400 text-brand-700'
                                : 'bg-gray-50 border-gray-200 text-gray-400 hover:border-gray-300'
                            }`}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                    <input
                      type='url'
                      value={img.sourceUrl ?? ''}
                      onChange={(e) =>
                        handleGallerySourceUrlChange(i, e.target.value)
                      }
                      onBlur={() => handleGallerySourceUrlBlur(i)}
                      placeholder='Source URL'
                      title='Source URL'
                      className='w-full border border-gray-300 rounded px-1.5 py-0.5 text-[10px] mt-1 focus:ring-1 focus:ring-brand-500 focus:border-brand-500'
                    />
                  </div>
                ))}
              </div>
            )}

            <input
              type='file'
              ref={galleryInputRef}
              onChange={handleGalleryUpload}
              accept='image/*'
              className='hidden'
            />
            {galleryImages.length < 5 && (
              <Button
                variant='secondary'
                size='sm'
                className='w-full'
                onClick={() => {
                  setReplacingGalleryIndex(null);
                  galleryInputRef.current?.click();
                }}
                loading={uploadingGalleryImage}
              >
                Add Gallery Image
              </Button>
            )}
          </div>

          {/* Links Section */}
          <div className='bg-white shadow rounded-lg p-4 mt-4'>
            <h3 className='font-medium text-gray-900 mb-3'>Links</h3>

            {/* Dedicated product-page URL — drives the RN "View product page" link */}
            <div className='mb-4'>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Product page URL
              </label>
              <input
                type='url'
                value={productUrl}
                onChange={(e) => setProductUrl(e.target.value)}
                placeholder='https://brand.com/.../reference'
                className='w-full border border-gray-300 rounded px-2 py-1 text-sm'
              />
              <p className='text-xs text-gray-500 mt-1'>
                Shown as “View product page” in the app. Leave blank to hide the link.
              </p>
            </div>

            {links.length > 0 && (
              <div className='space-y-2 mb-3'>
                {links.map((link, index) => (
                  <div
                    key={index}
                    className='flex items-center gap-2 p-2 bg-gray-50 rounded'
                  >
                    <div className='flex-1 min-w-0'>
                      <a
                        href={link.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-sm text-brand-600 hover:underline truncate block'
                      >
                        {link.title || link.url}
                      </a>
                    </div>
                    <button
                      onClick={() => handleRemoveLink(index)}
                      className='text-red-500 hover:text-red-700'
                    >
                      <svg
                        className='w-4 h-4'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                      >
                        <path
                          fillRule='evenodd'
                          d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className='space-y-2'>
              <input
                type='url'
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                placeholder='URL'
                className='w-full border border-gray-300 rounded px-2 py-1 text-sm'
              />
              <input
                type='text'
                value={newLinkTitle}
                onChange={(e) => setNewLinkTitle(e.target.value)}
                placeholder='Title (optional)'
                className='w-full border border-gray-300 rounded px-2 py-1 text-sm'
              />
              <Button
                variant='secondary'
                size='sm'
                className='w-full'
                onClick={handleAddLink}
                disabled={!newLinkUrl.trim()}
              >
                Add Link
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Form Fields */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Basic Info */}
          <div className='bg-white shadow rounded-lg p-4'>
            <h3 className='font-medium text-gray-900 mb-4'>
              Basic Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Reference <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  value={refNumber}
                  onChange={(e) => setRefNumber(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Model
                </label>
                <input
                  type='text'
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Collection
                </label>
                <input
                  type='text'
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Product Name
                </label>
                <input
                  type='text'
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    // A person wrote this — the one source a regenerated
                    // description is never pre-ticked over.
                    setDescriptionSource('manual');
                  }}
                  rows={3}
                  placeholder='Watch description...'
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Nickname
                </label>
                <input
                  type='text'
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <div className='md:col-span-2 flex items-center gap-6'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={discontinued}
                    onChange={(e) => setDiscontinued(e.target.checked)}
                    className='rounded text-brand-600 focus:ring-brand-500'
                  />
                  <span className='text-sm font-medium text-gray-700'>
                    Discontinued
                  </span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={specialEdition}
                    onChange={(e) => setSpecialEdition(e.target.checked)}
                    className='rounded text-brand-600 focus:ring-brand-500'
                  />
                  <span className='text-sm font-medium text-gray-700'>
                    Special Edition
                  </span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={isLimited}
                    onChange={(e) => {
                      setIsLimited(e.target.checked);
                      if (!e.target.checked) setLimitedSize('');
                    }}
                    className='rounded text-brand-600 focus:ring-brand-500'
                  />
                  <span className='text-sm font-medium text-gray-700'>
                    Limited Edition
                  </span>
                </label>
                {isLimited && (
                  <div className='flex items-center gap-2'>
                    <input
                      type='number'
                      value={limitedSize}
                      onChange={(e) => setLimitedSize(e.target.value)}
                      placeholder='Qty'
                      className='w-24 border border-gray-300 rounded-lg px-3 py-2'
                    />
                    <span className='text-xs text-gray-500'>pieces</span>
                  </div>
                )}
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Series
                </label>
                <input
                  type='text'
                  value={series}
                  onChange={(e) => setSeries(e.target.value)}
                  className='w-full border border-gray-300 rounded-lg px-3 py-2'
                />
              </div>
              <AdaptiveLookupField
                label='Watch Type'
                singleValue={watchType}
                lookupCollection='lookup_watch_types'
                onSingleChange={setWatchType}
              />
              <AdaptiveLookupField
                label='Occasion'
                multiValues={occasion}
                lookupCollection='lookup_watch_occasion'
                onMultiChange={setOccasion}
              />
              {/* Bezel Type used to sit here, alone, while the bezel MATERIAL
                  was a free-text box down in Dial & Hands. Both now live in the
                  Bezel subsection at the foot of the Case section, with the new
                  Bezel Colors — see the comment there. */}
              {/* Crown Type — multi-select over ONE vocabulary that mixes
                  attachment (screw_down / push_pull), sealing system (twinlock
                  / triplock) and shape (onion / cabochon). A Rolex crown is
                  genuinely two of those at once; as a single-value field it
                  swallowed the whole phrase as one custom value. */}
              <AdaptiveLookupField
                label='Crown Type'
                multiValues={crownType}
                lookupCollection='lookup_crown_types'
                onMultiChange={setCrownType}
              />
            </div>
          </div>

          {/* Variants — parents only (≥1 ref points here). Sits directly under
              basic identity, above the deep spec sections: landing on a Shopify
              parent, managing its variants is often the whole job. Collapsed by
              default; the child docs load on first expand. */}
          {!aggregatesLoading && isVariantParent && (
            <VariantsPanel
              brand={brand}
              parent={reference}
              variantCount={variantCount}
              onOpenReference={
                onOpenReference
                  ? (v) => requestExit(() => onOpenReference(v))
                  : undefined
              }
            />
          )}

          {/* Movement */}
          {showMovement && (
            <div className='bg-white shadow rounded-lg'>
              <button
                type='button'
                onClick={() => toggleSection('movement')}
                className='w-full p-4 flex items-center justify-between text-left'
              >
                <div className='flex items-center gap-2'>
                  <h3 className='font-medium text-gray-900'>Movement</h3>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setShowSlugMappings(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShowSlugMappings(true); } }}
                    className='text-gray-400 hover:text-gray-600 transition-colors'
                    title='Manage calibre lookup slug mappings'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                    </svg>
                  </span>
                  <span
                    role='button'
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setShowCustomCalibres(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShowCustomCalibres(true); } }}
                    className='text-gray-400 hover:text-gray-600 transition-colors'
                    title='Manage custom calibres'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' />
                    </svg>
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.movement ? 'rotate-180' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
              {expandedSections.movement && (
                <div className='px-4 pb-4'>
                  {/* Linked: the calibre owns the movement spec and its read-only
                      summary (tier picker, effective spec, cache, overrides, legacy
                      list) is a single tall column — a 2-col grid here leaves the
                      right half of row 1 empty. Render full-width when linked; keep
                      the 2-col editable grid only for the unlinked spec. */}
                  <div className={calibreId ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                    <div>
                      <div className='flex items-center justify-between mb-1'>
                        <label className='block text-sm font-medium text-gray-700'>
                          {calibreId ? 'Linked calibre' : 'Calibre'}
                        </label>
                        <div className='flex items-center gap-1.5'>
                          <button
                            type='button'
                            onClick={() => setShowCalibrePicker(true)}
                            className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-700 hover:bg-brand-200 transition-colors'
                            title='Link this reference to a calibre (sets calibreId)'
                          >
                            <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656m-1.414-1.414a4 4 0 00-5.656 5.656l3 3a4 4 0 005.656-5.656' />
                            </svg>
                            {calibreId ? 'Change link' : 'Link calibre'}
                          </button>
                          {/* Edit the linked calibre's own doc in a modal over the
                              ref editor (item 5). On save, `handleLinkedCalibreSaved`
                              refetches the calibre + the ref's rebuilt cache. */}
                          {calibreId && linkedCalibreById && (
                            <button
                              type='button'
                              onClick={() =>
                                setEditingLinkedCalibre(linkedCalibreById)
                              }
                              className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors'
                              title='Edit the linked calibre (opens in a modal)'
                            >
                              <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                              </svg>
                              Edit calibre
                            </button>
                          )}
                          {/* One-click link when the free-text string already
                              resolves to a DB calibre (replaces the old "Lookup
                              Calibre" hydrate-legacy action — linking supersedes
                              it). Only when unlinked and a name/AKA match exists. */}
                          {!calibreId && linkedCalibre && (
                            <button
                              type='button'
                              onClick={() => handlePickCalibre(linkedCalibre)}
                              className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors'
                              title={`Link this reference to "${linkedCalibre.name}" (matched in DB)`}
                            >
                              <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656m-1.414-1.414a4 4 0 00-5.656 5.656l3 3a4 4 0 005.656-5.656' />
                              </svg>
                              Link this calibre
                            </button>
                          )}
                          {/* "In DB" is redundant once linked — the "Linked: X"
                              pill below already conveys DB membership. */}
                          {!calibreId && calibreExistsInDb && (
                            <Badge variant='success'>In DB</Badge>
                          )}
                          <CalibreActionToolbar
                            calibreName={calibre}
                            brandName={brand.name}
                            brandId={brand.id}
                            showPushToDb={!calibreId && !calibreExistsInDb}
                            showCreateNew={false}
                            // AI Identify is only useful when unlinked (identity
                            // unknown). When linked, offer a one-click "Save as
                            // AKA" of the linked calibre instead of a picker.
                            showAIIdentify={!calibreId}
                            directAkaTarget={
                              calibreId && linkedCalibreById
                                ? linkedCalibreById
                                : undefined
                            }
                            onPushToDb={handlePushCalibre}
                            onCalibreCreated={handleCalibreToolbarSuccess}
                            onAliasAdded={handleCalibreToolbarSuccess}
                            compact={true}
                            disabled={pushCalibreLoading}
                          />
                        </div>
                      </div>
                      {/* Unlinked: the legacy free-text calibre string IS the only
                          calibre indicator, so it stays at the top, editable.
                          When linked, the calibre owns the identity (primary panel
                          below) and this string drops to the legacy-values box. */}
                      {!calibreId && (
                        <input
                          type='text'
                          value={calibre}
                          onChange={(e) => setCalibre(e.target.value)}
                          className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        />
                      )}
                      {/* Phase 3a: linked-calibre identity panel (primary). Shown
                          when a calibreId is set (the authoritative link) — the
                          calibre name leads, with the override count + Unlink. */}
                      {calibreId && (
                        <div className='flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2'>
                          <div className='min-w-0'>
                            <div className='font-mono text-base font-semibold text-brand-800 truncate'>
                              {linkedCalibreById?.name ?? calibre}
                            </div>
                            <div className='text-[11px] text-brand-500'>
                              <span className='text-brand-400'>{calibreId}</span>
                              {countCalibreOverrides(calibreOverrides) > 0 && (
                                <span className='ml-1'>
                                  · {countCalibreOverrides(calibreOverrides)} override
                                  {countCalibreOverrides(calibreOverrides) === 1 ? '' : 's'}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            type='button'
                            onClick={handleUnlinkCalibre}
                            className='ml-auto shrink-0 rounded border border-brand-300 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-100'
                          >
                            Unlink
                          </button>
                        </div>
                      )}
                      {calibreId && calibreRedirectFrom && (
                        <p className='mt-1 text-[11px] text-amber-600'>
                          Redirected from deprecated calibre{' '}
                          <span className='font-mono'>“{calibreRedirectFrom}”</span>{' '}
                          → now resolves to{' '}
                          <span className='font-mono'>
                            {linkedCalibreById?.name}
                          </span>
                          . Save to re-point this reference.
                        </p>
                      )}
                      {/* Tier picker + effective-spec preview. Only shown when
                          the resolved calibre (by id, else by name) has tiers[].
                          Picker options are brand-scoped: generic grades + this
                          brand's own modifications. A shared base movement
                          carries brand-specific tiers for every brand that mods
                          it, and offering the lot invites a curator to pick
                          another brand's modification off a loose textual cue.
                          Display lookups keep reading the full `tiers[]` so a
                          stored cross-brand pointer still renders its name in
                          the selected option, with its owning brand called out —
                          this is a scope boundary on NEW picks, not a reason to
                          erase an existing one. */}
                      {(() => {
                        const tiers = activeLinkedCalibre?.data?.tiers ?? [];
                        const selectedTier = calibreTierId
                          ? tiers.find((t) => t.id === calibreTierId)
                          : undefined;
                        // A foreign-brand pinned tier isn't in the scoped list;
                        // `tierPickerOptions` prepends it so the <select>'s
                        // value stays valid and the curator can see what the ref
                        // is pointing at. One tier either way, no duplicates.
                        const pickerOptions = tierPickerOptions(
                          tiers,
                          brand.id,
                          calibreTierId,
                        );
                        const selectedIsForeign =
                          !!selectedTier &&
                          !brandScopedTiers.some((t) => t.id === selectedTier.id);
                        const foreignBrandLabel =
                          selectedIsForeign && selectedTier
                            ? foreignTierBrand?.id === selectedTier.brandId?.trim()
                              ? foreignTierBrand?.brand?.name ?? selectedTier.brandId
                              : selectedTier.brandId
                            : undefined;
                        // Nothing to pick AND no stored pointer → hide the picker.
                        if (pickerOptions.length === 0 && !calibreTierId) return null;
                        // Ref points at a tier that no longer exists on the calibre.
                        const orphaned =
                          !!calibreTierId && !selectedTier;
                        const effective = applyTierOverrides(
                          activeLinkedCalibre?.data,
                          orphaned ? undefined : selectedTier,
                        );
                        // The three accuracy keys render as one combined row via
                        // formatAccuracy — drop them from the generic grid.
                        const effectiveNonAccuracy = effective.filter(
                          (f) => !isAccuracyKey(f.key),
                        );
                        const previewGates = specGates({
                          tierHasDate:
                            orphaned ? undefined : selectedTier?.overrides?.hasDate,
                          date: activeLinkedCalibre?.data?.date,
                        });
                        const acc = effectiveAccuracy(
                          activeLinkedCalibre?.data,
                          orphaned ? undefined : selectedTier,
                        );
                        // The calibre's own name for its base (untiered) spec,
                        // falling back to a generic "Base" when unnamed.
                        const baseTierName =
                          activeLinkedCalibre?.data?.baseTierName || 'Base';
                        return (
                          <div className='mt-2 space-y-2'>
                            <div>
                              <label className='block text-xs font-medium text-gray-600 mb-1'>
                                Calibre Tier
                              </label>
                              <select
                                value={orphaned ? '' : calibreTierId}
                                onChange={(e) => setCalibreTierId(e.target.value)}
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'
                              >
                                <option value=''>{baseTierName}</option>
                                {pickerOptions.map((t) => {
                                  const isForeign =
                                    selectedIsForeign &&
                                    selectedTier &&
                                    t.id === selectedTier.id;
                                  return (
                                    <option key={t.id} value={t.id}>
                                      {isForeign && foreignBrandLabel
                                        ? `${t.name} — ${foreignBrandLabel} mod (other brand)`
                                        : t.name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {orphaned && (
                              <p className='text-xs text-amber-600'>
                                This reference points at a tier that no longer
                                exists on the linked calibre. Showing the base
                                spec — pick a tier or leave as Base to clear.
                              </p>
                            )}
                            {/* Effective spec = base calibre overlaid with the
                                selected tier's overrides. Overridden fields are
                                badged. */}
                            <div className='border rounded-lg p-2 bg-gray-50'>
                              <div className='text-[11px] font-medium text-gray-500 uppercase mb-1.5'>
                                Effective spec
                                {selectedTier && !orphaned
                                  ? ` · ${selectedTier.name}`
                                  : ` · ${baseTierName}`}
                              </div>
                              <div className='grid grid-cols-2 gap-x-3 gap-y-1'>
                                {effectiveNonAccuracy.map((f) => (
                                  <div
                                    key={f.key}
                                    className='flex items-center gap-1.5 text-xs'
                                  >
                                    <span className='text-gray-500 truncate'>
                                      {f.label}
                                    </span>
                                    <span
                                      className={`ml-auto font-medium ${
                                        f.overridden
                                          ? 'text-brand-700'
                                          : 'text-gray-700'
                                      }`}
                                    >
                                      {/* Gated by the effective hasDate — the
                                          same rule the Effective Calibre Spec
                                          panel below hides the row by. */}
                                      {isGatedKey(f.key, previewGates)
                                        ? '—'
                                        : displayOverrideLookupValue(
                                            f.key,
                                            f.value,
                                            resolveLookupLabel,
                                          )}
                                    </span>
                                    {f.overridden && (
                                      <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                                        tier
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {/* Accuracy: the two bounds + symmetric flag
                                    collapsed into one formatted row. */}
                                <div className='flex items-center gap-1.5 text-xs'>
                                  <span className='text-gray-500 truncate'>
                                    Accuracy
                                  </span>
                                  <span
                                    className={`ml-auto font-medium ${
                                      acc.overridden
                                        ? 'text-brand-700'
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {acc.display}
                                  </span>
                                  {acc.overridden && (
                                    <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                                      tier
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Phase 3a: primary effective-spec panel + collapsible
                          per-ref overrides editor. Only when a calibreId is
                          linked. */}
                      {calibreId && (() => {
                        const tiers = activeLinkedCalibre?.data?.tiers ?? [];
                        const selectedTier = calibreTierId
                          ? tiers.find((t) => t.id === calibreTierId)
                          : undefined;
                        // Prefer the trigger-built cache; fall back to a snapshot
                        // synthesized straight from the linked calibre so the
                        // spec renders immediately on link (before the cache
                        // trigger fires). Guards on `activeLinkedCalibre?.data`
                        // inside the helper (returns undefined → panel renders
                        // nothing).
                        const effectiveCache =
                          liveCalibreCache ??
                          calibreDataToCacheSnapshot(
                            activeLinkedCalibre,
                            selectedTier,
                          );
                        const overrideCount =
                          countCalibreOverrides(calibreOverrides);
                        const gates = specGates({
                          tierHasDate: selectedTier?.overrides?.hasDate,
                          date: effectiveCache?.date,
                          isChronograph: effectiveCache?.isChronograph,
                          isGMT: effectiveCache?.isGMT,
                        });
                        const baseManufacturerId = effectiveCache?.manufacturerId;
                        const baseManufacturerName = baseManufacturerId
                          ? manufacturerNameById[baseManufacturerId] ?? baseManufacturerId
                          : undefined;
                        const maker = effectiveManufacturerLabel({
                          tierBrandId: selectedTier?.brandId,
                          refBrand: brand,
                          fetchedBrand:
                            foreignTierBrand?.id === selectedTier?.brandId?.trim()
                              ? foreignTierBrand?.brand
                              : undefined,
                          baseManufacturerName,
                        });
                        return (
                          <>
                            <RefCalibreCachePanel
                              cache={effectiveCache}
                              gates={gates}
                              manufacturerOverride={
                                maker.fromBrandTier && maker.label
                                  ? { label: maker.label, baseName: baseManufacturerName }
                                  : undefined
                              }
                              resolveLookupLabel={resolveLookupLabel}
                              manufacturerNameById={manufacturerNameById}
                              onEditCalibre={
                                linkedCalibreById
                                  ? () =>
                                      setEditingLinkedCalibre(linkedCalibreById)
                                  : undefined
                              }
                            />
                            {/* Read-only supplement: construction, decoration &
                                tiers straight from the full linked CalibreData
                                (fields the cache snapshot doesn't carry). */}
                            <CalibreSpecDetail
                              data={activeLinkedCalibre?.data}
                              resolveLookupLabel={resolveLookupLabel}
                            />
                            {/* Collapsible per-ref overrides. Auto-expanded when
                                an override is already active (overridesOpen
                                init). */}
                            <div className='mt-3'>
                              <button
                                type='button'
                                onClick={() => setOverridesOpen((o) => !o)}
                                className='flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left'
                              >
                                <span className='text-xs font-medium text-gray-500 uppercase'>
                                  Overrides
                                  {overrideCount > 0 && (
                                    <span className='ml-1.5 rounded bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[9px] font-semibold normal-case'>
                                      {overrideCount} active
                                    </span>
                                  )}
                                </span>
                                <svg
                                  className={`w-4 h-4 text-gray-500 transition-transform ${overridesOpen ? 'rotate-180' : ''}`}
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M19 9l-7 7-7-7'
                                  />
                                </svg>
                              </button>
                              {overridesOpen && (
                                <RefCalibreOverridesEditor
                                  cache={effectiveCache}
                                  overrides={calibreOverrides}
                                  onOverrideChange={setCalibreOverride}
                                  resolveLookupLabel={resolveLookupLabel}
                                />
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {/* Linked: the calibre owns the movement spec. Show the
                        Calibre Entry + overrides above as source of truth, and
                        render the ref's legacy movement.* values read-only (mirrors the
                        Module section's read-only legacy list). Display stays
                        editable — it's a watch-level field, not on the calibre. */}
                    {calibreId && (() => {
                      const isSet = (
                        v: boolean | number | string | string[] | undefined,
                      ): boolean => {
                        if (v === undefined || v === null || v === '') return false;
                        if (typeof v === 'boolean') return v === true;
                        if (Array.isArray(v)) return v.length > 0;
                        return true;
                      };
                      const roRows: {
                        label: string;
                        group: 'A' | 'B' | 'C';
                        ck: keyof CalibreData;
                        legacy: boolean | number | string | string[] | undefined;
                        ok?: OverrideKey;
                      }[] = [
                        { label: 'Movement Type', group: 'B', ck: 'movementType', legacy: movementType || undefined },
                        { label: 'Base Movement', group: 'B', ck: 'baseMovement', legacy: baseMovement || undefined },
                        { label: 'Frequency (VPH)', group: 'B', ck: 'frequencyVph', legacy: frequencyVph || undefined },
                        { label: 'Diameter (mm)', group: 'B', ck: 'diameterMm', legacy: movementDiameterMm || undefined },
                        { label: 'Height (mm)', group: 'B', ck: 'heightMm', legacy: movementHeightMm || undefined },
                        { label: 'Winding Direction', group: 'B', ck: 'windingDirection', legacy: windingDirection || undefined },
                        { label: 'Manual Winding', group: 'B', ck: 'manualWinding', legacy: manualWinding || undefined },
                        { label: 'GMT', group: 'B', ck: 'isGMT', legacy: isGMT || undefined },
                        { label: 'Chronograph', group: 'B', ck: 'isChronograph', legacy: isChronograph || undefined },
                        { label: 'In-House', group: 'B', ck: 'isInHouse', legacy: isInHouse || undefined },
                        { label: 'Column Wheel', group: 'B', ck: 'columnWheel', legacy: columnWheel || undefined },
                        { label: 'Double Barrel', group: 'B', ck: 'doubleBarrel', legacy: doubleBarrel || undefined },
                        { label: 'Battery Type', group: 'B', ck: 'batteryType', legacy: batteryType || undefined },
                        { label: 'Battery Life (y)', group: 'B', ck: 'batteryLifeYears', legacy: batteryLifeYears || undefined },
                        { label: 'Date', group: 'B', ck: 'date', legacy: movementDate.length ? movementDate : undefined },
                        { label: 'Hands', group: 'B', ck: 'hands', legacy: movementHands.length ? movementHands : undefined },
                        { label: 'Chronograph types', group: 'B', ck: 'chronograph', legacy: movementChronograph.length ? movementChronograph : undefined },
                        { label: 'Additionals', group: 'B', ck: 'additionals', legacy: movementAdditionals.length ? movementAdditionals : undefined },
                        { label: 'Astronomical', group: 'B', ck: 'astronomical', legacy: movementAstronomical.length ? movementAstronomical : undefined },
                        { label: 'Acoustic', group: 'B', ck: 'acoustic', legacy: movementAcoustic.length ? movementAcoustic : undefined },
                        { label: 'Jewels', group: 'A', ck: 'jewels', ok: 'jewels', legacy: jewels || undefined },
                        { label: 'Power Reserve (h)', group: 'A', ck: 'powerReserveHours', ok: 'powerReserveHours', legacy: powerReserveHours || undefined },
                        { label: 'Chronometer', group: 'A', ck: 'isChronometer', ok: 'isChronometer', legacy: isChronometer || undefined },
                        { label: 'COSC', group: 'A', ck: 'isCosc', ok: 'isCosc', legacy: isCosc || undefined },
                        { label: 'Glashütte Regulation', group: 'A', ck: 'isGlashuetteRegulation', ok: 'isGlashuetteRegulation', legacy: isGlashuetteRegulation || undefined },
                        { label: 'Geneva Seal', group: 'A', ck: 'isGenevaSeal', ok: 'isGenevaSeal', legacy: isGenevaSeal || undefined },
                        { label: 'Grand Seiko Standard', group: 'A', ck: 'isGrandSeikoStandard', ok: 'isGrandSeikoStandard', legacy: isGrandSeikoStandard || undefined },
                        { label: 'METAS', group: 'A', ck: 'isMETAS', ok: 'isMETAS', legacy: isMETAS || undefined },
                        { label: 'Hacking', group: 'A', ck: 'hacking', ok: 'hacking', legacy: hacking || undefined },
                        { label: 'Quick Set Date', group: 'A', ck: 'quickSetDate', ok: 'quickSetDate', legacy: quickSetDate || undefined },
                        { label: 'Regulator System', group: 'A', ck: 'regulatorSystem', ok: 'regulatorSystem', legacy: regulatorSystem || undefined },
                        { label: 'Hairspring', group: 'A', ck: 'hairspring', ok: 'hairspring', legacy: hairspring || undefined },
                        { label: 'Escapement', group: 'A', ck: 'escapement', ok: 'escapement', legacy: escapement || undefined },
                        { label: 'Anti-Shock System', group: 'A', ck: 'antiShockSystem', ok: 'antiShockSystem', legacy: antiShockSystem || undefined },
                        { label: 'Rotor Type', group: 'A', ck: 'rotorType', ok: 'rotorType', legacy: rotorType || undefined },
                        { label: 'Lift Angle (°)', group: 'C', ck: 'liftAngleDegrees', legacy: liftAngleDegrees || undefined },
                        { label: 'Lignes', group: 'C', ck: 'lignes', legacy: lignes || undefined },
                        { label: 'Casing Diameter (mm)', group: 'C', ck: 'casingDiameterMm', legacy: casingDiameterMm || undefined },
                        { label: 'Service Interval (y)', group: 'C', ck: 'serviceIntervalYears', legacy: serviceIntervalYears || undefined },
                        { label: 'Country of Manufacture', group: 'C', ck: 'countryOfManufacture', legacy: countryOfManufacture || undefined },
                        { label: 'Manual Winding Direction', group: 'C', ck: 'manualWindingDirection', legacy: manualWindingDirection || undefined },
                      ];
                      const shown = roRows.filter(
                        (r) => isSet(r.legacy) || isSet(calibreValueFor(r.ck)),
                      );
                      // Does any shown row diverge from the linked calibre (a
                      // Group-A → override migration, or a B/C conflict)? Drives
                      // the default-open state of the collapsible.
                      const anyDivergence = shown.some((r) => {
                        const calVal = calibreValueFor(r.ck);
                        const diverges = !valuesMatch(r.ck, r.legacy, calVal);
                        const overridden =
                          r.group === 'A' &&
                          r.ok !== undefined &&
                          calibreOverrides[r.ok] !== undefined;
                        if (overridden) return false;
                        const aDiverges =
                          r.group === 'A' && isSet(r.legacy) && diverges;
                        const bcConflict =
                          r.group !== 'A' &&
                          isSet(r.legacy) &&
                          isSet(calVal) &&
                          diverges;
                        return aDiverges || bcConflict;
                      });
                      // Untouched (null) → default to open when there's a
                      // divergence to surface; otherwise honor the admin's toggle.
                      const legacyExpanded = legacyMovementOpen ?? anyDivergence;
                      return (
                        <div className='space-y-3'>
                          <div className='border rounded-lg p-3 bg-gray-50'>
                            <button
                              type='button'
                              onClick={() =>
                                setLegacyMovementOpen(!legacyExpanded)
                              }
                              className='flex w-full items-center justify-between text-left'
                            >
                              <span className='text-[11px] font-medium text-gray-500 uppercase'>
                                Legacy movement values (read-only)
                                {anyDivergence && (
                                  <span className='ml-1.5 rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[9px] font-semibold normal-case'>
                                    diverges
                                  </span>
                                )}
                              </span>
                              <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${legacyExpanded ? 'rotate-180' : ''}`}
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M19 9l-7 7-7-7'
                                />
                              </svg>
                            </button>
                            {legacyExpanded && (
                            <>
                            <p className='text-[11px] text-gray-400 mt-2 mb-2'>
                              The linked Calibre Entry + overrides above are the
                              source of truth. These stored{' '}
                              <span className='font-mono'>movement.*</span> values
                              are read-only — edit specs on the calibre, or use a
                              per-ref override for a one-off difference.
                            </p>
                            {/* The original free-text calibre string this ref was
                                imported/scraped with. Kept as read-only legacy
                                context now that the calibre link owns identity. */}
                            {calibre.trim() && (
                              <div className='mb-2 flex items-baseline gap-1.5 border-b border-gray-200 pb-2 text-xs'>
                                <span className='text-gray-400'>
                                  Legacy calibre string:
                                </span>
                                <span className='font-mono text-gray-500'>
                                  {calibre}
                                </span>
                              </div>
                            )}
                            {shown.length === 0 ? (
                              <p className='text-xs text-gray-400 italic'>
                                No legacy movement values recorded.
                              </p>
                            ) : (
                              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1'>
                                {shown.map((r) => {
                                  const calVal = calibreValueFor(r.ck);
                                  // Display resolves lookup ids → labels; the
                                  // divergence test compares on the normalized id
                                  // level, so "Manual" (legacy label) and "manual"
                                  // (calibre id) don't read as a conflict.
                                  const legacyDisp = displayField(r.ck, r.legacy);
                                  const calDisp = displayField(r.ck, calVal);
                                  const diverges = !valuesMatch(r.ck, r.legacy, calVal);
                                  const overridden =
                                    r.group === 'A' &&
                                    r.ok !== undefined &&
                                    calibreOverrides[r.ok] !== undefined;
                                  // Group A migrates on ANY divergence (even vs an
                                  // empty calibre value). B/C can't be overridden,
                                  // so only flag a genuine conflict (both sides set).
                                  const aDiverges =
                                    r.group === 'A' &&
                                    isSet(r.legacy) &&
                                    diverges;
                                  const bcConflict =
                                    r.group !== 'A' &&
                                    isSet(r.legacy) &&
                                    isSet(calVal) &&
                                    diverges;
                                  return (
                                    <div
                                      key={r.label}
                                      className='flex items-center gap-1.5 text-xs'
                                    >
                                      <span className='text-gray-500 truncate'>
                                        {r.label}
                                      </span>
                                      <span className='ml-auto font-medium text-gray-700 text-right'>
                                        {legacyDisp}
                                      </span>
                                      {overridden ? (
                                        <span className='shrink-0 rounded bg-brand-100 text-brand-700 px-1 py-0.5 text-[9px] font-semibold uppercase'>
                                          override
                                        </span>
                                      ) : aDiverges ? (
                                        <span
                                          className='shrink-0 rounded bg-amber-100 text-amber-700 px-1 py-0.5 text-[9px] font-semibold uppercase'
                                          title={`Linked calibre: ${calDisp} — saved as a per-ref override on next save`}
                                        >
                                          → override
                                        </span>
                                      ) : bcConflict ? (
                                        <span
                                          className='shrink-0 rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[9px] font-medium'
                                          title={`Linked calibre: ${calDisp}`}
                                        >
                                          differs — fix on calibre
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Unlinked: the full editable movement spec grid. */}
                    {!calibreId && (
                      <>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Base Movement
                      </label>
                      <input
                        type='text'
                        value={baseMovement}
                        onChange={(e) => setBaseMovement(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      />
                    </div>
                    <AdaptiveLookupField
                      label='Movement Type'
                      singleValue={movementType}
                      lookupCollection='lookup_movement_types'
                      onSingleChange={setMovementType}
                    />
                    {movementTypeNeedsBattery && (
                      <>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-1'>Battery Type</label>
                          <input
                            type='text'
                            value={batteryType}
                            onChange={(e) => setBatteryType(e.target.value)}
                            className='w-full border border-gray-300 rounded-lg px-3 py-2'
                            placeholder='e.g., SR920SW, CR2025'
                          />
                        </div>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-1'>Battery Life (years)</label>
                          <input
                            type='number'
                            step='0.5'
                            min='0'
                            max='20'
                            value={batteryLifeYears}
                            onChange={(e) => setBatteryLifeYears(e.target.value)}
                            className='w-full border border-gray-300 rounded-lg px-3 py-2'
                            placeholder='e.g., 2, 3.5, 10'
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Frequency (VPH)
                      </label>
                      <input
                        type='number'
                        value={frequencyVph}
                        onChange={(e) => setFrequencyVph(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 28800'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Jewels
                      </label>
                      <input
                        type='number'
                        value={jewels}
                        onChange={(e) => setJewels(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 25'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Power Reserve (hours)
                      </label>
                      <input
                        type='number'
                        value={powerReserveHours}
                        onChange={(e) => setPowerReserveHours(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 70'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Diameter (mm)
                      </label>
                      <input
                        type='number'
                        step='0.1'
                        value={movementDiameterMm}
                        onChange={(e) => setMovementDiameterMm(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Height (mm)
                      </label>
                      <input
                        type='number'
                        step='0.1'
                        value={movementHeightMm}
                        onChange={(e) => setMovementHeightMm(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isChronometer}
                            onChange={(e) => setIsChronometer(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Chronometer
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isCosc}
                            onChange={(e) => setIsCosc(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            COSC Certified
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isGlashuetteRegulation}
                            onChange={(e) => setIsGlashuetteRegulation(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Glashütte Regulation
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isGenevaSeal}
                            onChange={(e) => setIsGenevaSeal(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Geneva Seal
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isGrandSeikoStandard}
                            onChange={(e) => setIsGrandSeikoStandard(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Grand Seiko Standard
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isMETAS}
                            onChange={(e) => setIsMETAS(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            METAS Certified
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={hacking}
                            onChange={(e) => setHacking(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>Hacking</span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={manualWinding}
                            onChange={(e) => setManualWinding(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Manual Winding
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={quickSetDate}
                            onChange={(e) => setQuickSetDate(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Quick Set Date
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isChronograph}
                            onChange={(e) => setIsChronograph(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>
                            Chronograph
                          </span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isGMT}
                            onChange={(e) => setIsGMT(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>GMT</span>
                        </label>
                      </div>
                    </div>
                    <AdaptiveLookupField
                      label='Winding Direction'
                      singleValue={windingDirection}
                      lookupCollection='lookup_winding_directions'
                      onSingleChange={setWindingDirection}
                    />
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Lift Angle (degrees)
                      </label>
                      <input
                        type='number'
                        value={liftAngleDegrees}
                        onChange={(e) => setLiftAngleDegrees(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 52'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Lignes
                      </label>
                      <input
                        type='text'
                        value={lignes}
                        onChange={(e) => setLignes(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 11.5"&apos;'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Casing Diameter (mm)
                      </label>
                      <input
                        type='number'
                        step='0.01'
                        value={casingDiameterMm}
                        onChange={(e) => setCasingDiameterMm(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 25.60'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Service Interval (years)
                      </label>
                      <input
                        type='number'
                        value={serviceIntervalYears}
                        onChange={(e) => setServiceIntervalYears(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., 5'
                      />
                    </div>
                    <AdaptiveLookupField
                      label='Country of Manufacture'
                      singleValue={countryOfManufacture}
                      lookupCollection='lookup_movement_origin'
                      onSingleChange={setCountryOfManufacture}
                    />
                    <AdaptiveLookupField
                      label='Anti-Shock System'
                      singleValue={antiShockSystem}
                      lookupCollection='lookup_shock_protection'
                      onSingleChange={setAntiShockSystem}
                    />
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Rotor Type
                      </label>
                      <input
                        type='text'
                        value={rotorType}
                        onChange={(e) => setRotorType(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='Ball-bearing, jewelled bearing, tungsten rotor…'
                      />
                    </div>
                    <AdaptiveLookupField
                      label='Regulator System'
                      singleValue={regulatorSystem}
                      lookupCollection='lookup_regulation'
                      onSingleChange={setRegulatorSystem}
                    />
                    <AdaptiveLookupField
                      label='Manual Winding Direction'
                      singleValue={manualWindingDirection}
                      lookupCollection='lookup_winding_directions'
                      onSingleChange={setManualWindingDirection}
                    />
                    <AdaptiveLookupField
                      label='Hairspring'
                      singleValue={hairspring}
                      lookupCollection='lookup_balance_spring'
                      onSingleChange={setHairspring}
                    />
                    <AdaptiveLookupField
                      label='Escapement'
                      singleValue={escapement}
                      lookupCollection='lookup_escapement_type'
                      onSingleChange={setEscapement}
                    />
                    <div className='md:col-span-2'>
                      <div className='flex flex-wrap gap-4'>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={isInHouse}
                            onChange={(e) => setIsInHouse(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>In-House</span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={columnWheel}
                            onChange={(e) => setColumnWheel(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>Column Wheel</span>
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={doubleBarrel}
                            onChange={(e) => setDoubleBarrel(e.target.checked)}
                            className='rounded text-brand-600 focus:ring-brand-500'
                          />
                          <span className='text-sm text-gray-700'>Double Barrel</span>
                        </label>
                      </div>
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Date'
                        values={movementDate}
                        onChange={setMovementDate}
                        placeholder='Add date type...'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Hands'
                        values={movementHands}
                        onChange={setMovementHands}
                        placeholder='Add hand type...'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Chronograph'
                        values={movementChronograph}
                        onChange={setMovementChronograph}
                        placeholder='Add chronograph type...'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Additionals'
                        values={movementAdditionals}
                        onChange={setMovementAdditionals}
                        placeholder='Add additional feature...'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Astronomical'
                        values={movementAstronomical}
                        onChange={setMovementAstronomical}
                        placeholder='Add astronomical complication...'
                      />
                    </div>
                    <div className='md:col-span-2'>
                      <TagInput
                        label='Acoustic'
                        values={movementAcoustic}
                        onChange={setMovementAcoustic}
                        placeholder='Add acoustic feature...'
                      />
                    </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Module (Electronics) - for Digital and Hybrid watches */}
          {showModule && (
            <div className='bg-white shadow rounded-lg'>
              <button
                type='button'
                onClick={() => toggleSection('module')}
                className='w-full p-4 flex items-center justify-between text-left'
              >
                <h3 className='font-medium text-gray-900'>Module</h3>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.module ? 'rotate-180' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
              {expandedSections.module && (
                <div className='px-4 pb-4 space-y-4'>
                  {/* ── Current state ─────────────────────────────────────── */}
                  <ElectronicModulePicker
                    value={moduleId}
                    initialLabel={elecModule}
                    onChange={(id) => setModuleId(id)}
                    defaultManufacturerId={defaultModuleManufacturerId}
                    createNameSeed={elecModule || undefined}
                    aiContext={buildModuleAiContext}
                    onRefresh={handleRefreshModule}
                    refreshing={refreshingModule}
                  />
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <AdaptiveLookupField
                      label='Display Color'
                      singleValue={displayColor}
                      lookupCollection='lookup_display_colors'
                      onSingleChange={setDisplayColor}
                    />
                    <AdaptiveLookupField
                      label='Text Color'
                      singleValue={textColor}
                      lookupCollection='lookup_text_colors'
                      onSingleChange={setTextColor}
                    />
                    {/* Shares lookup_text_colors with Text Color: both are
                        colours of light on the display. */}
                    <AdaptiveLookupField
                      label='Backlight Color'
                      singleValue={backlightColor}
                      lookupCollection='lookup_text_colors'
                      onSingleChange={setBacklightColor}
                    />
                  </div>

                  {/* ── Module details (read-only, from linked module) ────── */}
                  {/* Prefer the freshly-fetched module doc so it renders on link
                      immediately; fall back to the ref's cache while in-flight. */}
                  {moduleId.trim() && (
                    <ModuleDetailsSummary
                      electronics={
                        linkedModule
                          ? moduleToElectronicsInfo(linkedModule)
                          : reference.electronics
                      }
                    />
                  )}

                  {/* ── Legacy electronics (read-only cache) ──────────────── */}
                  {(() => {
                    const legacyRows = [
                      { label: 'Module', value: elecModule },
                      { label: 'Battery', value: elecBattery },
                      { label: 'Battery Life', value: elecBatteryLife },
                      { label: 'Charge Time', value: elecChargeTime },
                      { label: 'Display', value: elecDisplay },
                      { label: 'LCD Type', value: elecLcdType },
                      { label: 'Light Type', value: elecLightType },
                    ].filter((r) => r.value.trim().length > 0);
                    if (legacyRows.length === 0) return null;
                    const rows = (
                      <div className='space-y-1.5'>
                          {legacyRows.map((row) => {
                            const match = legacyModuleMatches.get(
                              row.value.trim(),
                            );
                            const linkedToMatch =
                              !!match && moduleId.trim() === match.id;
                            return (
                              <div
                                key={row.label}
                                className='flex items-center gap-2 text-sm py-1'
                              >
                                <span className='w-28 shrink-0 text-gray-500'>
                                  {row.label}
                                </span>
                                <span className='flex-1 break-words text-gray-800'>
                                  {row.value}
                                </span>
                                {/* AKA has semantic meaning only for the module
                                    identifier itself (e.g. push "5416" as an AKA
                                    of the linked module) — spec rows like Battery
                                    or Display carry no alias/match affordances. */}
                                {row.label === 'Module' && (
                                  <div className='flex shrink-0 items-center gap-1.5'>
                                    {match ? (
                                      linkedToMatch ? (
                                        <span className='text-xs text-emerald-600'>
                                          ✓ matches linked module
                                        </span>
                                      ) : !moduleId.trim() ? (
                                        <button
                                          type='button'
                                          onClick={() =>
                                            handleLinkLegacyModule(match)
                                          }
                                          className='rounded border border-brand-300 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50'
                                        >
                                          Link
                                        </button>
                                      ) : (
                                        <span className='text-xs text-gray-400'>
                                          module “{match.name}” exists
                                        </span>
                                      )
                                    ) : (
                                      <>
                                        <ElectronicModuleAiLookup
                                          buildInput={() =>
                                            buildModuleAiContext(row.value)
                                          }
                                          onLink={handleLinkModuleFromAi}
                                          defaultManufacturerId={
                                            defaultModuleManufacturerId
                                          }
                                          variant='mini'
                                        />
                                        <button
                                          type='button'
                                          disabled={
                                            !moduleId.trim() ||
                                            legacyAkaBusy === row.value
                                          }
                                          onClick={() =>
                                            handleAddLegacyAka(row.value.trim())
                                          }
                                          title={
                                            moduleId.trim()
                                              ? 'Add this value as an alias on the linked module'
                                              : 'Link a module first'
                                          }
                                          className='rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40'
                                        >
                                          {legacyAkaBusy === row.value
                                            ? 'Adding…'
                                            : 'Add as AKA'}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    );
                    const helper = (
                      <p className='mt-2 text-xs text-gray-400'>
                        These values are the auto-synced cache from the linked
                        module (or pre-migration inline values). Edit them on the
                        module entity, not here.
                      </p>
                    );
                    // Once a module is linked the legacy cache is redundant noise
                    // — collapse it (collapsed by default). Unlinked, it's the
                    // "here's what you have to work with" view, so keep it open.
                    if (moduleId.trim()) {
                      return (
                        <details className='border-t border-gray-100 pt-3'>
                          <summary className='cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                            Legacy electronics{' '}
                            <span className='font-normal normal-case text-gray-400'>
                              ({legacyRows.length} field
                              {legacyRows.length === 1 ? '' : 's'} — click to show)
                            </span>
                          </summary>
                          <div className='mt-2'>
                            {rows}
                            {helper}
                          </div>
                        </details>
                      );
                    }
                    return (
                      <div className='border-t border-gray-100 pt-3'>
                        <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                          Legacy electronics{' '}
                          <span className='font-normal normal-case text-gray-400'>
                            (read-only cache)
                          </span>
                        </h4>
                        {rows}
                        {helper}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Pocket Watch — only for watch types whose lookup carries
              `showPocketWatch` (e.g. pocket_watch). Writes to pocketWatch.*. */}
          {showPocketWatch && (
            <div className='bg-white shadow rounded-lg'>
              <button
                type='button'
                onClick={() => toggleSection('pocketWatch')}
                className='w-full p-4 flex items-center justify-between text-left'
              >
                <h3 className='font-medium text-gray-900'>Pocket Watch</h3>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.pocketWatch ? 'rotate-180' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
              {expandedSections.pocketWatch && (
                <div className='px-4 pb-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Grade
                      </label>
                      <input
                        type='text'
                        value={pwGrade}
                        onChange={(e) => setPwGrade(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2'
                        placeholder='e.g., Vanguard, 992B'
                      />
                    </div>
                    <AdaptiveLookupField
                      label='Case Style'
                      singleValue={pwCaseStyle}
                      lookupCollection='lookup_pocket_watch_case_style'
                      onSingleChange={setPwCaseStyle}
                    />
                    <AdaptiveLookupField
                      label='Escapement'
                      singleValue={pwEscapement}
                      lookupCollection='lookup_pocket_watch_escapement'
                      onSingleChange={setPwEscapement}
                    />
                    <AdaptiveLookupField
                      label='Jewel Bearing'
                      singleValue={pwJewelBearing}
                      lookupCollection='lookup_pocket_watch_jewel_bearing'
                      onSingleChange={setPwJewelBearing}
                    />
                    <AdaptiveLookupField
                      label='Movement Size'
                      singleValue={pwMovementSize}
                      lookupCollection='lookup_pocket_watch_movement_size'
                      onSingleChange={setPwMovementSize}
                    />
                    <AdaptiveLookupField
                      label='Adjustments'
                      multiValues={pwAdjustments}
                      lookupCollection='lookup_pocket_watch_adjustments'
                      onMultiChange={setPwAdjustments}
                    />
                    <div className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        id='pwIsKeyWound'
                        checked={pwIsKeyWound}
                        onChange={(e) => setPwIsKeyWound(e.target.checked)}
                        className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                      />
                      <label
                        htmlFor='pwIsKeyWound'
                        className='text-sm font-medium text-gray-700'
                      >
                        Key-wound
                      </label>
                    </div>
                    <div className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        id='pwIsFusee'
                        checked={pwIsFusee}
                        onChange={(e) => setPwIsFusee(e.target.checked)}
                        className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                      />
                      <label
                        htmlFor='pwIsFusee'
                        className='text-sm font-medium text-gray-700'
                      >
                        Fusée
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Case */}
          <div className='bg-white shadow rounded-lg'>
            <button
              type='button'
              onClick={() => toggleSection('case')}
              className='w-full p-4 flex items-center justify-between text-left'
            >
              <h3 className='font-medium text-gray-900'>Case</h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.case ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {expandedSections.case && (
              <div className='px-4 pb-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Diameter (mm)
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      value={caseDiameter}
                      onChange={(e) => setCaseDiameter(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Thickness / Height (mm)
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      value={caseThickness}
                      onChange={(e) => setCaseThickness(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Lug to Lug (mm)
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      value={caseLugToLug}
                      onChange={(e) => setCaseLugToLug(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Lug Width (mm)
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      value={caseLugWidth}
                      onChange={(e) => setCaseLugWidth(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  <AdaptiveLookupField
                    label='Material'
                    singleValue={caseMaterial}
                    lookupCollection='lookup_case_materials'
                    onSingleChange={setCaseMaterial}
                  />
                  <AdaptiveLookupField
                    label='Water Resistance'
                    singleValue={caseWaterResistance}
                    lookupCollection='lookup_water_resistance'
                    onSingleChange={setCaseWaterResistance}
                  />
                  <AdaptiveLookupField
                    label='Shape'
                    singleValue={caseShape}
                    lookupCollection='lookup_case_shapes'
                    onSingleChange={setCaseShape}
                  />
                  <AdaptiveLookupField
                    label='Case Back'
                    singleValue={caseCaseBack}
                    lookupCollection='lookup_caseback_type'
                    onSingleChange={setCaseCaseBack}
                  />
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Case Code
                    </label>
                    <input
                      type='text'
                      value={caseCode}
                      onChange={(e) => setCaseCode(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  {/* caseCoating is single-select today; Phase 3's Q2 multi-flip
                      will promote it to string[] alongside the other five
                      case/dial fields, at which point flipping `isMultiSelect`
                      on the Firestore doc + swapping useState<string> →
                      useState<string[]> + swapping singleValue/onSingleChange →
                      multiValues/onMultiChange here is the entire editor-side
                      change (schema + backfill live elsewhere). That swap is now
                      the shape of every lookup on this screen. */}
                  <AdaptiveLookupField
                    label='Coating'
                    lookupCollection='lookup_case_coatings'
                    singleValue={caseCoating}
                    onSingleChange={setCaseCoating}
                  />
                  <AdaptiveLookupField
                    label='Finish'
                    singleValue={caseFinish}
                    lookupCollection='lookup_case_finishes'
                    onSingleChange={setCaseFinish}
                  />
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Weight (g)
                    </label>
                    <input
                      type='number'
                      step='0.1'
                      value={caseWeightG}
                      onChange={(e) => setCaseWeightG(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                </div>
                {/* Bezel — three fields that were scattered across the form:
                    Bezel Type sat alone in Basic Information, and the bezel
                    MATERIAL was an unlabelled free-text "Bezel" box in Dial &
                    Hands. They belong together, and they belong under Case:
                    the bezel is part of the case assembly, even though it is
                    the dial you see through it.

                    Type and Material are single-select — a bezel has one
                    function and one primary material (lookup_bezel_material
                    carries a `mixed` slug for two-tone rings). Colors is
                    MULTI-select because a two-colour insert is genuinely two
                    values: a "Pepsi" is red AND blue. */}
                <div className='border-t border-gray-100 pt-3 mt-4'>
                  <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                    Bezel{' '}
                    <span className='font-normal normal-case text-gray-400'>
                      (leave empty if the reference has no bezel)
                    </span>
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <AdaptiveLookupField
                      label='Bezel Type'
                      singleValue={bezelType}
                      lookupCollection='lookup_bezel_types'
                      onSingleChange={setBezelType}
                    />
                    {/* Persists to `bezel`, not `bezelMaterial` — the field
                        predates the lookup and the RN app, the brand portal and
                        the dedup differ all read that key. */}
                    <AdaptiveLookupField
                      label='Bezel Material'
                      singleValue={bezel}
                      lookupCollection='lookup_bezel_material'
                      onSingleChange={setBezel}
                    />
                    <AdaptiveLookupField
                      label='Case Colors'
                      multiValues={caseColors}
                      lookupCollection='lookup_case_colors'
                      onMultiChange={setCaseColors}
                    />
                    <AdaptiveLookupField
                      label='Bezel Colors'
                      multiValues={bezelColors}
                      lookupCollection='lookup_bezel_colors'
                      onMultiChange={setBezelColors}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dial & Hands — only for watch types with a visible dial */}
          {showDial && (
          <div className='bg-white shadow rounded-lg'>
            <button
              type='button'
              onClick={() => toggleSection('dialAndHands')}
              className='w-full p-4 flex items-center justify-between text-left'
            >
              <h3 className='font-medium text-gray-900'>Dial & Hands</h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.dialAndHands ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {expandedSections.dialAndHands && (
              <div className='px-4 pb-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <AdaptiveLookupField
                    label='Dial Color'
                    multiValues={dialColors}
                    lookupCollection='lookup_dial_colors'
                    onMultiChange={handleDialChange}
                  />
                  <AdaptiveLookupField
                    label='Dial Style'
                    singleValue={dialStyle}
                    lookupCollection='lookup_dial_styles'
                    onSingleChange={setDialStyle}
                  />
                  <AdaptiveLookupField
                    label='Hands Color'
                    multiValues={handsColors}
                    lookupCollection='lookup_hand_colors'
                    onMultiChange={setHandsColors}
                  />
                  <AdaptiveLookupField
                    label='Material'
                    singleValue={dialMaterial}
                    lookupCollection='lookup_dial_materials'
                    onSingleChange={setDialMaterial}
                  />
                  <AdaptiveLookupField
                    label='Finish'
                    singleValue={dialFinish}
                    lookupCollection='lookup_dial_finish'
                    onSingleChange={setDialFinish}
                  />
                  {/* MULTI, and the sibling of Finish: what has been DONE to the
                      dial rather than how its surface is treated. Whole-dial
                      materials stay in Material; an inlay of the same stone is a
                      feature here. */}
                  <AdaptiveLookupField
                    label='Dial Features'
                    multiValues={dialFeatures}
                    lookupCollection='lookup_dial_features'
                    onMultiChange={setDialFeatures}
                  />
                  <AdaptiveLookupField
                    label='Hands'
                    singleValue={dialHands}
                    lookupCollection='lookup_dial_hands'
                    onSingleChange={setDialHands}
                  />
                  {/* Marker TYPE — how the markers are constructed. Stored
                      under the historical `dialAndHands.indexes` key (the RN
                      app reads it), but backed by lookup_dial_marker_type: it
                      shared lookup_dial_numerals with the Numerals field below
                      until this split, which is how numeral slugs ended up in
                      the field. */}
                  <AdaptiveLookupField
                    label='Marker Type'
                    multiValues={dialIndexes}
                    lookupCollection='lookup_dial_marker_type'
                    onMultiChange={setDialIndexes}
                  />
                  <AdaptiveLookupField
                    label='Index Color'
                    multiValues={indexColors}
                    lookupCollection='lookup_index_colors'
                    onMultiChange={setIndexColors}
                  />
                  <AdaptiveLookupField
                    label='Hand Type'
                    multiValues={handTypes}
                    lookupCollection='lookup_hand_types'
                    onMultiChange={setHandTypes}
                  />
                  {/* Marker STYLE — what the markers are. Keeps
                      lookup_dial_numerals; Marker Type above moved off it. */}
                  <AdaptiveLookupField
                    label='Numerals'
                    multiValues={dialNumerals}
                    lookupCollection='lookup_dial_numerals'
                    onMultiChange={setDialNumerals}
                  />
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Subdials
                    </label>
                    <input
                      type='number'
                      value={dialSubdials}
                      onChange={(e) => setDialSubdials(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      placeholder='e.g., 3'
                      min={0}
                      max={6}
                    />
                  </div>
                  {/* WHAT each register does, sat next to the count it
                      complements. Three subdials is where a chronograph, a
                      triple calendar and a regulator all land, so the number
                      alone never identifies the dial. Multi-select by
                      definition — a three-register chronograph is three
                      separate functions — and stored comma-joined at
                      dialAndHands.subdialTypes, the convention Hand Type and
                      Index Color above already use. */}
                  <AdaptiveLookupField
                    label='Subdial Types'
                    multiValues={subdialTypes}
                    lookupCollection='lookup_dial_subdials'
                    onMultiChange={setSubdialTypes}
                  />
                  <div className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      id='luminova'
                      checked={dialLuminova}
                      onChange={(e) => setDialLuminova(e.target.checked)}
                      className='h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                    />
                    <label
                      htmlFor='luminova'
                      className='text-sm font-medium text-gray-700'
                    >
                      Luminova
                    </label>
                  </div>
                  {/* Crystal — multi-select over ONE vocabulary holding the
                      material AND its features (cyclops lens, domed, flat, AR
                      coating). A Datejust crystal is ["sapphire_crystal",
                      "cyclops_lens"]; the material/feature split is deferred
                      rather than designed in. */}
                  <AdaptiveLookupField
                    label='Crystal'
                    multiValues={crystal}
                    lookupCollection='lookup_crystal_materials'
                    onMultiChange={setCrystal}
                  />
                  {/* The free-text "Bezel" box that sat here was the bezel
                      MATERIAL — misfiled under Dial & Hands, unlabelled as a
                      material, and unbacked by lookup_bezel_material despite
                      the AI already answering it against that list. It is now
                      a picker in the Bezel subsection of the Case section. */}
                </div>
                {/* Chapter Ring — the dial's furniture: the ring around its
                    edge, what is printed on that ring, the measurement scales
                    the watch carries and how the brand mark is applied. Its own
                    subsection because it is a coherent group of fine detail,
                    NOT because it is gated: unlike Lume and Date below, none of
                    these four gates another, so there is no "leave empty if…"
                    hint on the header. A dial with no chapter ring still prints
                    a minute track on its face, and every dial has some logo
                    treatment.

                    Dial Scales sits here despite covering BEZEL scales too — a
                    Daytona's tachymeter is engraved on the bezel and still
                    belongs in this picker. One field for both surfaces, because
                    nobody asking whether a chronograph has a tachymeter cares
                    which surface carries it. */}
                <div className='border-t border-gray-100 pt-3 mt-4'>
                  <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                    Chapter Ring &amp; Dial Detail
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* WHERE the ring is… */}
                    <AdaptiveLookupField
                      label='Chapter Ring'
                      singleValue={chapterRing}
                      lookupCollection='lookup_chapter_ring_type'
                      onSingleChange={setChapterRing}
                    />
                    {/* …and WHAT is printed on it. Independent of the above:
                        the track sits on the ring where there is one and on the
                        dial face where there is not. */}
                    <AdaptiveLookupField
                      label='Minute Track'
                      singleValue={minuteTrack}
                      lookupCollection='lookup_minute_track_type'
                      onSingleChange={setMinuteTrack}
                    />
                    {/* MULTI — a doctor's chronograph prints two or three
                        concentric scales on one dial. Stored comma-joined at
                        dialAndHands.dialScales, the convention Subdial Types
                        above already uses. */}
                    <AdaptiveLookupField
                      label='Dial Scales'
                      multiValues={dialScales}
                      lookupCollection='lookup_dial_scales'
                      onMultiChange={setDialScales}
                    />
                    <AdaptiveLookupField
                      label='Logo Type'
                      singleValue={logoType}
                      lookupCollection='lookup_dial_logo_type'
                      onSingleChange={setLogoType}
                    />
                  </div>
                </div>
                {/* Lume — four single-select lookups stored flat under
                    dialAndHands, grouped for the same reason the Date block
                    below is: they only apply to a watch that HAS lume, and
                    Presence gates the other three (a `none` answer means there
                    is no material to describe).

                    The Luminova checkbox above is NOT replaced by this. It is
                    the boolean the RN app reads and the one every legacy import
                    set, so it stays where it is; these four are the detail
                    behind it. Day Color and Glow Color are genuinely different
                    answers — the faux-patina cream every homage advertises
                    glows plain green — which is why they are two fields. */}
                <div className='border-t border-gray-100 pt-3 mt-4'>
                  <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                    Lume{' '}
                    <span className='font-normal normal-case text-gray-400'>
                      (leave empty if the reference has no luminous material)
                    </span>
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <AdaptiveLookupField
                      label='Lume Presence'
                      singleValue={lumePresence}
                      lookupCollection='lookup_lume_presence'
                      onSingleChange={setLumePresence}
                    />
                    <AdaptiveLookupField
                      label='Lume Material'
                      singleValue={lumeMaterial}
                      lookupCollection='lookup_lume_material'
                      onSingleChange={setLumeMaterial}
                    />
                    <AdaptiveLookupField
                      label='Lume Day Color'
                      singleValue={lumeDayColor}
                      lookupCollection='lookup_lume_day_color'
                      onSingleChange={setLumeDayColor}
                    />
                    <AdaptiveLookupField
                      label='Lume Glow Color'
                      singleValue={lumeGlowColor}
                      lookupCollection='lookup_lume_glow_color'
                      onSingleChange={setLumeGlowColor}
                    />
                  </div>
                </div>
                {/* Date — four single-select lookups stored flat under
                    dialAndHands. Grouped in their own subsection because they
                    only apply to a watch that HAS a date, and because the last
                    three only apply to an APERTURE date: a pointer-date or
                    subsidiary-dial reference fills in Date Display alone. */}
                <div className='border-t border-gray-100 pt-3 mt-4'>
                  <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
                    Date{' '}
                    <span className='font-normal normal-case text-gray-400'>
                      (leave empty if the reference has no date)
                    </span>
                  </h4>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <AdaptiveLookupField
                      label='Date Display'
                      singleValue={dateDisplay}
                      lookupCollection='lookup_date_display'
                      onSingleChange={setDateDisplay}
                    />
                    <AdaptiveLookupField
                      label='Date Window Position'
                      singleValue={dateWindowPosition}
                      lookupCollection='lookup_date_window_position'
                      onSingleChange={setDateWindowPosition}
                    />
                    <AdaptiveLookupField
                      label='Date Window Frame'
                      singleValue={dateWindowFrame}
                      lookupCollection='lookup_date_window_frame'
                      onSingleChange={setDateWindowFrame}
                    />
                    {/* The colour of the DISC behind the aperture, not the
                        numerals printed on it. */}
                    <AdaptiveLookupField
                      label='Date Wheel Color'
                      singleValue={dateWheelColor}
                      lookupCollection='lookup_date_wheel_color'
                      onSingleChange={setDateWheelColor}
                    />
                    {/* …and the colour(s) of the numerals printed ON it. MULTI,
                        because a day-date routinely prints the weekend
                        positions in red and the weekdays in black on one wheel.

                        Bound to lookup_text_colors, the list the electronic
                        module's Text Color field already uses — a printed date
                        numeral comes in the same handful of colours a digital
                        readout does. That collection doc is flagged
                        isMultiSelect: false for that single-select field's
                        sake, so AdaptiveLookupField logs a dev-only drift
                        warning here and honours the multi binding (which is
                        exactly what its "the BINDING wins" rule is for).
                        Flipping the shared flag would break the other field's
                        prompt header instead, so it stays as it is. */}
                    <AdaptiveLookupField
                      label='Date Wheel Text Color'
                      multiValues={dateWheelTextColors}
                      lookupCollection='lookup_text_colors'
                      onMultiChange={setDateWheelTextColors}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Strap - hidden for watch types with the `hideStrap` property
              (e.g. stopwatch, pocket_watch). Hiding the UI only; existing
              strap values on the ref are preserved. */}
          {showStrap && (
          <div className='bg-white shadow rounded-lg'>
            <button
              type='button'
              onClick={() => toggleSection('strap')}
              className='w-full p-4 flex items-center justify-between text-left'
            >
              <h3 className='font-medium text-gray-900'>Strap / Bracelet</h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.strap ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {expandedSections.strap && (
              <div className='px-4 pb-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <AdaptiveLookupField
                    label='Material'
                    singleValue={strapMaterial}
                    lookupCollection='lookup_strap_materials'
                    onSingleChange={setStrapMaterial}
                  />
                  <AdaptiveLookupField
                    label='Color'
                    singleValue={strapColor}
                    lookupCollection='lookup_strap_color'
                    onSingleChange={setStrapColor}
                  />
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Width (mm)
                    </label>
                    <input
                      type='number'
                      step='0.5'
                      value={strapWidth}
                      onChange={(e) => setStrapWidth(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    />
                  </div>
                  <AdaptiveLookupField
                    label='Buckle/Clasp Type'
                    singleValue={strapBuckleType}
                    lookupCollection='lookup_clasp_type'
                    onSingleChange={setStrapBuckleType}
                  />
                  <div className='flex items-center'>
                    <label className='flex items-center gap-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={strapIsMicroAdjustable}
                        onChange={(e) =>
                          setStrapIsMicroAdjustable(e.target.checked)
                        }
                        className='rounded text-brand-600 focus:ring-brand-500'
                      />
                      <span className='text-sm text-gray-700'>
                        Micro Adjustable
                      </span>
                    </label>
                  </div>
                  <div className='flex items-center'>
                    <label className='flex items-center gap-2 cursor-pointer'>
                      <input
                        type='checkbox'
                        checked={hasIntegratedBracelet}
                        onChange={(e) =>
                          setHasIntegratedBracelet(e.target.checked)
                        }
                        className='rounded text-brand-600 focus:ring-brand-500'
                      />
                      <span className='text-sm text-gray-700'>
                        Integrated Bracelet{' '}
                        <span className='text-gray-400'>
                          — fused to the case (Royal Oak, Nautilus, PRX). Not a
                          lug-mounted bracelet, which can be swapped.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Production */}
          <div className='bg-white shadow rounded-lg'>
            <button
              type='button'
              onClick={() => toggleSection('production')}
              className='w-full p-4 flex items-center justify-between text-left'
            >
              <h3 className='font-medium text-gray-900'>Production</h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.production ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {expandedSections.production && (
              <div className='px-4 pb-4'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Release Year
                    </label>
                    <input
                      type='number'
                      value={productionReleaseYear}
                      onChange={(e) => setProductionReleaseYear(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      placeholder='e.g., 2020'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Start Year
                    </label>
                    <input
                      type='number'
                      value={productionStartYear}
                      onChange={(e) => setProductionStartYear(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      placeholder='e.g., 2020'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      End Year
                    </label>
                    <input
                      type='number'
                      value={productionEndYear}
                      onChange={(e) => setProductionEndYear(e.target.value)}
                      className='w-full border border-gray-300 rounded-lg px-3 py-2'
                      placeholder='e.g., 2023'
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Functions */}
          <div className='bg-white shadow rounded-lg'>
            <button
              type='button'
              onClick={() => toggleSection('functions')}
              className='w-full p-4 flex items-center justify-between text-left'
            >
              <h3 className='font-medium text-gray-900'>
                Functions / Complications
              </h3>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.functions ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 9l-7 7-7-7'
                />
              </svg>
            </button>
            {expandedSections.functions && (
              <div className='px-4 pb-4'>
                <AdaptiveLookupField
                  label='Functions / Complications'
                  multiValues={functions}
                  lookupCollection='lookup_watch_functions'
                  onMultiChange={setFunctions}
                />
              </div>
            )}
          </div>

          {/* Variant Aggregates — parents only (≥1 variant points here). Each of
              the six aggregated arrays can be hand-overridden; an overridden
              field is owned by the editor and the variantAggregates propagation
              trigger leaves it alone (aggregateOverrides[field] === true). */}
          {!aggregatesLoading && isVariantParent && (
            <div className='bg-white shadow rounded-lg'>
              <button
                type='button'
                onClick={() => toggleSection('variantAggregates')}
                className='w-full p-4 flex items-center justify-between text-left'
              >
                <h3 className='font-medium text-gray-900'>Variant Aggregates</h3>
                <svg
                  className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.variantAggregates ? 'rotate-180' : ''}`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
              {expandedSections.variantAggregates && (
                <div className='px-4 pb-4 space-y-4'>
                  <p className='text-sm text-gray-500'>
                    Values spread across this model&apos;s variants, surfaced to
                    consumer search/filter. Auto-managed from the variants by
                    default. Turn on <strong>Manual override</strong> to pin a
                    field — the propagation trigger then leaves it untouched.
                    Turning it off hands the field back to auto-management on the
                    next variant write (or use Recompute now).
                  </p>
                  {AGGREGATE_FIELD_CONFIG.map(({ key, label, lookup }) => {
                    const overridden = aggOverrides[key];
                    return (
                      <div
                        key={key}
                        className='border border-gray-200 rounded-md p-3'
                      >
                        <div className='flex items-center justify-between mb-2'>
                          <span className='text-sm font-medium text-gray-700'>
                            {label}
                          </span>
                          <label className='flex items-center gap-2 cursor-pointer'>
                            <input
                              type='checkbox'
                              checked={overridden}
                              onChange={(e) =>
                                handleAggOverrideToggle(key, e.target.checked)
                              }
                              className='rounded text-brand-600 focus:ring-brand-500'
                            />
                            <span className='text-xs font-medium text-gray-600'>
                              Manual override
                            </span>
                          </label>
                        </div>
                        {lookup ? (
                          <AdaptiveLookupField
                            label=''
                            multiValues={aggValues[key]}
                            lookupCollection={lookup}
                            onMultiChange={(vals) => handleAggValueChange(key, vals)}
                            disabled={!overridden}
                            allowCustom={overridden}
                          />
                        ) : (
                          <TagInput
                            label=''
                            values={aggValues[key]}
                            onChange={(vals) => handleAggValueChange(key, vals)}
                            disabled={!overridden}
                            placeholder='Add calibre…'
                          />
                        )}
                        {!overridden && (
                          <p className='mt-1 text-xs text-gray-400'>
                            Auto-managed — read-only.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  <div className='flex items-center gap-3 pt-1'>
                    <Button
                      variant='secondary'
                      onClick={handleRecomputeAggregates}
                      disabled={recomputingAggregates}
                    >
                      {recomputingAggregates
                        ? 'Recomputing…'
                        : 'Recompute now'}
                    </Button>
                    <span className='text-xs text-gray-400'>
                      Re-derives all non-overridden aggregates for{' '}
                      {brand.name} from current variants. Save edits first.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Raw Data Modal */}
      <Modal
        isOpen={showRawData}
        onClose={() => setShowRawData(false)}
        title='Raw Firebase Data'
        size='xl'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-500'>
            This shows the raw data stored in Firebase for this reference.
            Fields not shown in the editor may need to be added.
          </p>
          <pre className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-[60vh] text-sm'>
            {JSON.stringify(reference, null, 2)}
          </pre>
          <div className='flex justify-end'>
            <Button
              variant='secondary'
              onClick={() => setShowRawData(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title='Delete Reference'
        size='sm'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-700'>
            Are you sure you want to delete{' '}
            <strong>{reference.reference}</strong>
            {reference.model ? ` (${reference.model})` : ''}? This cannot be
            undone.
          </p>
          <div className='flex justify-end gap-2'>
            <Button
              variant='secondary'
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={handleDelete}
              loading={isProcessing}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      <CalibreSlugMappingsModal
        isOpen={showSlugMappings}
        onClose={() => setShowSlugMappings(false)}
      />
      <CalibrePickerModal
        isOpen={showCalibrePicker}
        brandName={brand.name}
        brandId={brand.id}
        currentCalibreId={calibreId || undefined}
        currentCalibreString={calibre || undefined}
        onSelect={(entry) => void handlePickCalibreChecked(entry)}
        onClose={() => setShowCalibrePicker(false)}
      />
      <Modal
        isOpen={!!pendingRevisionPick}
        onClose={() => setPendingRevisionPick(null)}
        title={
          pendingRevisionPick?.warning.kind === 'placeholder' ? 'Revision unspecified' : 'Superseded revision'
        }
        size="md"
      >
        {pendingRevisionPick && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {pendingRevisionPick.warning.kind === 'superseded' ? (
                <>
                  <span className="font-medium">{pendingRevisionPick.entry.name}</span> is a superseded revision —
                  the current is <span className="font-medium">{pendingRevisionPick.warning.currentName}</span>.
                  Continue?
                </>
              ) : pendingRevisionPick.warning.kind === 'placeholder' ? (
                <>
                  <span className="font-medium">{pendingRevisionPick.entry.name}</span> stands for an unspecified
                  revision
                  {pendingRevisionPick.warning.currentName
                    ? `; the current is ${pendingRevisionPick.warning.currentName}`
                    : ''}
                  . Link it only if this reference&rsquo;s revision is unknown. Continue?
                </>
              ) : (
                <>
                  <span className="font-medium">{pendingRevisionPick.entry.name}</span> is not marked as the current
                  revision of its line, and no revision is. Continue?
                </>
              )}
            </p>
            <p className="text-xs text-gray-500">
              No warning is shown when this reference&rsquo;s release or start year falls inside the revision&rsquo;s
              active years.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingRevisionPick(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handlePickCalibre(pendingRevisionPick.entry);
                  setPendingRevisionPick(null);
                }}
              >
                Link {pendingRevisionPick.entry.name}
              </Button>
            </div>
          </div>
        )}
      </Modal>
      <CustomCalibreModal
        isOpen={showCustomCalibres}
        onClose={() => setShowCustomCalibres(false)}
        currentBrandName={brand.name}
        defaultBrandId={brand.id}
      />
      {/* Item 5: edit the linked calibre in a modal over the ref editor. Opens
          directly into the edit form (initialEditEntry ⇒ directEditMode) and
          stacks above via Modal's own z-index/Escape layering. On save the modal
          fires onSaved then auto-closes; we refetch the calibre + rebuilt cache. */}
      {editingLinkedCalibre && (
        <CustomCalibreModal
          isOpen={!!editingLinkedCalibre}
          initialEditEntry={editingLinkedCalibre}
          currentBrandName={brand.name}
          defaultBrandId={brand.id}
          onSaved={() => {
            void handleLinkedCalibreSaved();
          }}
          onClose={() => setEditingLinkedCalibre(null)}
        />
      )}
      <ImportRulesEditorModal
        isOpen={showImportRules}
        onClose={() => setShowImportRules(false)}
        brandId={brand.id}
        brandName={brand.name}
      />

      {/* AI Pre-Enrichment Dialog — gathers optional extra context before the run */}
      <Modal
        isOpen={showAIPreDialog}
        onClose={() => setShowAIPreDialog(false)}
        title='Suggest with AI'
        size='md'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Claude will suggest values for empty fields. Optionally give it more
            to work with — both inputs are optional; leave them as-is to run a
            standard enrichment.
          </p>

          <div>
            <label className='flex items-start gap-2'>
              <input
                type='checkbox'
                className='mt-1'
                checked={aiFetchUrl}
                disabled={!productUrl.trim()}
                onChange={(e) => setAIFetchUrl(e.target.checked)}
              />
              <span className='text-sm'>
                <span className='font-medium'>Fetch product URL</span>
                <span className='block text-xs text-gray-500'>
                  {productUrl.trim()
                    ? 'Scrapes the product page and feeds its text to the AI. Off by default (extra cost).'
                    : 'No product URL on this reference.'}
                </span>
                {productUrl.trim() && (
                  <span className='block text-xs text-gray-400 truncate mt-0.5'>
                    {productUrl.trim()}
                  </span>
                )}
              </span>
            </label>
          </div>

          <div>
            <label className='flex items-start gap-2'>
              <input
                type='checkbox'
                className='mt-1'
                checked={aiUseImage && aiImageCount > 0}
                disabled={aiImageCount === 0}
                onChange={(e) => setAIUseImage(e.target.checked)}
              />
              <span className='text-sm'>
                <span className='font-medium'>
                  Use product images
                  {aiImageCount > 0 && ` (${aiImageCount})`}
                </span>
                <span className='block text-xs text-gray-500'>
                  {aiImageCount > 0
                    ? `Sends this reference’s image${aiImageCount > 1 ? 's' : ''} to Claude with their gallery tags, so dial colour, numerals, hand types, crown and caseback are read off the photos instead of guessed from the name. On by default (~$0.005 per image).`
                    : 'This reference has no images — enrichment will run on text only.'}
                </span>
                {aiImageCount > 0 && (
                  <span className='block text-xs text-gray-400 mt-0.5'>
                    Tag gallery images (dial, crown, caseback…) to tell the AI
                    what each one shows.
                  </span>
                )}
              </span>
            </label>
          </div>

          <div>
            <label className='block text-sm font-medium mb-1'>
              Additional context (optional)
            </label>
            <textarea
              className='w-full border border-gray-300 rounded px-2 py-1 text-sm'
              rows={4}
              value={aiAdditionalContext}
              onChange={(e) => setAIAdditionalContext(e.target.value)}
              placeholder='Paste extra specs, notes, or anything not already in this reference…'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Model</label>
            <select
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              value={aiModel}
              onChange={(e) => setAIModel(e.target.value)}
              disabled={aiModelChoices.loading}
            >
              <option value=''>
                {aiModelChoices.loading ? 'Loading models…' : 'Configured default'}
              </option>
              {aiModelChoices.options.map((m) => (
                <option key={m.id} value={m.id} title={m.title}>
                  {m.title}
                </option>
              ))}
            </select>
            <p className='mt-1 text-xs text-gray-500'>
              This run only — it resets to the configured model afterwards. Opus costs several
              times what Sonnet does per run; keep it for references the default cannot settle.
              {aiModelChoices.failed && ' (Live model list unavailable — showing a local fallback.)'}
            </p>
          </div>

          <div className='flex justify-end gap-2 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setShowAIPreDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const fetchUrl = aiFetchUrl;
                const additionalContext = aiAdditionalContext;
                const useImage = aiUseImage;
                const model = aiModel;
                setShowAIPreDialog(false);
                setAIModel('');
                void handleSuggestWithAI({
                  fetchUrl,
                  additionalContext,
                  useImage,
                  model,
                });
              }}
            >
              Run enrichment
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI Suggestions Modal */}
      <Modal
        isOpen={showAISuggestions}
        onClose={() => setShowAISuggestions(false)}
        title='AI Suggestions'
        size='xl'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-600'>
            Review Claude's suggestions below. Check the rows you want to apply,
            then click <span className='font-medium'>Apply Selected</span>.
          </p>
          <SuggestionSelectToolbar
            onSelect={applyAISelectionMode}
            disabled={aiApplying}
            note={(() => {
              const held = aiSelectionRows.filter((r) => r.heldBack).length;
              const ticked = Object.values(aiAccepted).filter(Boolean).length;
              return held > 0
                ? `${ticked} of ${aiSelectionRows.length} selected — ${held} held back, tick by hand`
                : `${ticked} of ${aiSelectionRows.length} selected`;
            })()}
          />
          <div className='border rounded-lg overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='px-3 py-2 w-12'></th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Field
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Current
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide'>
                    Suggested
                  </th>
                  <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-24'>
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {Object.entries(aiSuggestions).map(([key, value]) => {
                  // Fold a same-response `calibreTierName` into the picker row
                  // that `calibreTierId` already renders — one tier, one row.
                  if (
                    key === 'calibreTierName' &&
                    aiSuggestions.calibreTierId !== undefined
                  ) {
                    return null;
                  }
                  const handler = aiFieldHandlers[key];
                  const label = handler?.label ?? key;
                  const current = handler?.currentDisplay ?? '';
                  const suggested =
                    typeof value === 'object'
                      ? JSON.stringify(value)
                      : String(value);
                  const confidence = aiConfidence[key] || 'medium';
                  const badgeVariant: 'success' | 'warning' | 'default' =
                    confidence === 'high'
                      ? 'success'
                      : confidence === 'medium'
                      ? 'warning'
                      : 'default';
                  const unsupported = !handler;

                  // ── Calibre tier: a picker, not a take-it-or-leave-it value ──
                  const isTierRow = TIER_SUGGESTION_KEYS.has(key);
                  // The same apply may also re-link the calibre, in which case
                  // the tier list below belongs to the OUTGOING calibre and its
                  // ids are meaningless on the incoming one. Offering it would
                  // invite a choice we would then have to throw away, so the
                  // picker stands down and the model's answer is shown as-is —
                  // it gets resolved by NAME against the new calibre's tiers
                  // after the re-link, which is the one thing that can work.
                  const calibreRelinkPending =
                    !showModule &&
                    aiSuggestions.calibre !== undefined &&
                    !!aiAccepted.calibre;
                  // What the picker shows: the admin's explicit choice when
                  // there is one, else whatever the model's answer resolves to
                  // on the currently linked calibre (blank if it resolves to
                  // nothing — the model named a tier this calibre lacks).
                  const tierPickValue =
                    aiTierOverride !== null
                      ? aiTierOverride
                      : resolveTierSuggestion(
                          brandScopedTiers,
                          key === 'calibreTierId' ? String(value) : undefined,
                          key === 'calibreTierName'
                            ? String(value)
                            : typeof aiSuggestions.calibreTierName === 'string'
                            ? aiSuggestions.calibreTierName
                            : undefined,
                        ) ?? '';
                  // The one-sided low-confidence hold: only says anything when
                  // there is an existing human decision for it to protect.
                  const tierHeldLowConfidence =
                    isTierRow &&
                    shouldHoldTierLowConfidence(
                      confidence,
                      calibreTierId,
                      aiTierOverride,
                    );
                  // What ticking the calibre row will actually do: link the ref,
                  // link it under a different name, or write free text and leave
                  // it unlinked. Null on every other row, and on a calibre that
                  // resolved to the calibre of the same name — the ordinary case
                  // says nothing, so the cautions keep their weight.
                  const calibreMatchNotice =
                    key === 'calibre' && aiCalibreCheck
                      ? buildAiCalibreMatchNotice(aiCalibreCheck)
                      : null;

                  // A suggestion that simply confirms the stored value has
                  // nothing to write, so the row reports rather than asks: no
                  // checkbox, muted, with the reason in the note below. Every
                  // other unticked row carries its reason as a tooltip on the
                  // box, so "why isn't this ticked?" is answerable in place.
                  const reason = aiAcceptReason[key];
                  const isConfirmation = reason === 'matchesCurrent';
                  return (
                    <tr
                      key={key}
                      className={
                        unsupported
                          ? 'opacity-50'
                          : isConfirmation
                          ? 'bg-emerald-50/40 text-gray-500'
                          : ''
                      }
                    >
                      <td className='px-3 py-2 text-center'>
                        {isConfirmation ? (
                          <span
                            className='text-emerald-600'
                            title={CHECKED_REASON_LABEL.matchesCurrent}
                            aria-label='Matches the stored value'
                          >
                            ✓
                          </span>
                        ) : (
                          <input
                            type='checkbox'
                            checked={!!aiAccepted[key]}
                            disabled={unsupported}
                            title={reason ? CHECKED_REASON_LABEL[reason] : undefined}
                            onChange={(e) =>
                              setAIAccepted((prev) => ({
                                ...prev,
                                [key]: e.target.checked,
                              }))
                            }
                            className='rounded border-gray-300'
                          />
                        )}
                      </td>
                      <td className='px-3 py-2 font-medium text-gray-900'>
                        {label}
                        {unsupported && (
                          <span className='ml-2 text-xs text-gray-400'>
                            (not editable here)
                          </span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-gray-500 max-w-xs truncate'>
                        {current || (
                          <span className='italic text-gray-400'>empty</span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-gray-900 max-w-md whitespace-pre-wrap break-words'>
                        {isTierRow && !calibreRelinkPending ? (
                          <div className='flex items-center gap-2'>
                            <select
                              value={tierPickValue}
                              onChange={(e) => {
                                setAITierOverride(e.target.value);
                                // Choosing from the picker IS the decision —
                                // the admin should not also have to find the
                                // checkbox for it to count.
                                setAIAccepted((prev) => ({ ...prev, [key]: true }));
                              }}
                              className='rounded border-gray-300 text-sm py-1'
                            >
                              <option value=''>— No tier —</option>
                              {brandScopedTiers.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                            {tierPickValue && (
                              <button
                                type='button'
                                onClick={() => {
                                  setAITierOverride('');
                                  setAIAccepted((prev) => ({ ...prev, [key]: true }));
                                }}
                                className='text-xs text-gray-500 hover:text-gray-700 underline'
                              >
                                Clear
                              </button>
                            )}
                            {aiTierOverride !== null && (
                              <span className='text-xs text-gray-400'>edited</span>
                            )}
                          </div>
                        ) : (
                          suggested
                        )}
                        {isTierRow && calibreRelinkPending && (
                          <div className='mt-1 text-xs text-gray-500'>
                            The calibre is changing in this apply — the tier is
                            matched by name against the new calibre once it is
                            linked, so there is nothing to pick from yet.
                          </div>
                        )}
                        {isTierRow &&
                          !calibreRelinkPending &&
                          tierPickValue === '' &&
                          aiTierOverride === null && (
                            <div className='mt-1 text-xs text-amber-600'>
                              “{suggested}” matches no tier on the linked
                              calibre — pick one, or leave it to apply none.
                            </div>
                          )}
                        {tierHeldLowConfidence && (
                          <div className='mt-1 text-xs text-amber-600'>
                            AI unsure — kept your existing tier. Tick the box to
                            apply the suggestion anyway.
                          </div>
                        )}
                        {isConfirmation && (
                          <div className='mt-1 text-xs text-emerald-700'>
                            Matches the stored value — nothing to write.
                          </div>
                        )}
                        {reason === 'existingValue' && (
                          <div className='mt-1 text-xs text-gray-500'>
                            This field already has a different value — tick to
                            replace it.
                          </div>
                        )}
                        {reason === 'rewritesDescription' && (
                          <div className='mt-1 text-xs text-gray-500'>
                            Replaces the current description (AI-written, imported
                            or untagged). The first text replaced is kept.
                          </div>
                        )}
                        {reason === 'manualDescription' && (
                          <div className='mt-1 text-xs text-amber-700'>
                            The current description was written by hand, so it is
                            not replaced by default — tick to replace it.
                          </div>
                        )}
                        {aiUnverified[key] && (
                          <div className='mt-1 text-xs text-amber-600'>
                            Not in the current lookup list for this field —
                            review before accepting.
                          </div>
                        )}
                        {calibreMatchNotice && (
                          <div
                            className={`mt-1 text-xs ${
                              calibreMatchNotice.kind === 'renamed'
                                ? 'text-gray-500'
                                : 'text-amber-600'
                            }`}
                          >
                            {calibreMatchNotice.message}
                          </div>
                        )}
                      </td>
                      <td className='px-3 py-2'>
                        <Badge variant={badgeVariant}>{confidence}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className='flex justify-end gap-2 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setShowAISuggestions(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApplyAISuggestions} loading={aiApplying} disabled={aiApplying}>
              {aiApplying ? 'Applying calibre data...' : 'Apply Selected'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unsaved-changes confirm — gates any exit (header back, breadcrumb,
          wrapping-Modal Esc/backdrop) while the form is dirty. */}
      <Modal
        isOpen={pendingExit !== null}
        onClose={() => setPendingExit(null)}
        title='Unsaved changes'
        size='sm'
      >
        <div className='space-y-4'>
          <p className='text-sm text-gray-700'>
            You have unsaved changes to this reference. Discard them and leave?
          </p>
          <div className='flex justify-end gap-2'>
            <Button
              variant='secondary'
              onClick={() => setPendingExit(null)}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={() => {
                const go = pendingExit?.proceed;
                setPendingExit(null);
                go?.();
              }}
            >
              Discard
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
